import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import html2canvas from 'html2canvas';
import toast from 'react-hot-toast';
import { confirmDialog } from './utils/dialogHelper';
import { saveRevision } from './utils/templateRevisions';

// Import Firebase modules
import { signOut } from 'firebase/auth';
import { addDoc, collection, getDocs, doc, setDoc, deleteDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth as firebaseAuth, db as firebaseDb, firebaseConfig, getArtifactProjectId } from './firebaseInit';

// Import new script engine
import { scriptGraph, ScriptEngine } from './scriptGraph';
import ScriptBuilder from './components/ScriptBuilder';
import ProfileCompletionPrompt from './components/ProfileCompletionPrompt';
import CallScript from './components/CallScript';
import Login from './components/Login';
import AdminPanel from './components/AdminPanel';
import TeamManager from './components/TeamManager';
import HelpPanel from './components/HelpPanel';
import Leads from './components/Leads';
import ProblemReportAnalyzer from './components/ProblemReportAnalyzer';
import AdminTemplateBrowser from './components/AdminTemplateBrowser';
import LoadingSpinner from './components/shared/LoadingSpinner';
import Breadcrumbs from './components/shared/Breadcrumbs';
import CallEvaluator from './components/CallEvaluator/CallEvaluator';
import { AdminPreviewProvider } from './contexts/AdminPreviewContext';
import FlowChartEditorPage from './pages/FlowChartEditorPage';
import MyScriptsPage from './pages/MyScriptsPage';
import SettingsPage from './pages/SettingsPage';
import PerformancePage from './pages/PerformancePage';
import HelpCenter from './components/HelpCenter/HelpCenter';
import { serializeScriptGraph, deserializeScriptGraph } from './utils/graphSerialization';

// Import Marketplace components
import MarketplaceBrowser from './components/Marketplace/MarketplaceBrowser';
import MarketplaceListingDetail from './components/Marketplace/MarketplaceListingDetail';
import MarketplaceListingForm from './components/Marketplace/MarketplaceListingForm';
import SellerDashboard from './components/Marketplace/SellerDashboard';
import MyPurchases from './components/Marketplace/MyPurchases';
import MarketplaceAdminQueue from './components/Marketplace/MarketplaceAdminQueue';

// Import team management utilities
// Note: updateUserOnAuthentication disabled - using Custom Claims for role management
import * as firestorePaths from './utils/firestorePaths';

// Import DataContext for template loading
import { useData } from './contexts/DataContext';
import { useAuth } from './contexts/AuthContext';
import { useNotification } from './contexts/NotificationContext';
import { useBranding } from './contexts/BrandingContext';

// Import shared components
import TeamSelector from './components/shared/TeamSelector';

// Import analytics service
import AnalyticsService from './utils/analytics';

// Import email normalization utility
import { normalizeEmailToDocId } from './utils/emailNormalization';
// Import global setup utilities
import { logCriticalAction, getConsoleHistory } from './globalSetup';
// Firebase functions imports removed - not used in App.js

// Import lazy loading utility with chunk error handling
import { lazyWithRetry } from './utils/lazyWithRetry';

// Lazy load AI Script Builder for code splitting (with auto-reload on chunk errors)
const AIScriptBuilder = lazyWithRetry(() => import('./components/AIScriptBuilder'), 'AIScriptBuilder');

const DEBUG_AUTH = process.env.REACT_APP_DEBUG_AUTH === '1' || process.env.NODE_ENV === 'development';

const debugLog = (message, ...args) => {
  if (DEBUG_AUTH) {
    console.log(`🔐 [APP-DEBUG] ${message}`, ...args);
  }
};


// Main App component for The Call Script Genie
const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
    
  // Use notification system
  const { showError, showWarning, showSuccess, showInfo } = useNotification();
  
  // Get team branding
  const { branding } = useBranding();
    
  // Get authentication state from AuthContext
  const { 
    user,
    status, 
    isLoading: isAuthLoading,
    isAuthenticated,
    isAdmin,
    isTeamLead,
    userTeam,       // Auto-switches when admin views another team
    userTeamName,   // Auto-switches when admin views another team
    realTeamId,     // Admin's actual team (doesn't switch)
    userRole,
    db,
    userId,
    userEmail,
    isAuthReady,
    // Admin team switcher
    isViewingOtherTeam,
    viewingTeamId,
    setViewingTeamId
  } = useAuth();

  // Admin "view as" role toggle — UI-only, kept local to avoid unnecessary re-renders
  // of all useAuth() consumers when toggled. 'team_lead' | 'agent'
  const [viewingRole, setViewingRole] = useState('team_lead');

  // Reset to team_lead whenever the admin switches to a different team
  useEffect(() => {
    setViewingRole('team_lead');
  }, [viewingTeamId]);

  // Effective display flags (UI-only — real security is enforced server-side)
  const effectiveDisplayIsAgent = isViewingOtherTeam && viewingRole === 'agent';
  const effectiveDisplayIsTeamLead = isViewingOtherTeam ? viewingRole === 'team_lead' : isTeamLead;

  // Effective role props to pass to pages that receive role via props (not useAuth)
  // When admin previews agent view, these override the real role for display purposes only
  const effectiveIsAdmin    = effectiveDisplayIsAgent ? false   : isAdmin;
  const effectiveIsTeamLead = effectiveDisplayIsAgent ? false   : isTeamLead;
  const effectiveUserRole   = effectiveDisplayIsAgent ? 'basic' : userRole;

  // Get templates from DataContext
  const { templates: dataContextTemplates, templateVersion, isLoadingData, refreshTemplates } = useData();

  // UI state
  const [showLogin, setShowLogin] = useState(false);
  const [loginMode, setLoginMode] = useState('signin'); // 'signin' or 'signup'
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  
  // Admin override for test environment account creation
  const isTestEnvironment = firebaseConfig.projectId === 'treegenie-test';
  const [showAdminOverride, setShowAdminOverride] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminOverrideActive, setAdminOverrideActive] = useState(false);
  const [adminPasswordError, setAdminPasswordError] = useState('');

  // App flow state - start directly with sales flow
  const [appStage, setAppStage] = useState('salesFlow');
  const [currentStage, setCurrentStage] = useState('opening');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [propertyDetails, setPropertyDetails] = useState({});
  const [urgency, setUrgency] = useState('Low');
  const [leadCategory, setLeadCategory] = useState('Nurture Campaign');
  const [saveStatus, setSaveStatus] = useState('');
  const [prospectInput, setProspectInput] = useState('');
    
  // Check for deactivation/removal/session-conflict parameter in URL (dynamic reading)
  const [isDeactivated, setIsDeactivated] = useState(false);
  const [isRemoved, setIsRemoved] = useState(false);
  const [isSessionConflict, setIsSessionConflict] = useState(false);

  // Read URL parameters on each mount (for session isolation)
  useEffect(() => {
    // Re-read URL parameters on each mount
    const urlParams = new URLSearchParams(window.location.search);
    const deactivated = urlParams.get('deactivated') === 'true';
    const removed = urlParams.get('removed') === 'true';
    const sessionConflict = urlParams.get('session_conflict') === 'true';
    setIsDeactivated(deactivated);
    setIsRemoved(removed);
    setIsSessionConflict(sessionConflict);
        
    // Clear the parameters after reading to prevent persistence across sessions
    if (deactivated || removed || sessionConflict) {
      const url = new URL(window.location);
      url.searchParams.delete('deactivated');
      url.searchParams.delete('removed');
      url.searchParams.delete('session_conflict');
      window.history.replaceState({}, '', url);
      console.log('🔐 SESSION: Cleared deactivated/removed/session_conflict URL parameters for session isolation');
    }
    
    // Auto-open signup modal when arriving via agent invite link
    const inviteParam = urlParams.get('invite');
    if (inviteParam) {
      setLoginMode('signup');
      setShowLogin(true);
    }
  }, []); // Run once per mount

  // Default favicon as SVG with sparkle emoji
  const DEFAULT_FAVICON = 'data:image/svg+xml,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><text y=\'.9em\' font-size=\'90\'>✨</text></svg>';

  // Helper to set favicon - removes ALL existing favicons and adds new ones
  const setFavicon = useCallback((href, type = 'image/x-icon') => {
    // Remove ALL existing favicon links
    const existingLinks = document.querySelectorAll('link[rel*=\'icon\']');
    existingLinks.forEach(link => link.remove());
    
    // Add both shortcut icon and icon for better browser compatibility
    const link1 = document.createElement('link');
    link1.rel = 'icon';
    link1.type = type;
    link1.href = href;
    document.head.appendChild(link1);
    
    const link2 = document.createElement('link');
    link2.rel = 'shortcut icon';
    link2.type = type;
    link2.href = href;
    document.head.appendChild(link2);
    
    // Also add apple-touch-icon for iOS
    const link3 = document.createElement('link');
    link3.rel = 'apple-touch-icon';
    link3.href = href;
    document.head.appendChild(link3);
  }, []);

  // Update browser tab title and favicon with team branding
  useEffect(() => {
    if (branding?.companyName) {
      const title = (branding.tagline && branding.showTaglineInBrowserTitle)
        ? `${branding.companyName} - ${branding.tagline}`
        : branding.companyName;
      document.title = title;
    }
    
    // Update favicon
    if (branding?.logoUrl) {
      console.log('🎨 Setting favicon from logo URL:', branding.logoUrl);
      
      // For SVG logos, use directly
      if (branding.logoUrl.includes('.svg') || branding.logoUrl.includes('image/svg')) {
        console.log('🎨 Using SVG logo directly as favicon');
        setFavicon(branding.logoUrl, 'image/svg+xml');
        return;
      }
      
      // For other images, try to use directly first (simpler approach)
      // Firebase Storage URLs should work if CORS is configured
      setFavicon(branding.logoUrl, 'image/png');
      console.log('🎨 Favicon set to:', branding.logoUrl);
    } else {
      // Use default sparkle emoji favicon
      console.log('🎨 No logo URL, using default sparkle favicon');
      setFavicon(DEFAULT_FAVICON, 'image/svg+xml');
    }
  }, [branding?.companyName, branding?.tagline, branding?.showTaglineInBrowserTitle, branding?.logoUrl, setFavicon]);

  // Multiple open text responses state
  const [multipleInputs, setMultipleInputs] = useState({});
  const [showHelpPanel, setShowHelpPanel] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [showScriptBuilder, setShowScriptBuilder] = useState(false);
  const [scriptBuilderHasUnsavedChanges, setScriptBuilderHasUnsavedChanges] = useState(false);
  const [scriptBuilderAutoSave, setScriptBuilderAutoSave] = useState(true);
  const scriptBuilderSaveRef = useRef(null);
  const [scriptBuilderFlowchartModalOpen, setScriptBuilderFlowchartModalOpen] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showTeamLeadMessage, setShowTeamLeadMessage] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackDescription, setFeedbackDescription] = useState('');
  const [feedbackType, setFeedbackType] = useState('bug');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [capturedScreenshot, setCapturedScreenshot] = useState(null);
  const [isCapturingData, setIsCapturingData] = useState(false);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const [showProblemAnalyzer, setShowProblemAnalyzer] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showAdminTemplateBrowser, setShowAdminTemplateBrowser] = useState(false);
  const [isUpdatingMaintenance, setIsUpdatingMaintenance] = useState(false);
  const [appStats, setAppStats] = useState({
    totalCalls: 0,
    leadsSaved: 0,
    lastSaveTime: null
  });

  // Navigation state
  const [previousStages, setPreviousStages] = useState([]);
  const [currentStageConfig, setCurrentStageConfig] = useState(null);
    
  // Get initial navigation from URL or default to callScript
  const getInitialNavigation = () => {
    const path = location.pathname;
    if (path.startsWith('/flowchart-editor/')) return 'callScript'; // Flowchart is separate
    if (path === '/my-scripts' || path.startsWith('/my-scripts')) return 'myScripts';
    // Legacy redirects - map old routes to new myScripts
    if (path === '/script-editor') return 'myScripts';
    if (path === '/ai-script-builder') return 'myScripts';
    if (path === '/leads') return 'leads';
    if (path === '/admin-panel') return 'adminPanel';
    if (path === '/team-manager') return 'teamManager';
    if (path === '/ai-call-evaluator') return 'callEvaluator';
    if (path === '/settings') return 'settings';
    if (path === '/admin-templates') return 'adminTemplateBrowser';
    if (path === '/problem-analyzer') return 'problemAnalyzer';
    if (path === '/marketplace' || path.startsWith('/marketplace/')) return 'marketplace';
    if (path === '/help-center') return 'helpCenter';
    if (path === '/command-center' || path === '/dashboard') return 'dashboard';
    if (path === '/call-script' || path === '/') return 'callScript';
    return 'callScript'; // Default fallback
  };

  // Active navigation state - initialize from URL
  const [activeNavigation, setActiveNavigation] = useState(() => getInitialNavigation());

  // Marketplace tab state
  const [marketplaceTab, setMarketplaceTab] = useState('browse');

  // Global admin team selection (persistent across all pages) - from AuthContext
  // const [adminSelectedTeam, setAdminSelectedTeam] = useState(userTeam); // DEPRECATED: Moved to AuthContext
    
  // Sync activeNavigation with React Router location when URL changes
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/flowchart-editor/')) {
      // Don't change activeNavigation for flowchart editor
      return;
    } else if (path === '/my-scripts' || path.startsWith('/my-scripts')) {
      setActiveNavigation('myScripts');
    } else if (path === '/script-editor' || path === '/ai-script-builder') {
      // Legacy routes redirect to myScripts
      setActiveNavigation('myScripts');
    } else if (path === '/leads') {
      setActiveNavigation('leads');
    } else if (path === '/admin-panel') {
      setActiveNavigation('adminPanel');
    } else if (path === '/team-manager') {
      setActiveNavigation('teamManager');
    } else if (path === '/ai-call-evaluator') {
      setActiveNavigation('callEvaluator');
    } else if (path === '/settings') {
      setActiveNavigation('settings');
    } else if (path === '/admin-templates') {
      setActiveNavigation('adminTemplateBrowser');
    } else if (path === '/problem-analyzer') {
      setActiveNavigation('problemAnalyzer');
    } else if (path === '/marketplace' || path.startsWith('/marketplace/')) {
      setActiveNavigation('marketplace');
      // Set appropriate tab based on sub-route
      if (path === '/marketplace/purchases') {
        setMarketplaceTab('purchases');
      } else if (path === '/marketplace/seller') {
        setMarketplaceTab('seller');
      } else if (path === '/marketplace/sell') {
        setMarketplaceTab('list');
      } else if (path === '/marketplace/admin') {
        setMarketplaceTab('admin');
      } else if (path === '/marketplace' && !path.startsWith('/marketplace/listing/')) {
        // Only reset to browse if navigating to /marketplace directly (not listing detail)
        setMarketplaceTab('browse');
      }
    } else if (path === '/help-center') {
      setActiveNavigation('helpCenter');
    } else if (path === '/command-center' || path === '/dashboard') {
      setActiveNavigation('dashboard');
    } else if (path === '/call-script' || path === '/') {
      setActiveNavigation('callScript');
    }
  }, [location.pathname]);

  // Route-level access control: redirect agents who navigate directly to restricted URLs
  useEffect(() => {
    if (!userEmail || !userRole) return; // Auth not loaded yet
    // Only redirect genuine agents — not admins or team leads in "view as agent" mode
    if (userRole !== 'basic' || isAdmin || isTeamLead) return;
    const path = location.pathname;
    if (
      path === '/my-scripts' || path.startsWith('/my-scripts/') ||
      path === '/marketplace' || path.startsWith('/marketplace/')
    ) {
      navigate('/call-script', { replace: true });
    }
  }, [location.pathname, userEmail, userRole, isAdmin, isTeamLead, navigate]);
    
  // Analytics service state
  const [analytics, setAnalytics] = useState(null);
  const [currentCallSessionId, setCurrentCallSessionId] = useState(null);
    
  // Script engine instance
  const scriptEngine = useMemo(() => new ScriptEngine(scriptGraph), []);
    
  // Script template management
  const [currentScriptTemplate, setCurrentScriptTemplateState] = useState(null);
  const [availableScripts, setAvailableScripts] = useState({});
  // Track last revision content per template to avoid duplicate revisions
  const lastRevisionGraphRef = useRef({});
    
  // Sync availableScripts with DataContext templates
  // Syncs on: template IDs changing (add/remove), first load, or DataContext version bump
  // (version bumps when templates are reloaded from Firestore, e.g. after FlowChartEditor save).
  const prevTemplateIdsRef = useRef('');
  const prevTemplateVersionRef = useRef(0);
  useEffect(() => {
    if (dataContextTemplates && Object.keys(dataContextTemplates).length > 0) {
      const newIds = Object.keys(dataContextTemplates).sort().join(',');
      const currentIds = Object.keys(availableScripts).sort().join(',');
      const versionChanged = templateVersion !== prevTemplateVersionRef.current;
      
      // Full sync if template IDs changed, first load, or DataContext reloaded from Firestore
      if (newIds !== currentIds || currentIds === '' || versionChanged) {
        const reason = newIds !== currentIds ? 'IDs changed' : currentIds === '' ? 'initial load' : 'version bumped (Firestore reload)';
        console.log(`🔄 Syncing availableScripts with DataContext (${reason}):`, Object.keys(dataContextTemplates).length, 'templates');
        setAvailableScripts(dataContextTemplates);
        prevTemplateIdsRef.current = newIds;
        prevTemplateVersionRef.current = templateVersion;
      }
    }
  }, [dataContextTemplates, templateVersion]); // eslint-disable-line react-hooks/exhaustive-deps
    
  // Wrapper function to track template changes
  const setCurrentScriptTemplate = (template) => {
    console.log('📝 TEMPLATE CHANGE:', currentScriptTemplate, '→', template);
    console.log('👤 User role:', userRole, '| Default template:', defaultTemplate);
    console.log('📊 Available scripts loaded:', Object.keys(availableScripts).length > 0);
    console.trace('📍 Template change stack trace:');
        
    // Reset template ready state ONLY when:
    // 1. Actually changing to a different template AND
    // 2. Both old and new templates are non-null (not initial load from null)
    const isActualChange = template !== currentScriptTemplate;
    const isRealChange = isActualChange && currentScriptTemplate !== null && template !== null;
        
    if (isRealChange) {
      console.log('🔄 Template actually changed (from one template to another), resetting ready state');
      setIsTemplateReady(false);
    } else if (isActualChange && currentScriptTemplate === null) {
      console.log('✅ Initial template load (from null), keeping/allowing ready state to be set');
    } else {
      console.log('✅ Same template, keeping ready state');
    }
        
    setCurrentScriptTemplateState(template);
  };
  const [templatesLoading, setTemplatesLoading] = useState(false);
    
  // Sync templatesLoading with DataContext loading state
  useEffect(() => {
    setTemplatesLoading(isLoadingData);
  }, [isLoadingData]);
    
  // Validate currentStage when script template changes
  useEffect(() => {
    if (currentScriptTemplate && availableScripts[currentScriptTemplate] && currentStage) {
      const template = availableScripts[currentScriptTemplate];
      // BACKWARD COMPATIBILITY: Check both locations for graph data
      const currentGraph = template?.graph || template?.data?.scriptGraph || scriptGraph;
            
      // Skip validation for objection nodes (they're dynamically created from steps.objectionResponses)
      const isObjectionNode = currentStage?.startsWith('objection_');
            
      // Check if current stage exists in the graph
      if (currentGraph?.nodes && !currentGraph.nodes[currentStage] && currentStage !== 'opening' && currentStage !== 'call_end' && currentStage !== '__end_script__' && currentStage !== '__error__' && !isObjectionNode) {
        console.log(`❌ Current stage "${currentStage}" not found in template "${currentScriptTemplate}", resetting...`);
                
        // Find the first valid node or designated opening node
        const availableNodes = Object.keys(currentGraph.nodes || {});
        const openingNode = availableNodes.find(id => currentGraph.nodes[id].isOpeningNode);
        const fallbackNode = openingNode || availableNodes[0] || 'opening';
                
        console.log(`🔄 Resetting invalid stage "${currentStage}" to "${fallbackNode}"`);
                
        // Log this critical error for debugging
        logCriticalAction('ERROR', 'Invalid stage detected on template load', {
          invalidStage: currentStage,
          template: currentScriptTemplate,
          fallbackStage: fallbackNode,
          availableNodes: availableNodes.length,
          userEmail: userEmail,
          timestamp: new Date().toISOString()
        });
                
        // SECURITY: This might be from a previous user's session - DO NOT use fallback
        // Instead, always reset to opening for safety
        console.log('🔒 SECURITY: Stage mismatch detected - forcing reset to opening for session isolation');
        setCurrentStage('opening');
                
        // Clear conversation history since we're resetting
        setConversationHistory([]);
      } else if (isObjectionNode) {
        console.log(`✅ Objection node "${currentStage}" - skipping validation (dynamically created)`);
      }
    }
  }, [currentScriptTemplate, availableScripts, currentStage, userEmail]);
  const [leadSaving, setLeadSaving] = useState(false);
  const [defaultTemplate, setDefaultTemplate] = useState(null); // Initialize to null, will be set from Firestore
  const [viewingTeamLeadDefault, setViewingTeamLeadDefault] = useState(null); // Team lead's default for viewing team (for admins)
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isTemplateReady, setIsTemplateReady] = useState(false); // New state to track when template is fully loaded
    
  // REMOVED: Old initialization logic replaced with unified team-aware template selection below
  const [justSubmittedLead, setJustSubmittedLead] = useState(false);
    
  // Remove the automatic sync useEffect - template loading logic in loadUserDefaults/loadUserTemplateSettings
  // already handles setting currentScriptTemplate correctly based on defaultTemplate

  // Helper function to create a safe document ID from email
  // DEPRECATED: Use normalizeEmailToDocId from utils/emailNormalization.js instead
  // Keeping this for backward compatibility during migration
  const getEmailDocumentId = useCallback((email) => {
    if (!email) return null;
    return normalizeEmailToDocId(email);
  }, []);

  // Utility functions exposed on window for admin debugging (test/dev only)
  useEffect(() => {
    if (!isTestEnvironment && process.env.NODE_ENV !== 'development') return;

    window.migrateLegacyInvitations = async () => {
      try {
        const { httpsCallable } = await import('firebase/functions');
        const { functions } = await import('./firebaseInit');
                
        console.log('🔄 Starting legacy invitation migration...');
        const migrateFunction = httpsCallable(functions, 'migrateExistingInvitations');
        const result = await migrateFunction();
                
        console.log('✅ Migration completed!');
        console.log('📊 Results:', result.data);
        console.log('🎉 Legacy users can now sign up using email + team lead email');
                
      } catch (error) {
        console.error('❌ Migration failed:', error);
        console.error('❌ Error details:', error.message);
      }
    };

    // Debug: Check all pending invitations and cross-reference with team containers
    window.debugPendingInvitations = async () => {
      try {
        const { collection, getDocs, doc, getDoc } = await import('firebase/firestore');
                
        console.log('🔍 Checking all pending invitations...');
        const appId = getArtifactProjectId();
        const pendingInvitationsRef = collection(db, `artifacts/${appId}/pendingInvitations`);
                
        const snapshot = await getDocs(pendingInvitationsRef);
        console.log(`📊 Found ${snapshot.docs.length} total invitations in pendingInvitations`);
                
        for (const docSnap of snapshot.docs) {
          const data = docSnap.data();
          console.log(`📄 ${docSnap.id}:`, {
            status: data.status,
            email: data.email,
            invitedBy: data.invitedBy,
            teamId: data.teamId,
            createdAt: data.createdAt,
            completedAt: data.completedAt,
            userId: data.userId
          });
                    
          // Check if this user is actually active in their team container
          if (data.teamId && data.status === 'invited') {
            try {
              const teamUserRef = doc(db, `artifacts/${appId}/teams/${data.teamId}/users`, docSnap.id);
              const teamUserDoc = await getDoc(teamUserRef);
              if (teamUserDoc.exists()) {
                const teamUserData = teamUserDoc.data();
                console.log(`  📋 Team container status: ${teamUserData.status} (signupCompleted: ${teamUserData.signupCompleted})`);
                                
                if (teamUserData.status === 'active') {
                  console.log('  ⚠️ MISMATCH: Pending invitation shows \'invited\' but team container shows \'active\'');
                }
              }
            } catch (teamError) {
              console.log('  ❌ Could not check team container:', teamError.message);
            }
          }
        }
                
      } catch (error) {
        console.error('❌ Debug failed:', error);
        console.error('❌ Error details:', error.message);
      }
    };

    // Fix mismatched invitations (pending shows 'invited' but team container shows 'active')
    window.fixMismatchedInvitations = async () => {
      try {
        const { collection, getDocs, doc, getDoc, setDoc, deleteDoc } = await import('firebase/firestore');
                
        console.log('🔧 Fixing mismatched invitations...');
        const appId = getArtifactProjectId();
        const pendingInvitationsRef = collection(db, `artifacts/${appId}/pendingInvitations`);
                
        const snapshot = await getDocs(pendingInvitationsRef);
        console.log(`📊 Checking ${snapshot.docs.length} pending invitations for mismatches...`);
                
        let fixedCount = 0;
                
        for (const docSnap of snapshot.docs) {
          const invitationData = docSnap.data();
                    
          // Only check invitations that show as 'invited'
          if (invitationData.status === 'invited' && invitationData.teamId) {
            try {
              // Check team container status
              const teamUserRef = doc(db, `artifacts/${appId}/teams/${invitationData.teamId}/users`, docSnap.id);
              const teamUserDoc = await getDoc(teamUserRef);
                            
              if (teamUserDoc.exists()) {
                const teamUserData = teamUserDoc.data();
                                
                // If team container shows 'active', this invitation should be archived
                if (teamUserData.status === 'active' && teamUserData.signupCompleted) {
                  console.log(`🔧 Fixing mismatch for ${docSnap.id}: pending='invited', team='active'`);
                  console.log('🔧 Current user:', firebaseAuth.currentUser?.email);
                  console.log('🔧 Invitation email:', invitationData.email);
                  console.log('🔧 Archive path:', `artifacts/${appId}/archivedInvitations/${docSnap.id}`);
                                    
                  // Create archived record
                  const archivedData = {
                    ...invitationData,
                    status: 'completed',
                    completedAt: teamUserData.updatedAt || teamUserData.createdAt || new Date(),
                    userId: teamUserData.userId,
                    archivedFrom: 'pendingInvitations',
                    archivedAt: new Date(),
                    fixedMismatch: true
                  };
                                    
                  try {
                    // Write to archive collection and delete from pending
                    const archivedRef = doc(db, `artifacts/${appId}/archivedInvitations`, docSnap.id);
                    console.log('🔧 Attempting to write archived invitation...');
                    await setDoc(archivedRef, archivedData);
                    console.log('✅ Archived invitation written successfully');
                                        
                    console.log('🔧 Attempting to delete from pending...');
                    await deleteDoc(doc(db, `artifacts/${appId}/pendingInvitations`, docSnap.id));
                    console.log('✅ Deleted from pending successfully');
                                        
                    fixedCount++;
                    console.log(`✅ Fixed and archived: ${docSnap.id}`);
                  } catch (archiveError) {
                    console.error(`❌ Failed to archive ${docSnap.id}:`, archiveError);
                    console.error('❌ Error code:', archiveError.code);
                    console.error('❌ Error message:', archiveError.message);
                  }
                }
              }
            } catch (teamError) {
              console.error(`❌ Error checking team container for ${docSnap.id}:`, teamError);
            }
          }
        }
                
        console.log(`✅ Mismatch fixing completed! Fixed ${fixedCount} mismatched invitations`);
                
      } catch (error) {
        console.error('❌ Mismatch fixing failed:', error);
        console.error('❌ Error details:', error.message);
      }
    };

    // Archive existing completed invitations (status = 'completed')
    window.archiveCompletedInvitations = async () => {
      try {
        const { collection, getDocs, query, where, deleteDoc, doc, setDoc, getDoc } = await import('firebase/firestore');
                
        console.log('📦 Archiving completed invitations...');
        const appId = getArtifactProjectId();
        const pendingInvitationsRef = collection(db, `artifacts/${appId}/pendingInvitations`);
                
        // Look for completed ones
        const completedQuery = query(pendingInvitationsRef, where('status', '==', 'completed'));
        const snapshot = await getDocs(completedQuery);
        console.log(`📊 Found ${snapshot.docs.length} invitations with status 'completed'`);
                
        let archivedCount = 0;
                
        for (const docSnap of snapshot.docs) {
          try {
            const invitationData = docSnap.data();
            console.log(`📦 Archiving invitation ${docSnap.id}:`, invitationData);
                        
            const archivedData = {
              ...invitationData,
              archivedAt: new Date(),
              archivedFrom: 'pendingInvitations'
            };
                        
            // Write to archive collection
            const archivedRef = doc(db, `artifacts/${appId}/archivedInvitations`, docSnap.id);
            await setDoc(archivedRef, archivedData);
                        
            // Delete from pending
            await deleteDoc(doc(db, `artifacts/${appId}/pendingInvitations`, docSnap.id));
                        
            archivedCount++;
            console.log(`📦 Archived completed invitation: ${docSnap.id}`);
          } catch (error) {
            console.error(`❌ Failed to archive ${docSnap.id}:`, error);
          }
        }
                
        console.log(`✅ Archiving completed! Moved ${archivedCount} completed invitations to archive`);
                
      } catch (error) {
        console.error('❌ Archiving failed:', error);
        console.error('❌ Error details:', error.message);
      }
    };

    // Debug function to check Firebase Auth vs Firestore mismatch
    window.debugUserAuthMismatch = async (email) => {
      try {
        if (!email) {
          console.error('❌ Please provide an email: debugUserAuthMismatch("user@example.com")');
          return;
        }
                
        console.log('🔍 Checking Firebase Auth vs Firestore for:', email);
                
        // Check Firestore invitation status
        const emailDocId = getEmailDocumentId(email);
        const appId = getArtifactProjectId();
                
        // Check pending invitations
        const { doc, getDoc } = await import('firebase/firestore');
        const pendingRef = doc(db, `artifacts/${appId}/pendingInvitations`, emailDocId);
        const pendingDoc = await getDoc(pendingRef);
                
        console.log('📄 Pending invitation:', pendingDoc.exists() ? pendingDoc.data() : 'Not found');
                
        // Check team container
        if (pendingDoc.exists()) {
          const teamId = pendingDoc.data().teamId;
          const teamUserRef = doc(db, `artifacts/${appId}/teams/${teamId}/users`, emailDocId);
          const teamUserDoc = await getDoc(teamUserRef);
          console.log('👥 Team container:', teamUserDoc.exists() ? teamUserDoc.data() : 'Not found');
        }
                
        // Check archived invitations
        const archivedRef = doc(db, `artifacts/${appId}/archivedInvitations`, emailDocId);
        const archivedDoc = await getDoc(archivedRef);
        console.log('📦 Archived invitation:', archivedDoc.exists() ? archivedDoc.data() : 'Not found');
                
        console.log('💡 If Firebase Auth account exists but Firestore shows invited, the user should sign in instead of signing up');
                
      } catch (error) {
        console.error('❌ Debug failed:', error);
      }
    };

    return () => {
      delete window.migrateLegacyInvitations;
      delete window.debugPendingInvitations;
      delete window.fixMismatchedInvitations;
      delete window.archiveCompletedInvitations;
      delete window.debugUserAuthMismatch;
    };
  }, [userId, userEmail, userTeam, db, getEmailDocumentId, isTestEnvironment]);

  // Team-level lead capture form and subscription plan
  const [teamLeadCaptureForm, setTeamLeadCaptureForm] = useState(null);
  const [teamSubscriptionPlan, setTeamSubscriptionPlan] = useState('starter');

  // Load team lead capture form and subscription from Firestore
  useEffect(() => {
    if (!db || !userTeam) return;

    const loadTeamConfig = async () => {
      try {
        const appId = getArtifactProjectId();
        const teamRef = doc(db, `artifacts/${appId}/teams/${userTeam}`);
        const teamDoc = await getDoc(teamRef);
        if (teamDoc.exists()) {
          const teamData = teamDoc.data();
          if (teamData.leadCaptureForm) {
            setTeamLeadCaptureForm(teamData.leadCaptureForm);
          }
          if (teamData.subscription?.plan) {
            setTeamSubscriptionPlan(teamData.subscription.plan);
          }
        }
      } catch (err) {
        console.error('Error loading team config:', err);
      }
    };

    loadTeamConfig();
  }, [db, userTeam]);

  // Auto-apply prospect form configuration when template changes
  // Priority: script-specific form (Business only) → team-wide form → default
  useEffect(() => {
    console.log('🔧 Setting up prospect form config for template:', currentScriptTemplate);
        
    const defaultProspectForm = {
      title: 'Lead Data',
      fields: [
        { id: 'agentName', label: 'Agent Name', type: 'text', placeholder: 'Enter your name', required: true },
        { id: 'prospectName', label: 'Contact Name', type: 'text', placeholder: 'Enter contact\'s full name', required: true },
        { id: 'company', label: 'Company', type: 'text', placeholder: 'Enter company name', required: false },
        { id: 'prospectPhone', label: 'Phone Number', type: 'tel', placeholder: 'Enter phone number', required: true }
      ]
    };

    // 1. Business: Check for script-specific form (must have actual fields)
    if (teamSubscriptionPlan === 'pro_plus' && currentScriptTemplate && availableScripts[currentScriptTemplate]?.prospectForm?.fields?.length > 0) {
      console.log('✅ Business: Using script-specific prospect form');
      setCurrentProspectFormConfig(availableScripts[currentScriptTemplate].prospectForm);
    }
    // 2. Team-wide form (configured on Leads page)
    else if (teamLeadCaptureForm && teamLeadCaptureForm.fields?.length > 0) {
      console.log('✅ Using team-wide lead capture form');
      setCurrentProspectFormConfig(teamLeadCaptureForm);
    }
    // 3. Default fallback
    else {
      console.log('⚠️ No custom form configured, using default');
      setCurrentProspectFormConfig(defaultProspectForm);
    }
  }, [currentScriptTemplate, availableScripts, teamLeadCaptureForm, teamSubscriptionPlan]);



  // Prospect data form state (shown after call ends)
  const [prospectDataForm, setProspectDataForm] = useState({});

    
  // Current prospect form configuration from template
  const [currentProspectFormConfig, setCurrentProspectFormConfig] = useState({
    title: 'Lead Data',
    fields: [
      { id: 'agentName', label: 'Agent Name', type: 'text', placeholder: 'Enter your name', required: true },
      { id: 'prospectName', label: 'Contact Name', type: 'text', placeholder: 'Enter contact\'s full name', required: true },
      { id: 'company', label: 'Company', type: 'text', placeholder: 'Enter company name', required: false },
      { id: 'prospectPhone', label: 'Phone Number', type: 'tel', placeholder: 'Enter phone number', required: true }
    ]
  });
  const [prospectDataError, setProspectDataError] = useState('');
  const [rightPanelContent, setRightPanelContent] = useState('script'); // 'script', 'prospectData', or 'callSummary'

  // RBAC State Variables (using AuthContext for auth state, keeping local state for app-specific features)
  // Note: Removed assignedTemplate - everyone now uses defaultTemplate from user document
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showTeamManager, setShowTeamManager] = useState(false);
    
  // Derived auth state from AuthContext (preserves all security checks)
  const hasAccess = isAuthenticated && userRole !== null;
  const isLoadingUserAccess = isAuthLoading || status === 'initializing';
  const [appInitialized, setAppInitialized] = useState(false);

  // Refs
  const conversationEndRef = useRef(null);

  // RBAC Access Control Functions (using AuthContext values for security)
  // When admin views another team as "Agent", certain nav items are hidden to reflect the agent experience
  const canAccessScriptBuilder = useCallback(() => !effectiveDisplayIsAgent && (isAdmin || isTeamLead), [effectiveDisplayIsAgent, isAdmin, isTeamLead]);
  const canSwitchTemplates = useCallback(() => !effectiveDisplayIsAgent && (isAdmin || isTeamLead), [effectiveDisplayIsAgent, isAdmin, isTeamLead]);
  const canManageUsers = useCallback(() => !effectiveDisplayIsAgent && (isAdmin || isTeamLead), [effectiveDisplayIsAgent, isAdmin, isTeamLead]);
  const canAccessTeamManager = useCallback(() => !effectiveDisplayIsAgent && (isAdmin || isTeamLead), [effectiveDisplayIsAgent, isAdmin, isTeamLead]);
  const canAccessAdminPanel = useCallback(() => !isViewingOtherTeam && isAdmin, [isViewingOtherTeam, isAdmin]);
  const canAccessLeads = useCallback(() => isAdmin || isTeamLead || userRole === 'basic', [isAdmin, isTeamLead, userRole]);



  // Map navigation keys to URL paths
  const navigationToPath = {
    callScript: '/call-script',
    myScripts: '/my-scripts',
    leads: '/leads',
    dashboard: '/command-center',
    adminPanel: '/admin-panel',
    teamManager: '/team-manager',
    callEvaluator: '/ai-call-evaluator',
    settings: '/settings',
    adminTemplateBrowser: '/admin-templates',
    problemAnalyzer: '/problem-analyzer',
    marketplace: '/marketplace',
    marketplacePurchases: '/marketplace/purchases',
    marketplaceSeller: '/marketplace/seller',
    marketplaceSell: '/marketplace/sell',
    marketplaceAdmin: '/marketplace/admin',
    helpCenter: '/help-center',
  };

  // Perform the actual navigation (shared by direct and deferred paths)
  const doNavigate = (targetNavigation, targetUrl = null) => {
    setActiveNavigation(targetNavigation);
    window.currentNavigation = targetNavigation;
    const targetRoute = targetUrl || navigationToPath[targetNavigation] || '/call-script';
    navigate(targetRoute);
    logCriticalAction('NAV', `Navigation completed: ${targetNavigation}`, {
      to: targetNavigation,
      targetRoute,
      timestamp: new Date().toISOString()
    });
    setShowScriptBuilder(false);
    setShowAdminPanel(false);
    setShowTeamManager(false);
    setShowHelpPanel(false);
  };

  // Navigation with unsaved changes check
  const navigateWithUnsavedCheck = (targetNavigation, targetUrl = null) => {
    logCriticalAction('NAV', `Navigation requested: ${activeNavigation} → ${targetNavigation}`, {
      from: activeNavigation,
      to: targetNavigation,
      targetUrl,
      hasUnsavedChanges: scriptBuilderHasUnsavedChanges,
      autoSave: scriptBuilderAutoSave,
      userRole: userRole
    });

    // Only block when autosave is OFF and there are unsaved changes in Script Builder
    if (activeNavigation === 'myScripts' && scriptBuilderHasUnsavedChanges && !scriptBuilderAutoSave) {
      // Show Save / Discard / Cancel toast dialog
      toast(
        (t) => (
          <div>
            <p style={{ marginBottom: '12px', fontWeight: 500 }}>You have unsaved changes in Script Builder.</p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { toast.dismiss(t.id); }}
                style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #D1D5DB', background: '#fff', color: '#374151', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
              >
                Cancel
              </button>
              <button
                onClick={() => { toast.dismiss(t.id); if (refreshTemplates) refreshTemplates(); doNavigate(targetNavigation, targetUrl); }}
                style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #D1D5DB', background: '#fff', color: '#DC2626', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
              >
                Discard
              </button>
              <button
                onClick={async () => {
                  toast.dismiss(t.id);
                  if (scriptBuilderSaveRef.current) {
                    try { await scriptBuilderSaveRef.current(); } catch (e) { console.error('Save before navigate failed:', e); }
                  }
                  doNavigate(targetNavigation, targetUrl);
                }}
                style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: '#2563EB', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}
              >
                Save
              </button>
            </div>
          </div>
        ),
        {
          duration: Infinity,
          position: 'top-center',
          style: {
            background: '#fff',
            color: '#374151',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            padding: '20px',
            fontSize: '14px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
            maxWidth: '420px',
            minWidth: '320px'
          }
        }
      );
      return;
    }

    // Otherwise navigate immediately
    doNavigate(targetNavigation, targetUrl);
  };

  // Listen for navigation events dispatched from child components (e.g., Leads page buttons, Marketplace)
  // Supports two formats:
  //   detail: 'myScripts'  (simple string - navigates to default route)
  //   detail: { target: 'myScripts', url: '/my-scripts?tab=edit&template=abc' }  (with custom URL)
  useEffect(() => {
    const handleNavigateTo = (e) => {
      const detail = e.detail;
      if (typeof detail === 'string') {
        navigateWithUnsavedCheck(detail);
      } else if (detail && detail.target) {
        navigateWithUnsavedCheck(detail.target, detail.url || null);
      }
    };
    window.addEventListener('navigateTo', handleNavigateTo);
    return () => window.removeEventListener('navigateTo', handleNavigateTo);
  });

  // Get stage configuration using the new script engine
  const getStageConfig = useCallback((stage) => {
    const template = availableScripts[currentScriptTemplate];
    // BACKWARD COMPATIBILITY: Check both locations for graph data (graph is primary after normalization)
    let currentGraph = template?.graph || template?.data?.scriptGraph || scriptGraph;
        
    // Handle "opening" stage for templates that don't have it
    if (stage === 'opening' && currentGraph?.nodes && !currentGraph.nodes[stage]) {
      console.log('🔄 "Opening" node not found, finding designated or actual start node...');
            
      // First, look for a node explicitly marked as opening node
      const designatedOpeningNode = Object.entries(currentGraph.nodes).find(([nodeId, node]) => node.isOpeningNode);
            
      if (designatedOpeningNode) {
        const [actualStartId] = designatedOpeningNode;
        console.log(`🔄 Found designated opening node: "${actualStartId}"`);
                
        // Update currentStage to the actual node ID for navigation to work
        if (currentStage === 'opening') {
          console.log(`🔄 Updating currentStage from "opening" to "${actualStartId}"`);
          setCurrentStage(actualStartId);
        }
                
        const currentEngine = new ScriptEngine(currentGraph);
        return currentEngine.getNodeConfig(actualStartId, propertyDetails);
      }
            
      // Fallback: Find the actual start node (node with content that no other node points to)
      const referencedNodes = new Set();
      const nodeEntries = Object.entries(currentGraph.nodes);
            
      // Build reference map
      nodeEntries.forEach(([nodeId, node]) => {
        if (node.responses) {
          Object.values(node.responses).forEach(resp => {
            if (resp.nextNode && resp.nextNode !== '__end_script__') {
              referencedNodes.add(resp.nextNode);
            }
          });
        }
      });
            
      // Find start nodes (not referenced by others and have content)
      // IMPORTANT: Exclude objection nodes - they should never be opening nodes
      const startNodes = nodeEntries.filter(([nodeId, node]) => {
        const isNotObjection = !nodeId.startsWith('objection_');
        const isNotReferenced = !referencedNodes.has(nodeId);
        const hasContent = node.script && node.script.trim() !== '' && node.script !== 'New question...';
        const hasResponses = node.responses && Object.keys(node.responses).length > 0;
        return isNotObjection && isNotReferenced && hasContent && hasResponses;
      });
            
      if (startNodes.length > 0) {
        const [actualStartId] = startNodes[0];
        console.log(`🔄 Fallback: Using inferred start node: "${actualStartId}"`);
                
        // Update currentStage to the actual node ID for navigation to work
        if (currentStage === 'opening') {
          console.log(`🔄 Updating currentStage from "opening" to "${actualStartId}"`);
          setCurrentStage(actualStartId);
        }
                
        // Use the actual start node ID instead of "opening"
        const currentEngine = new ScriptEngine(currentGraph);
        return currentEngine.getNodeConfig(actualStartId, propertyDetails);
      }
            
      // Final fallback: if no proper start nodes found, use the first available non-objection node
      const allNodes = Object.keys(currentGraph.nodes).filter(id => !id.startsWith('objection_'));
      if (allNodes.length > 0) {
        const firstNodeId = allNodes[0];
        console.log(`🔄 Final fallback: Using first available non-objection node: "${firstNodeId}"`);
                
        // Update currentStage to the actual node ID for navigation to work
        if (currentStage === 'opening') {
          console.log(`🔄 Updating currentStage from "opening" to "${firstNodeId}"`);
          setCurrentStage(firstNodeId);
        }
                
        const currentEngine = new ScriptEngine(currentGraph);
        return currentEngine.getNodeConfig(firstNodeId, propertyDetails);
      }
    }
        
    // Enhanced debug logging for template graph issues
    console.log('🔍 getStageConfig debug:', {
      requestedStage: stage,
      isObjectionNode: stage?.startsWith('objection_'),
      currentScriptTemplate,
      hasTemplate: !!template,
      templateStructure: template ? Object.keys(template) : 'no template',
      hasDataScriptGraph: !!template?.data?.scriptGraph,
      hasGraph: !!template?.graph,
      dataVersion: template?._dataVersion || 'legacy',
      currentGraphNodes: Object.keys(currentGraph?.nodes || {}),
      fallbackToDefault: currentGraph === scriptGraph,
      usingTemplateGraph: currentGraph === template?.graph || currentGraph === template?.data?.scriptGraph,
      requestedStageExists: !!(currentGraph?.nodes?.[stage]),
      availableStages: Object.keys(currentGraph?.nodes || {}),
      sampleNode: currentGraph?.nodes ? Object.values(currentGraph.nodes)[0] : null,
      templateId: template?.id,
      templateName: template?.name,
      templateDataKeys: template?.data ? Object.keys(template.data) : 'no data',
      scriptGraphNodeCount: template?.data?.scriptGraph?.nodes ? Object.keys(template.data.scriptGraph.nodes).length : 'no nodes',
      availableScriptsKeys: Object.keys(availableScripts),
      objectionNodeExists: stage?.startsWith('objection_') ? !!(currentGraph?.nodes?.[stage]) : null,
      objectionNodeData: stage?.startsWith('objection_') ? {
        id: currentGraph?.nodes?.[stage]?.id,
        script: currentGraph?.nodes?.[stage]?.script?.substring?.(0, 100) + '...',
        hasResponses: !!currentGraph?.nodes?.[stage]?.responses,
        responseCount: Object.keys(currentGraph?.nodes?.[stage]?.responses || {}).length
      } : null
    });
        
    if (currentScriptTemplate && template && !currentGraph.nodes) {
      console.error('🚨 Template graph missing nodes:', {
        templateId: currentScriptTemplate,
        hasTemplate: !!template,
        hasDataScriptGraph: !!template?.data?.scriptGraph,
        currentGraphKeys: Object.keys(currentGraph || {}),
        templateKeys: Object.keys(template || {})
      });
    }
        
    // Special handling for objection nodes - ensure they have proper button configuration
    if (stage?.startsWith('objection_')) {
      console.log('🔍 Objection node requested:', stage, {
        nodeExists: !!currentGraph?.nodes?.[stage],
        allNodeIds: Object.keys(currentGraph?.nodes || {}),
        objectionNodeIds: Object.keys(currentGraph?.nodes || {}).filter(id => id.startsWith('objection_'))
      });
            
      if (currentGraph?.nodes?.[stage]) {
        const objectionNode = currentGraph.nodes[stage];
        console.log('✅ Found objection node:', stage, {
          id: objectionNode.id,
          script: objectionNode.script?.substring?.(0, 100) + '...',
          hasResponses: !!objectionNode.responses,
          responseKeys: Object.keys(objectionNode.responses || {})
        });
                
        // Ensure objection node has the required responses
        if (!objectionNode.responses || !objectionNode.responses.continue) {
          console.warn('⚠️ Objection node missing continue response, adding it:', stage);
          // Add missing continue response
          const updatedGraph = {
            ...currentGraph,
            nodes: {
              ...currentGraph.nodes,
              [stage]: {
                ...objectionNode,
                responses: {
                  ...objectionNode.responses,
                  continue: {
                    nextNode: '__return_to_previous__',
                    label: 'Continue with question',
                    data: {}
                  },
                  end_call: {
                    nextNode: '__end_script__',
                    label: 'End Call',
                    data: {}
                  }
                }
              }
            }
          };
                    
          // Update the template in availableScripts so the fix persists for this session
          setAvailableScripts(prev => ({
            ...prev,
            [currentScriptTemplate]: {
              ...prev[currentScriptTemplate],
              graph: updatedGraph
            }
          }));
                    
          const currentEngine = new ScriptEngine(updatedGraph);
          return currentEngine.getNodeConfig(stage, propertyDetails);
        }
      } else {
        console.error('❌ Objection node NOT FOUND in graph:', stage);
        console.error('Available objection nodes:', Object.keys(currentGraph?.nodes || {}).filter(id => id.startsWith('objection_')));
        console.error('Template steps:', template?.steps?.map(s => ({
          id: s.id,
          objections: s.objectionResponses?.map(o => `objection_${o.id}`)
        })));
                
        // Fallback: return error config
        return {
          script: 'Error: Objection handling not found. Please save your script in Script Editor first.',
          coaching: 'Objection node is missing from the script graph.',
          buttons: [{
            label: 'Return to previous question',
            value: 'continue',
            nextStage: '__return_to_previous__',
            type: 'predetermined'
          }],
          requiresTextInput: false,
          isError: true
        };
      }
    }
        
    // Handle error state - when no valid nodes exist or recovery failed
    if (stage === '__error__') {
      return {
        script: 'We\'re experiencing a technical issue with this script template. Please contact support for assistance.',
        coaching: 'Technical error - script template is corrupted or missing.',
        buttons: [{
          label: 'End Call and Report Issue',
          value: 'end_call',
          nextStage: 'call_end',
          type: 'predetermined'
        }],
        requiresTextInput: false,
        isError: true
      };
    }
        
    const currentEngine = new ScriptEngine(currentGraph);
    return currentEngine.getNodeConfig(stage, propertyDetails);
  }, [availableScripts, currentScriptTemplate, propertyDetails]);

  // Load templates from Firestore
  const loadTemplatesFromFirestore = useCallback(async (currentUserTeam = userTeam) => {
    // Allow loading as soon as Firestore is available
    if (!db) {
      console.log('Cannot load templates: Database not ready');
      return;
    }

    window.templateLoadStart = Date.now(); // Track loading duration
        
    logCriticalAction('DATA', 'Template loading initiated', {
      currentUserTeam,
      userTeam,
      userRole,
      userEmail: userEmail ? '[REDACTED]' : 'null',
      isAuthReady,
      hasDatabase: !!db,
      templatesLoading,
      currentTemplatesCount: Object.keys(availableScripts).length
    });

    // Prevent multiple simultaneous loads
    if (templatesLoading) {
      console.log('Templates already loading, skipping...');
      return;
    }
        
    // Must have user team context to load templates
    if (!currentUserTeam) {
      console.log('⚠️ Cannot load templates: User team not set', {
        currentUserTeam,
        userTeam,
        userRole,
        userEmail
      });
      return;
    }

    setTemplatesLoading(true);
    console.log('✅ Set templatesLoading to true');
        
    // Add a timeout fallback to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      console.log('⚠️ Template loading timeout reached, forcing loading state to false');
      setTemplatesLoading(false);
    }, 10000); // 10 second timeout
        
    try {
      const appId = getArtifactProjectId();
      console.log('Loading templates for appId:', appId, 'userTeam:', currentUserTeam);
            
      const loadedTemplates = {};

      // NOTE: Global templates are no longer loaded - each team has their own copy
      // of example-script that is fully editable. This was implemented in commit 98d41f0
      // "Template assignment architecture simplification" which copies example-script
      // to each team's templates directory on team creation.

      // Load team-specific templates (includes team's copy of example-script)
      try {
        console.log(`📁 Loading templates for team: ${currentUserTeam}...`);
        const teamTemplatesRef = collection(db, firestorePaths.getTemplatesPath(appId, currentUserTeam));
        const teamSnapshot = await getDocs(teamTemplatesRef);
        console.log(`Found ${teamSnapshot.size} team templates`);
                
        teamSnapshot.forEach((docSnap) => {
          const t = docSnap.data();
          console.log(`📁 Loading team template: ${docSnap.id}`, t);
                    
          // Template data might be in data.scriptGraph (new) or graph (legacy)
          const deserializedGraph = t?.data?.scriptGraph ? deserializeScriptGraph(t.data.scriptGraph) : 
            t?.graph ? deserializeScriptGraph(t.graph) : scriptGraph;
                    
          console.log('📍 LOADING DEBUG - Raw template data ui.positions:', t?.data?.scriptGraph?.ui?.positions);
          console.log('📍 LOADING DEBUG - Deserialized graph ui.positions:', deserializedGraph?.ui?.positions);
                    
          loadedTemplates[docSnap.id] = {
            id: docSnap.id,
            name: t?.metadata?.name || t?.name || `Template ${docSnap.id}`,
            graph: deserializedGraph,
            steps: t?.steps || null,
            prospectForm: t?.prospectForm || null,
            canEdit: true,  // All team templates are editable
            isReadOnly: false,
            metadata: {
              accessLevel: 'team',
              creatorId: t?.metadata?.creatorId || t?.creatorId || t?.creatorEmail || userEmail,
              creatorTeam: currentUserTeam,
              createdAt: t?.metadata?.createdAt,
              updatedAt: t?.metadata?.updatedAt
            }
          };
        });
      } catch (error) {
        console.warn(`⚠️ Error loading team templates for ${currentUserTeam}:`, error);
      }
      // Set the loaded templates
      setAvailableScripts(loadedTemplates);
      console.log('Final templates loaded:', Object.keys(loadedTemplates));
      console.log('Templates summary:', Object.entries(loadedTemplates).map(([id, t]) => ({
        id,
        name: t.name,
        accessLevel: t.metadata?.accessLevel
      })));
            
    } catch (error) {
      logCriticalAction('ERROR', 'Template loading failed', {
        error: error.message,
        errorCode: error.code,
        userTeam: currentUserTeam,
        attemptedAppId: firebaseConfig?.projectId
      });
      console.error('Error loading templates from Firestore:', error);
    } finally {
      clearTimeout(loadingTimeout); // Clear the timeout
      setTemplatesLoading(false);
            
      logCriticalAction('DATA', 'Template loading completed', {
        templatesLoaded: Object.keys(availableScripts).length,
        userTeam: currentUserTeam,
        loadingDuration: Date.now() - (window.templateLoadStart || Date.now())
      });
    }
  }, [db, templatesLoading, userTeam]);

  // Safety check: ensure currentScriptTemplate always exists in availableScripts
  useEffect(() => {
    // Don't run this check if templates are currently loading - prevents false fallback during refresh
    if (templatesLoading || isLoadingData) {
      console.log('⏳ Templates loading, skipping availability check');
      return;
    }
        
    if (currentScriptTemplate && availableScripts && Object.keys(availableScripts).length > 0) {
      if (!availableScripts[currentScriptTemplate]) {
        console.log(`⚠️ Current script template ${currentScriptTemplate} not found in availableScripts`);
                
        // For agents, don't immediately fall back - their team-assigned template might still be loading
        if (userRole === 'basic' && defaultTemplate && defaultTemplate !== 'example-script') {
          console.log(`⏳ Agent with team-assigned template ${defaultTemplate} - waiting for template to load instead of falling back`);
          // Don't change currentScriptTemplate - let the real-time listener handle it
          return;
        }
                
        console.log('🔄 Falling back to example-script');
        setCurrentScriptTemplate('example-script');
      }
    }
  }, [currentScriptTemplate, availableScripts, userRole, defaultTemplate, templatesLoading, isLoadingData]);


  // Wait for complete user context before loading templates (prevents security window)
  const initialTemplatesFetchRef = useRef(false);
  useEffect(() => {
    console.log('🔄 useEffect for template loading triggered:', {
      isAuthReady,
      db: !!db,
      userRole,
      userTeam,
      initialTemplatesFetchRef: initialTemplatesFetchRef.current
    });
        
    // DISABLED: Template loading now handled by DataContext
    console.log('🔄 Template loading disabled - using DataContext instead');
        
    // Still set initialTemplatesFetchRef to prevent other template loading attempts
    if (isAuthReady && db && userRole && userTeam && !initialTemplatesFetchRef.current) {
      initialTemplatesFetchRef.current = true;
    }
  }, [isAuthReady, db, userRole, userTeam]);


  // Save template to Firestore
  const saveTemplateToFirestore = useCallback(async (templateId, templateData) => {
    if (!db || !userId || !isAuthReady || !userEmail) {
      console.log('Cannot save template: Database not ready or user not authenticated');
      console.log('db:', !!db, 'userId:', !!userId, 'isAuthReady:', isAuthReady, 'userEmail:', userEmail);
      return false;
    }

    try {
      const appId = getArtifactProjectId();
      console.log('Saving template:', templateId, 'for appId:', appId, 'userEmail:', userEmail);
            
      // Use team-based path for templates
      if (!userTeam) {
        console.log('Cannot save template: User has no team assignment');
        return false;
      }
            
      const templateRef = doc(db, `artifacts/${appId}/teams/${userTeam}/templates`, templateId);
      console.log('Template document path:', `artifacts/${appId}/teams/${userTeam}/templates/${templateId}`);
            
      // Serialize the script graph to remove functions
      const serializedGraph = serializeScriptGraph(templateData.graph);
      console.log('💾 About to save graph ui.positions:', serializedGraph.ui?.positions);
            
      const templateDataToSave = {
        metadata: {
          name: templateData.name,
          description: templateData.description || '',
          creatorId: userEmail,
          creatorTeam: userTeam, // Add team information for access control
          createdAt: templateData.createdAt || new Date(),
          updatedAt: new Date(),
          accessLevel: templateData.accessLevel || 'private',
          sharedWith: templateData.sharedWith || [],
          tags: templateData.tags || []
        },
        data: {
          scriptGraph: serializedGraph
        },
        steps: templateData.steps, // Include steps configuration
        prospectForm: templateData.prospectForm // Include prospect form configuration
      };
            
      console.log('Saving template data:', templateDataToSave);
            
      await setDoc(templateRef, templateDataToSave);

      console.log('Template saved to Firestore:', templateId);
      return true;
    } catch (error) {
      console.error('Error saving template to Firestore:', error);
      console.error('Error details:', error.code, error.message);
      setSaveStatus(`Error saving template: ${error.message}`);
      return false;
    }
  }, [db, userId, isAuthReady, userEmail, userTeam]);

  // Handle logout
  const handleLogout = async () => {
    try {
      // Use global logging function
      if (window.logCriticalAction) {
        window.logCriticalAction('AUTH', 'User logout initiated', {
          userEmail: userEmail ? '[REDACTED]' : 'null',
          userRole: userRole,
          userTeam: userTeam,
          currentPage: activeNavigation,
          timestamp: new Date().toISOString()
        });
      }

      // SECURITY (M10): Comprehensive cleanup on logout
      // Clear session tokens from localStorage
      if (userId) {
        try {
          localStorage.removeItem(`treegenie_session_${userId}`);
        } catch (e) { /* ignore */ }
      }
      localStorage.removeItem('admin_viewing_team_id');
      localStorage.removeItem('admin_viewing_team_name');
      window.currentUserEmail = null;
      window.currentUserTeamId = null;

      // Reset URL BEFORE signOut — signOut triggers onAuthStateChanged which
      // remounts the App component via SessionContainer's key change. If we
      // navigate after signOut, the old component instance is already unmounting
      // and the new instance reads the stale URL.
      window.history.replaceState({}, '', '/');
      setShowLogin(false);
      setActiveNavigation('callScript');

      await signOut(firebaseAuth);
            
      if (window.logCriticalAction) {
        window.logCriticalAction('AUTH', 'User logout completed', {
          stateReset: true,
          timestamp: new Date().toISOString()
        });
      }
            
      // Don't show Sign In modal - let user go to main login screen
      // Main screen will show maintenance message if needed
    } catch (error) {
      if (window.logCriticalAction) {
        window.logCriticalAction('ERROR', 'Logout failed', {
          error: error.message,
          errorCode: error.code,
          userEmail: userEmail ? '[REDACTED]' : 'null'
        });
      }
      console.error('Error signing out:', error);
    }
  };


  // Handle successful login
  const handleLoginSuccess = () => {
    console.log('=== LOGIN SUCCESS CALLBACK ===');
    console.log('🔍 Current user state:', { userEmail, userRole, isAuthenticated, isAuthReady });
        
    // Always defer to the claims processing for proper role-based handling
    // Don't manage UI state here - let processUserClaims handle it after roles are determined
    console.log('🔄 Deferring UI management to claims processing...');
        
    // Close login modal after successful authentication
    setShowLogin(false);
        
    // Force check current auth state
    const currentUser = firebaseAuth.currentUser;
    console.log('Current user in login success:', currentUser);
        
    if (currentUser) {
      console.log('Setting user state from login success');
      
      // Check if there's a stored intended route from before login
      const intendedRoute = sessionStorage.getItem('intended_route_after_login');
      if (intendedRoute) {
        console.log('📍 Navigating to intended route:', intendedRoute);
        sessionStorage.removeItem('intended_route_after_login'); // Clean up
        navigate(intendedRoute);
        // activeNavigation will be synced automatically by the useEffect
        return;
      }
      
      // Preserve the current URL if it's a valid route, otherwise default to callScript
      const currentPath = location.pathname;
      const validRoutes = ['/call-script', '/my-scripts', '/command-center', '/dashboard', '/admin-panel',
        '/team-manager', '/ai-call-evaluator', '/settings',
        '/admin-templates', '/problem-analyzer', '/marketplace', '/marketplace/purchases',
        '/marketplace/seller', '/marketplace/sell', '/marketplace/admin'];
      
      // If we're on login page (/) or an invalid route, go to call-script
      // Otherwise, stay on current page
      if (currentPath === '/' || !validRoutes.includes(currentPath)) {
        console.log('📍 Redirecting to default page: /call-script');
        navigate('/call-script');
        setActiveNavigation('callScript');
      } else {
        console.log('📍 Staying on current page:', currentPath);
        // activeNavigation will be synced automatically by the useEffect
      }
    }
  };





  // Delete template from Firestore
  const deleteTemplateFromFirestore = async (templateId) => {
    if (!db || !userId || !isAuthReady) {
      console.log('Cannot delete template: Database not ready or user not authenticated');
      return false;
    }

    if (!userTeam) {
      console.log('Cannot delete template: User team information not available');
      return false;
    }

    try {
      const appId = getArtifactProjectId();
      // Use the correct team-containerized path
      const templateRef = doc(db, `artifacts/${appId}/teams/${userTeam}/templates`, templateId);
            
      // Check if user has permission to delete this template
      const templateDoc = await getDoc(templateRef);
      if (!templateDoc.exists()) {
        console.log('Template not found:', templateId);
        return false;
      }
            
      const templateData = templateDoc.data();
      const creatorId = templateData?.metadata?.creatorId || templateData?.creatorId || templateData?.ownerId || templateData?.userId;
      const creatorTeam = templateData?.metadata?.creatorTeam || templateData?.creatorTeam || null;
      const isOwnTemplate = creatorId === userEmail;
      const isSameTeam = userTeam && creatorTeam && userTeam === creatorTeam;
            
      // Check basic permission to delete (example-script is now deletable if not last template)
      let canDelete = false;
      if (isAdmin) {
        canDelete = true;
      } else if (isTeamLead) {
        // Team leads without teams can only delete their own templates
        if (!userTeam) {
          canDelete = isOwnTemplate;
        } else {
          canDelete = (isOwnTemplate || isSameTeam);
        }
      } else {
        canDelete = isOwnTemplate;
      }
            
      if (!canDelete) {
        console.log('Access denied: User cannot delete this template');
        console.log('Delete permission check:', {
          userRole,
          isAdmin: isAdmin,
          isTeamLead: isTeamLead,
          isOwnTemplate,
          isSameTeam,
          userTeam,
          creatorTeam
        });
        return false;
      }
      
      // Check if this is the last template - prevent deletion if so
      const templatesRef = collection(db, `artifacts/${appId}/teams/${userTeam}/templates`);
      const templatesSnapshot = await getDocs(templatesRef);
      const templateCount = templatesSnapshot.size;
      
      if (templateCount <= 1) {
        console.log('❌ Cannot delete last template');
        toast.error('Cannot delete the last template. Create another template first.');
        return false;
      }
            
      await deleteDoc(templateRef);
      console.log('Template deleted from Firestore:', templateId);
      return true;
    } catch (error) {
      console.error('Error deleting template from Firestore:', error);
      return false;
    }
  };

  // Delete template (both from state and Firestore)
  const deleteScriptTemplate = async (templateId) => {
    // Count available templates
    const templateCount = Object.keys(availableScripts).length;
    
    // Prevent deleting the last template
    if (templateCount <= 1) {
      showError('Cannot delete the last template. Create another template first.');
      return;
    }

    // Show confirmation dialog
    const templateName = availableScripts[templateId]?.name || templateId;
    const confirmed = await confirmDialog(
      `Delete "${templateName}"? This cannot be undone.`,
      { variant: 'danger', confirmText: 'Delete' }
    );
    
    if (!confirmed) {
      return;
    }

    const deleteSuccess = await deleteTemplateFromFirestore(templateId);
    
    // Calculate remaining templates BEFORE state updates (for fallback logic)
    const remainingTemplateIds = Object.keys(availableScripts).filter(id => id !== templateId);
    console.log('🔍 Remaining templates after deletion:', remainingTemplateIds);
        
    if (deleteSuccess) {
      // Refresh templates from DataContext to get updated list
      if (refreshTemplates) {
        await refreshTemplates();
      }
            
      setAvailableScripts(prev => {
        const newScripts = { ...prev };
        delete newScripts[templateId];
        return newScripts;
      });
            
      // If the deleted template was the current one, switch to user's default template or example-script
      if (currentScriptTemplate === templateId) {
        console.log('🔄 Template being deleted was current template. Finding fallback...');
        console.log('🔍 defaultTemplate:', defaultTemplate);
        console.log('🔍 templateId being deleted:', templateId);
        console.log('🔍 Remaining templates:', remainingTemplateIds);
                
        let fallbackTemplate = 'example-script'; // Default fallback
                
        // For admins/team leads, use the app state defaultTemplate directly
        console.log('🔍 defaultTemplate from app state:', defaultTemplate);
                
        // If user has a default template and it's not the one being deleted, use it
        if (defaultTemplate && defaultTemplate !== templateId && remainingTemplateIds.includes(defaultTemplate)) {
          fallbackTemplate = defaultTemplate;
          console.log('✅ Using app state default template as fallback:', fallbackTemplate);
        } else if (remainingTemplateIds.length > 0) {
          // Prefer example-script if available, otherwise use first available
          fallbackTemplate = remainingTemplateIds.includes('example-script') ? 'example-script' : remainingTemplateIds[0];
          console.log('✅ Using available template as fallback:', fallbackTemplate);
        }
                
        setCurrentScriptTemplate(fallbackTemplate);
                
        // If the deleted template was the user's default, update their default template setting
        if (defaultTemplate === templateId && fallbackTemplate !== templateId) {
          console.log('🔄 Updating user default template from', templateId, 'to', fallbackTemplate);
          saveUserTemplatePreference(fallbackTemplate);
        }
      }
            
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } else {
      // If Firestore delete failed, still remove locally but show warning
      setAvailableScripts(prev => {
        const newScripts = { ...prev };
        delete newScripts[templateId];
        return newScripts;
      });
            
      if (currentScriptTemplate === templateId) {
        console.log('🔄 Template being deleted was current template (fallback case). Finding fallback...');
        console.log('🔍 defaultTemplate:', defaultTemplate);
        console.log('🔍 templateId being deleted:', templateId);
        console.log('🔍 Remaining templates:', remainingTemplateIds);
                
        let fallbackTemplate = 'example-script'; // Default fallback
                
        // For admins/team leads, use the app state defaultTemplate directly
        console.log('🔍 defaultTemplate from app state (fallback case):', defaultTemplate);
                
        // If user has a default template and it's not the one being deleted, use it
        if (defaultTemplate && defaultTemplate !== templateId && remainingTemplateIds.includes(defaultTemplate)) {
          fallbackTemplate = defaultTemplate;
          console.log('✅ Using app state default template as fallback (fallback case):', fallbackTemplate);
        } else if (remainingTemplateIds.length > 0) {
          // Prefer example-script if available, otherwise use first available
          fallbackTemplate = remainingTemplateIds.includes('example-script') ? 'example-script' : remainingTemplateIds[0];
          console.log('✅ Using available template as fallback (fallback case):', fallbackTemplate);
        }
                
        setCurrentScriptTemplate(fallbackTemplate);
                
        // If the deleted template was the user's default, update their default template setting
        if (defaultTemplate === templateId && fallbackTemplate !== templateId) {
          console.log('🔄 Updating user default template from', templateId, 'to', fallbackTemplate, '(fallback case)');
          saveUserTemplatePreference(fallbackTemplate);
        }
      }
            
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      console.warn('Template deleted locally but failed to delete from Firestore');
    }
  };

  // Apply script builder changes
  const applyScriptBuilderChanges = async (newTemplateData, templateName = null) => {
    // Ensure user team information is available before saving
    if (!userTeam) {
      console.log('⚠️ Cannot save template: User team information not yet loaded');
      setSaveStatus('Please wait for your account to fully load before saving templates');
      return;
    }
        
    const newScriptId = `script_${Date.now()}`;
    const defaultName = `Custom Script ${new Date().toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: '2-digit' 
    }).replace(/\//g, '/')}, ${new Date().toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    })}`;
    const newScriptName = templateName || defaultName;
        
    // Handle both new structure (with steps) and legacy structure (just graph)
    const newTemplate = { 
      name: newScriptName, 
      graph: newTemplateData.graph || newTemplateData, // Support both structures
      steps: newTemplateData.steps, // Include steps if present
      prospectForm: newTemplateData.prospectForm, // Include prospect form configuration
      createdAt: new Date(),
      creatorId: userEmail,
      creatorTeam: userTeam, // Ensure team info is included for new templates
      canEdit: true,
      isReadOnly: false
    };
        
    console.log('Saving template with team context:', { userTeam, userRole, userEmail });
        
    // Save to Firestore
    const saveSuccess = await saveTemplateToFirestore(newScriptId, newTemplate);
        
    if (saveSuccess) {
      setAvailableScripts(prev => ({
        ...prev,
        [newScriptId]: newTemplate
      }));
            
      setCurrentScriptTemplate(newScriptId);
            
      // Success notification shown by ScriptBuilder
      setSaveStatus('Template saved successfully to Firestore!');
    } else {
      // If Firestore save failed, still save locally but show warning
      setAvailableScripts(prev => ({
        ...prev,
        [newScriptId]: newTemplate
      }));
            
      setCurrentScriptTemplate(newScriptId);
            
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      console.warn('Template saved locally but failed to save to Firestore');
      setSaveStatus('Template saved locally but failed to sync to cloud');
    }
  };

  // Update existing template
  const updateScriptTemplate = useCallback(async (templateId, newTemplateData, templateName = null, isAutoSave = false, revisionOverride = null) => {
    console.log('App: updateScriptTemplate called with:', { templateId, newTemplateData, templateName, isAutoSave });
        
    // Ensure user team information is available before saving
    if (!userTeam) {
      console.log('⚠️ Cannot update template: User team information not yet loaded');
      setSaveStatus('Please wait for your account to fully load before updating templates');
      return;
    }

    // Revision saving is done after the successful save below (see post-save revision logic)
        
    // Handle both new structure (with steps) and legacy structure (just graph)
    const updatedTemplate = {
      name: templateName || availableScripts[templateId]?.name || 'Updated Script',
      graph: newTemplateData.graph || newTemplateData, // Support both structures
      steps: newTemplateData.steps, // Include steps if present
      prospectForm: newTemplateData.prospectForm, // Include prospect form configuration
      createdAt: availableScripts[templateId]?.createdAt || new Date(),
      updatedAt: new Date(),
      creatorId: availableScripts[templateId]?.creatorId || userEmail,
      creatorTeam: availableScripts[templateId]?.creatorTeam || userTeam, // Ensure team info is preserved
      canEdit: availableScripts[templateId]?.canEdit ?? true,
      isReadOnly: availableScripts[templateId]?.isReadOnly ?? false
    };
        
    console.log('App: Final updatedTemplate object to save:', updatedTemplate);
    console.log('Updating template with team context:', { userTeam, userRole, userEmail });
        
    // Save to Firestore
    const saveSuccess = await saveTemplateToFirestore(templateId, updatedTemplate);
        
    if (saveSuccess) {
      // Only update state if the template actually changed to prevent unnecessary re-renders
      setAvailableScripts(prev => {
        const currentTemplate = prev[templateId];
        // Check if there are actual changes (ignore metadata fields like updatedAt)
        if (currentTemplate && 
                    JSON.stringify(currentTemplate.graph) === JSON.stringify(updatedTemplate.graph) &&
                    JSON.stringify(currentTemplate.steps) === JSON.stringify(updatedTemplate.steps) &&
                    JSON.stringify(currentTemplate.prospectForm) === JSON.stringify(updatedTemplate.prospectForm) &&
                    currentTemplate.name === updatedTemplate.name) {
          // No changes, return previous state to prevent re-render
          return prev;
        }
        return {
          ...prev,
          [templateId]: updatedTemplate
        };
      });

      // Save a revision of the new state if content has changed since last revision
      const newContentHash = JSON.stringify({
        graph: updatedTemplate.graph,
        steps: updatedTemplate.steps,
        prospectForm: updatedTemplate.prospectForm,
        name: updatedTemplate.name,
      });
      if (lastRevisionGraphRef.current[templateId] !== newContentHash) {
        lastRevisionGraphRef.current[templateId] = newContentHash;
        saveRevision(userTeam, templateId, updatedTemplate, {
          reason: revisionOverride?.reason ?? (isAutoSave ? 'Auto-save' : 'Manual save'),
          triggeredBy: revisionOverride?.triggeredBy ?? (isAutoSave ? 'autosave' : 'manual_save'),
          userEmail,
        }).catch((err) => console.warn('⚠️ Revision snapshot failed:', err));
      }
            
      // Success notification shown by ScriptBuilder (not for autosave)
    } else {
      // If Firestore save failed, still update locally but show warning
      setAvailableScripts(prev => ({
        ...prev,
        [templateId]: updatedTemplate
      }));
            
      // Success notification shown by ScriptBuilder
      console.warn('Template updated locally but failed to save to Firestore');
    }
  }, [userTeam, userEmail, userId, isAuthReady, availableScripts]);

  // Handle script template change
  const handleScriptTemplateChange = async (templateId) => {
    // For agents, only allow their team-assigned template
    if (userRole === 'basic' && templateId !== defaultTemplate) {
      console.log('Agent cannot switch templates. Team-assigned template:', defaultTemplate);
      return;
    }
        
    setCurrentScriptTemplate(templateId);
        
    // Note: We don't save as default here - that only happens when user clicks "Set as Default" button
    // This just changes the current session template without modifying the user's saved default preference
        
    resetApp(); // Reset the conversation when changing templates
  };

  // Update urgency and category based on collected data
  const updateUrgencyAndCategory = useCallback((propertyDetails, leadCategory, setLeadCategory, setUrgency) => {
    let currentUrgency = 'Low';
    let currentCategory = 'Add to Nurture Campaign';
        
    if (
      propertyDetails.vacantReason === 'Thinking of Selling' ||
            propertyDetails.purchaseStrategy === 'Need Sale Funds' ||
            propertyDetails.leaseType === 'Month-to-Month' ||
            propertyDetails.openToAcceleration ||
            leadCategory === 'Appointment Scheduled' ||
            propertyDetails.propertyStatus === 'Owner Occupied'
    ) {
      currentUrgency = 'High';
      currentCategory = 'Immediate Appointment Required';
    } else if (propertyDetails.timeline === '3-5 Years' ||
            (propertyDetails.relocation === 'Relocating' && !propertyDetails.idealMoveDate) ||
            propertyDetails.priority === 'Money' ||
            (propertyDetails.propertyStatus === 'Rented' && propertyDetails.leaseType === 'Annual' && propertyDetails.tenantsStaying === false)
    ) {
      currentUrgency = 'Medium';
      if (leadCategory !== 'Appointment Scheduled' && leadCategory !== 'Immediate Appointment Required') {
        currentCategory = 'Schedule Follow-Up Call';
      }
    } else if (propertyDetails.timeline === '5+ Years' ||
            propertyDetails.mayRetain ||
            propertyDetails.wantsContact === false ||
            (propertyDetails.propertyStatus === 'Rented' && propertyDetails.leaseType === 'Annual' && propertyDetails.tenantsStaying === true)
    ) {
      currentUrgency = 'Low';
      if (leadCategory !== 'Appointment Scheduled' && leadCategory !== 'Immediate Appointment Required' && leadCategory !== 'Schedule Follow-Up Call') {
        currentCategory = 'Add to Nurture Campaign';
      }
    }

    if (leadCategory === 'Appointment Scheduled') {
      setLeadCategory('Appointment Scheduled');
    } else if (leadCategory === 'Immediate Appointment Required' && currentCategory !== 'Appointment Scheduled') {
      setLeadCategory('Immediate Appointment Required');
    } else if (leadCategory === 'Schedule Follow-Up Call' && currentCategory === 'Add to Nurture Campaign') {
      setLeadCategory('Schedule Follow-Up Call');
    } else {
      setLeadCategory(currentCategory);
    }

    setUrgency(currentUrgency);
  }, []);

  // Serialize script graph for Firestore (remove functions)
  const serializeScriptGraph = (scriptGraph) => {
    const serialized = JSON.parse(JSON.stringify(scriptGraph));
        
    // Convert any function scripts to string representations
    Object.keys(serialized.nodes).forEach(nodeId => {
      const node = serialized.nodes[nodeId];
      if (typeof node.script === 'function') {
        node.script = node.script.toString();
      }
      if (typeof node.coaching === 'function') {
        node.coaching = node.coaching.toString();
      }
    });
        
    // FIRESTORE FIX: Replace __end_script__ with END_SCRIPT (Firestore doesn't allow fields starting/ending with __)
    if (serialized.ui?.positions && serialized.ui.positions['__end_script__']) {
      serialized.ui.positions['END_SCRIPT'] = serialized.ui.positions['__end_script__'];
      delete serialized.ui.positions['__end_script__'];
    }
        
    return serialized;
  };

  // Deserialize script graph from Firestore (restore functions if needed)
  const deserializeScriptGraph = (scriptGraph) => {
    const deserialized = { ...scriptGraph };
        
    // Convert string representations back to functions if they look like functions
    Object.keys(deserialized.nodes).forEach(nodeId => {
      const node = deserialized.nodes[nodeId];
      if (typeof node.script === 'string' && node.script.includes('function')) {
        try {
          // For now, just keep as string since we can't safely eval
          // This is a limitation - functions will be stored as strings
          console.log('Function script stored as string:', nodeId);
        } catch (error) {
          console.warn('Could not restore function for node:', nodeId);
        }
      }
    });
        
    // FIRESTORE FIX: Convert END_SCRIPT back to __end_script__ (reverse of serialization)
    if (deserialized.ui?.positions && deserialized.ui.positions['END_SCRIPT']) {
      deserialized.ui.positions['__end_script__'] = deserialized.ui.positions['END_SCRIPT'];
      delete deserialized.ui.positions['END_SCRIPT'];
    }
        
    return deserialized;
  };

  // Load user defaults and role information
  const loadUserDefaults = useCallback(async () => {
    console.log('=== LOAD USER DEFAULTS ===');
    console.log('🔍 Current state:', { currentScriptTemplate, defaultTemplate, userRole, userEmail });
    console.log('Database ready:', !!db);
    console.log('User ID:', userId);
    console.log('User Email:', userEmail);
    console.log('Auth ready:', isAuthReady);
    console.log('Available scripts keys:', Object.keys(availableScripts));
        
    if (!db || !userId || !isAuthReady || !userEmail) {
      console.log('❌ Cannot load user defaults: Database not ready or user not authenticated');
      console.log('db:', !!db, 'userId:', !!userId, 'isAuthReady:', isAuthReady, 'userEmail:', !!userEmail);
      return;
    }
        
    // If user is a team lead without a team, they're mid-signup - don't try to load defaults
    if (isTeamLead && !userTeam) {
      console.log('⏳ Team Lead without team - skipping data load until team is created');
      return;
    }

    try {
      // AuthContext handles user access state
      const appId = getArtifactProjectId();
      const emailDocId = getEmailDocumentId(userEmail);
            
      // Load user role and team information from team containers only
      let userDoc, userData, localUserRole, localUserTeam;
            
      // Load user data from team container
      // Use userTeam from custom claims for direct access (avoids listing all teams which requires admin permissions)
      if (userTeam) {
        console.log(`🔍 Loading user data directly from team: ${userTeam}`);
        const teamUserRef = doc(db, `artifacts/${appId}/teams/${userTeam}/users/${emailDocId}`);
        const teamUserDoc = await getDoc(teamUserRef);
                
        if (teamUserDoc.exists()) {
          userData = teamUserDoc.data();
          console.log(`✅ Found user data in team container (${userTeam}):`, userData);
          userDoc = teamUserDoc;
        }
      } else {
        // Fallback: search team containers (only admins can list all teams)
        console.log('🔍 No team in claims, searching team containers...');
        const teamsSnapshot = await getDocs(collection(db, `artifacts/${appId}/teams`));
              
        for (const teamDoc of teamsSnapshot.docs) {
          const teamUserRef = doc(db, `artifacts/${appId}/teams/${teamDoc.id}/users/${emailDocId}`);
          const teamUserDoc = await getDoc(teamUserRef);
                  
          if (teamUserDoc.exists()) {
            userData = teamUserDoc.data();
            console.log(`✅ Found user data in team container (${teamDoc.data().name}):`, userData);
            userDoc = teamUserDoc;
            break;
          }
        }
      }
            
      if (userDoc && userData) {
        // Set role and team information
        localUserRole = userData.role || 'basic';
        localUserTeam = userData.teamId || null;
        
        // Everyone uses defaultTemplate now (simplified architecture)
        const userDefaultTemplate = userData.defaultTemplate || 'example-script';
                
        // If user has a team, fetch the team name
        let teamName = null;
        if (localUserTeam) {
          try {
            const teamRef = doc(db, `artifacts/${appId}/teams/${localUserTeam}`);
            const teamDoc = await getDoc(teamRef);
            if (teamDoc.exists()) {
              teamName = teamDoc.data().name;
              console.log(`✅ Fetched team name: "${teamName}" for team ID: "${localUserTeam}"`);
            }
          } catch (error) {
            console.warn('Could not fetch team name for user:', userEmail, error);
          }
        }
                
        // Set app-specific state (AuthContext handles user role/team state)
        console.log(`📋 Setting defaultTemplate from user document: ${userDefaultTemplate}`);
        setDefaultTemplate(userDefaultTemplate);
                
        // Set global context for logging
        window.currentUserTeamId = localUserTeam;
        console.log('✅ User state after setting:', { localUserRole, localUserTeam, userTeamName: teamName, defaultTemplate: userDefaultTemplate, hasAccess: true });

        // Set current script template to user's default
        // For agents: this is team-assigned (read-only)
        // For team leads/admins: this is self-chosen
        if (userDefaultTemplate && availableScripts[userDefaultTemplate]) {
          console.log(`✅ Setting current template to user's default: ${userDefaultTemplate}`);
          setCurrentScriptTemplate(userDefaultTemplate);
        } else {
          console.log(`⚠️ Default template not found in available scripts: ${userDefaultTemplate}`);
          
          if (localUserRole === 'basic') {
            console.log('🔄 Agent: Falling back to team lead\'s default template');
            
            // Load team lead's default as fallback
            (async () => {
              try {
                const teamRef = doc(db, `artifacts/${appId}/teams/${localUserTeam}`);
                const teamDoc = await getDoc(teamRef);
                
                if (teamDoc.exists()) {
                  const teamData = teamDoc.data();
                  const teamLeadEmail = teamData.leadEmail;
                  
                  if (teamLeadEmail) {
                    console.log(`📧 Found team lead: ${teamLeadEmail}`);
                    const teamLeadDocId = getEmailDocumentId(teamLeadEmail);
                    // Load team lead's defaultTemplate from user document (not subcollection)
                    const teamLeadUserRef = doc(db, `artifacts/${appId}/teams/${localUserTeam}/users/${teamLeadDocId}`);
                    const teamLeadUserDoc = await getDoc(teamLeadUserRef);
                    
                    if (teamLeadUserDoc.exists()) {
                      const teamLeadData = teamLeadUserDoc.data();
                      const teamLeadTemplate = teamLeadData.defaultTemplate;
                      
                      if (teamLeadTemplate && availableScripts[teamLeadTemplate]) {
                        console.log(`✅ Using team lead's default template for agent: ${teamLeadTemplate}`);
                        setCurrentScriptTemplate(teamLeadTemplate);
                        return;
                      } else {
                        console.log(`⚠️ Team lead's default template not available: ${teamLeadTemplate}`);
                      }
                    }
                  }
                }
                
                // Final fallback: example-script
                console.log('⚠️ No team lead default found, falling back to example-script');
                setCurrentScriptTemplate('example-script');
              } catch (error) {
                console.error('❌ Error loading team lead default for agent:', error);
                setCurrentScriptTemplate('example-script');
              }
            })();
          }
        }

        // REMOVED: Redundant subcollection loading - defaultTemplate already loaded from user document above (line 1797)
      } else {
        // User not found in team containers - but custom claims should be authoritative
        console.log('ℹ️ User document not found in team containers, using custom claims data');
        console.log('Custom claims data:', { userRole, userTeam, userTeamName });
                
        // Don't reset user state if we have valid custom claims
        if (userRole && userTeam) {
          console.log('✅ Using custom claims data as authoritative source');
          // If we have available scripts but no template selected, pick the first available or example-script
          if (!currentScriptTemplate && Object.keys(availableScripts).length > 0) {
            const fallbackTemplate = availableScripts['example-script'] ? 'example-script' : Object.keys(availableScripts)[0];
            console.log(`🔄 No user doc found but scripts available — falling back to: ${fallbackTemplate}`);
            setDefaultTemplate(fallbackTemplate);
            setCurrentScriptTemplate(fallbackTemplate);
          }
        } else {
          console.log('❌ No valid custom claims data found');
          // defaultTemplate will be set when user data is loaded
        }
      }
      
      // NOTE: defaultTemplate has already been loaded from user document at line 1797
      // No additional loading needed here
    } catch (error) {
      console.error('❌ Error loading user defaults:', error);
      console.error('Error details:', error.message);
            
      // On permission-denied, keep current UI state but apply fallback if nothing is set
      const isPermissionDenied = error?.code === 'permission-denied' || /Missing or insufficient permissions/i.test(error?.message || '');
      if (isPermissionDenied) {
        if (currentScriptTemplate) {
          console.log('🔒 Permission denied while loading user defaults — preserving current template.');
          return; // Exit early; UI remains stable
        }
        // No template set yet — apply a safe fallback so the UI isn't stuck loading
        if (Object.keys(availableScripts).length > 0) {
          const fallbackTemplate = availableScripts['example-script'] ? 'example-script' : Object.keys(availableScripts)[0];
          console.log(`🔒 Permission denied but no template set — falling back to: ${fallbackTemplate}`);
          setDefaultTemplate(fallbackTemplate);
          setCurrentScriptTemplate(fallbackTemplate);
          setIsInitialLoad(false);
        } else {
          console.log('🔒 Permission denied and no scripts available yet — waiting for templates to load.');
        }
        return;
      }

      // Non-permission errors: conservative fallback only if nothing is set
      if (!currentScriptTemplate) {
        console.log('🔄 No current template set, applying safe fallback to example-script');
        setDefaultTemplate('example-script');
        setCurrentScriptTemplate('example-script');
        setIsInitialLoad(false);
      } else {
        console.log('ℹ️ Keeping existing currentScriptTemplate despite error:', currentScriptTemplate);
      }
            
      // CRITICAL FIX: Don't reset user role/team on defaults loading error
      // The user already has valid claims - this is just a template loading issue
      debugLog('User defaults loading failed, but preserving existing role/team', {
        userRole, userTeam, userTeamName
      });
            
      // Don't override assigned template here - let the proper loading logic handle it
      // setAssignedTemplate('example-script'); // Removed to prevent overriding real assignments
    } finally {
      // AuthContext handles loading state
    }
  }, [db, userId, isAuthReady, availableScripts, getEmailDocumentId, userRole, userTeam, userTeamName]);

  // Save user template preference to Firestore
  const saveUserTemplatePreference = useCallback(async (templateId, teamIdOverride = null) => {
    console.log('🔄 saveUserTemplatePreference called with:', { templateId, teamIdOverride, userTeam, userEmail, isAuthReady, db: !!db, userRole });
        
    // CRITICAL: Agents cannot save template preferences - their template is controlled by team lead
    if (userRole === 'basic') {
      console.log('⏭️ Skipping saveUserTemplatePreference for agent (template controlled by team lead)');
      return false;
    }
        
    if (!db || !userEmail || !isAuthReady) {
      console.log('❌ Cannot save template preference: Database not ready or user not authenticated', {
        db: !!db,
        userEmail: !!userEmail,
        isAuthReady
      });
      return false;
    }

    const currentUserTeam = teamIdOverride || userTeam;
    console.log('📍 Using team ID:', currentUserTeam, '(override:', teamIdOverride, 'userTeam:', userTeam, ')');

    try {
      const appId = getArtifactProjectId();
      const emailDocId = getEmailDocumentId(userEmail);
      // Use team containerized path
      if (!currentUserTeam) {
        console.log('❌ Cannot save template preference: User has no team assignment');
        return false;
      }
      // SIMPLIFIED: Write defaultTemplate directly to user document (not subcollection)
      const userDocRef = doc(db, `artifacts/${appId}/teams/${currentUserTeam}/users/${emailDocId}`);
      const firestorePath = `artifacts/${appId}/teams/${currentUserTeam}/users/${emailDocId}`;
      console.log('💾 Firestore path:', firestorePath);
            
      const dataToUpdate = {
        defaultTemplate: templateId,
        updatedAt: new Date()
      };
            
      console.log('💾 Saving user template preference:', templateId, 'for user:', userEmail, 'data:', dataToUpdate);
      await setDoc(userDocRef, dataToUpdate, { merge: true });
            
      console.log('✅ User template preference saved successfully to:', firestorePath);
      return true;
    } catch (error) {
      console.error('❌ Error saving user template preference:', error);
      console.error('❌ Error details:', error.code, error.message, error.stack);
      return false;
    }
  }, [db, userEmail, isAuthReady, getEmailDocumentId, userTeam]);

  // REMOVED: loadUserTemplateSettings function - defaultTemplate now comes directly from user document
  // The real-time listener (below) automatically syncs defaultTemplate from the user document

  useEffect(() => {
    // AuthContext handles access state based on user role and team
  }, [userTeam, hasAccess]);

  // Load team lead's default template when admin views another team
  useEffect(() => {
    const loadTeamLeadDefault = async () => {
      // Only for admins viewing another team
      // NOTE: Use realTeamId (admin's actual team) instead of userTeam (which auto-switches to viewed team)
      if (userRole !== 'admin' || !viewingTeamId || viewingTeamId === realTeamId || !db) {
        setViewingTeamLeadDefault(null);
        return;
      }

      try {
        const appId = getArtifactProjectId();
        const teamUsersRef = collection(db, `artifacts/${appId}/teams/${viewingTeamId}/users`);
        const usersSnapshot = await getDocs(teamUsersRef);
        
        // Find the team lead
        let teamLeadDefault = null;
        usersSnapshot.forEach(doc => {
          const userData = doc.data();
          if (userData.role === 'team_lead' && userData.defaultTemplate) {
            teamLeadDefault = userData.defaultTemplate;
            console.log(`📋 Found team lead default for team ${viewingTeamId}:`, teamLeadDefault);
          }
        });
        
        setViewingTeamLeadDefault(teamLeadDefault);
      } catch (error) {
        console.error('❌ Error loading team lead default:', error);
        setViewingTeamLeadDefault(null);
      }
    };

    loadTeamLeadDefault();
  }, [viewingTeamId, realTeamId, userRole, db]);

  // UNIFIED: Auto-select correct default template based on current team context
  // Track previous team context AND whether we've successfully auto-selected for it
  const previousTeamContext = useRef({ 
    viewingTeamId: null, 
    userTeam: null,
    hasAutoSelected: false,
    selectedTemplate: null
  });
  
  useEffect(() => {
    // Check if team context changed
    const teamContextChanged = 
      previousTeamContext.current.viewingTeamId !== viewingTeamId ||
      previousTeamContext.current.userTeam !== userTeam;
    
    // Determine which template should be selected for current context
    let templateToSelect = null;
    
    if (userRole === 'admin' && viewingTeamId && viewingTeamId !== realTeamId) {
      // Admin viewing another team → use that team lead's default
      templateToSelect = viewingTeamLeadDefault;
    } else if (defaultTemplate && defaultTemplate !== 'example-script') {
      // User on their own team → use their own default
      templateToSelect = defaultTemplate;
    }
    
    // Auto-select if:
    // 1. Team context changed (user switched teams), OR
    // 2. We haven't auto-selected yet for this context, OR
    // 3. The template to select just became available (was null, now has value)
    const shouldAutoSelect = 
      teamContextChanged || 
      !previousTeamContext.current.hasAutoSelected ||
      (templateToSelect && previousTeamContext.current.selectedTemplate !== templateToSelect);
    
    if (shouldAutoSelect && templateToSelect) {
      // On page refresh the URL may already contain a template the user had selected.
      // Respect that instead of overriding with the team default, unless the team
      // context actually changed (e.g. admin switched teams).
      const urlParams = new URLSearchParams(window.location.search);
      const urlTemplate = urlParams.get('template');
      const effectiveTemplate = (urlTemplate && !teamContextChanged) ? urlTemplate : templateToSelect;

      console.log(`🔄 Auto-selecting template: ${effectiveTemplate} (teamChanged: ${teamContextChanged}, urlTemplate: ${urlTemplate}, context: ${viewingTeamId || 'own team'})`);
      setCurrentScriptTemplate(effectiveTemplate);
      
      // Update context tracking
      previousTeamContext.current = {
        viewingTeamId,
        userTeam,
        hasAutoSelected: true,
        selectedTemplate: effectiveTemplate
      };
    } else if (teamContextChanged) {
      // Team changed but no template to select yet - update context but mark as not auto-selected
      previousTeamContext.current = {
        viewingTeamId,
        userTeam,
        hasAutoSelected: false,
        selectedTemplate: null
      };
    }
  }, [viewingTeamLeadDefault, viewingTeamId, realTeamId, userRole, defaultTemplate]);

  const loadTemplatesRef = useRef(loadTemplatesFromFirestore);
  const loadUserDefaultsRef = useRef(loadUserDefaults);
  useEffect(() => { loadTemplatesRef.current = loadTemplatesFromFirestore; }, [loadTemplatesFromFirestore]);
  useEffect(() => { loadUserDefaultsRef.current = loadUserDefaults; }, [loadUserDefaults]);

  // Set app initialized state on mount
  useEffect(() => {
    setAppInitialized(true);
  }, []);

  // App initialization - handles maintenance mode and template assignment (AuthContext handles auth)
  useEffect(() => {

    // Handle maintenance mode enforcement - preserve security
    if (isAuthenticated && userRole !== 'admin' && maintenanceEnabled) {
      console.log('🔧 Maintenance mode active - signing out non-admin user');
      logCriticalAction('AUTH', 'Non-admin user blocked by maintenance mode', {
        userRole: userRole,
        maintenanceEnabled: maintenanceEnabled
      });
      setShowLogin(false);
      signOut(firebaseAuth);
      return;
    }

    // Close login modal when user is authenticated AND has a team
    // Team Leads must have a team before the app can load
    if (isAuthenticated && hasAccess) {
      // If user is a team lead but has no team, they're mid-signup - keep showing loading
      if (isTeamLead && !userTeam) {
        console.log('⏳ Team Lead without team detected - waiting for team creation...');
        return; // Don't close modal, don't load app
      }
            
      setShowLogin(false);
      // DON'T reset activeNavigation here - it's initialized from URL and synced by useEffect
      // setActiveNavigation('callScript'); // REMOVED: This was breaking page refreshes
    }

    // Note: Template loading for agents is now handled by loadUserDefaults and real-time listener
    // This separate loading logic was causing conflicts and has been removed

    // Set global context for logging (masked in production for privacy)
    if (userEmail) {
      const parts = userEmail.split('@');
      window.currentUserEmail = (isTestEnvironment || process.env.NODE_ENV === 'development')
        ? userEmail
        : parts[0].slice(0, 3) + '***@' + (parts[1] || '');
      window.currentUserTeamId = userTeam;
    }
  }, [isAuthenticated, userRole, userTeam, userEmail, maintenanceEnabled, hasAccess, defaultTemplate]);

  // Track if this is the initial template load to prevent false notifications
  const [isInitialTemplateLoad, setIsInitialTemplateLoad] = useState(true);
    
  // Track pending template assignments that should be applied when call ends
  const [pendingTemplateAssignment, setPendingTemplateAssignment] = useState(null);
    
  // Function to check if user is currently in an active call
  const isInActiveCall = useCallback(() => {
    return currentCallSessionId && conversationHistory.length > 0;
  }, [currentCallSessionId, conversationHistory.length]);

  // Real-time listener for user document changes (template assignments, etc.)
  useEffect(() => {
    if (!isAuthenticated || !userEmail || !userTeam || !db) return;

    let isActive = true; // Cleanup guard
        
    const appId = getArtifactProjectId();
    const emailDocId = getEmailDocumentId(userEmail);
    const userDocPath = `artifacts/${appId}/teams/${userTeam}/users/${emailDocId}`;
        
    console.log('🔄 Setting up real-time listener for user document:', userDocPath);
    console.log('🔒 SECURITY: Listener setup for user:', userEmail, 'team:', userTeam);
        
    const unsubscribe = onSnapshot(
      doc(db, userDocPath),
      (docSnapshot) => {
        if (!isActive) return; // Skip if component unmounted
                
        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();
          console.log('📡 REAL-TIME LISTENER: User document updated:', userData);
          console.log('🔍 Current template state:', { currentScriptTemplate, defaultTemplate });
                    
          // Check if default template changed
          const newDefaultTemplate = userData.defaultTemplate;
          if (newDefaultTemplate && newDefaultTemplate !== defaultTemplate) {
            console.log(`🔄 Template assignment changed: ${defaultTemplate} → ${newDefaultTemplate}`);
            console.log('👤 User role:', userRole);
            console.log('📧 User email:', userEmail);
            console.log('🏁 Is initial load?', isInitialTemplateLoad);
            console.log('📊 Available scripts:', Object.keys(availableScripts));
            console.log('📊 Template in availableScripts?', !!availableScripts[newDefaultTemplate]);
            console.log('🔍 Full user data:', userData);
                        
            setDefaultTemplate(newDefaultTemplate);

            // On initial load the listener fires immediately with the
            // current document state. loadUserDefaults already handled
            // the first template selection, so skip the redundant switch.
            if (isInitialTemplateLoad) {
              console.log('🏁 Initial template load — syncing defaultTemplate only, no switch needed');
              setIsInitialTemplateLoad(false);
              setIsInitialLoad(false);
              return;
            }

            // For agents, handle template switching gracefully
            if (userRole === 'basic') {
              if (isInActiveCall()) {
                console.log(`⏳ Agent in active call - deferring template switch to: ${newDefaultTemplate}`);
                setPendingTemplateAssignment({
                  templateId: newDefaultTemplate,
                  templateName: availableScripts[newDefaultTemplate]?.name || `Template ${newDefaultTemplate}`,
                  assignedAt: new Date()
                });
              } else {
                console.log(`✅ Agent - switching current script to new assigned template: ${newDefaultTemplate}`);
                setCurrentScriptTemplate(newDefaultTemplate);
                setPendingTemplateAssignment(null);
                setIsInitialLoad(false);
              }
            }
                        
            // Check if template is available, if not, trigger template refresh and wait
            if (!availableScripts[newDefaultTemplate]) {
              console.log('⚠️ Assigned template not in availableScripts, refreshing templates...');
              // Trigger template refresh from DataContext
              if (refreshTemplates) {
                refreshTemplates();
              }
                            
              // Wait a bit for templates to load, then show notification
              setTimeout(() => {
                const templateName = availableScripts[newDefaultTemplate]?.name || `Template ${newDefaultTemplate}`;
                if (userRole === 'basic') {
                  if (pendingTemplateAssignment) {
                    showInfo(`📋 New script template assigned: ${templateName}. It will be applied when you start your next call.`);
                  } else {
                    showInfo(`📋 Your script template has been updated to: ${templateName}. The new template is now active.`);
                  }
                } else if (userRole === 'team_lead' || userRole === 'admin') {
                  // Team leads and admins don't get notifications for template changes
                  // They are usually the ones making the changes, and can see their current template in the UI
                  console.log('🔇 Skipping notification for team lead/admin - they can see current template in UI');
                }
              }, 2000); // Wait 2 seconds for templates to load
            } else {
              // Template is available, show notification immediately
              const templateName = availableScripts[newDefaultTemplate]?.name;
              if (userRole === 'basic') {
                if (pendingTemplateAssignment) {
                  showInfo(`📋 New script template assigned: ${templateName}. It will be applied when you start your next call.`);
                } else {
                  showInfo(`📋 Your script template has been updated to: ${templateName}. The new template is now active.`);
                }
              } else if (userRole === 'team_lead' || userRole === 'admin') {
                // Team leads and admins don't get notifications for template changes
                // They are usually the ones making the changes, and can see their current template in the UI
                console.log('🔇 Skipping notification for team lead/admin - they can see current template in UI');
              }
            }
          }
        } else {
          console.log('⚠️ User document does not exist');
        }
      },
      (error) => {
        if (!isActive) return;
        console.error('❌ Error listening to user document changes:', error);
      }
    );

    return () => {
      isActive = false; // Mark as inactive
      console.log('🔇 Cleaning up user document listener');
      console.log('🔒 SECURITY: Listener cleanup for user:', userEmail, 'team:', userTeam);
      unsubscribe();
    };
  }, [isAuthenticated, userEmail, userTeam, db, userRole, availableScripts, getEmailDocumentId, isInitialTemplateLoad, isInActiveCall]);

  // Track if user defaults have been loaded to prevent re-running after autosave
  const userDefaultsLoadedRef = useRef(false);
    
  // Reset user defaults loaded flag when user changes
  useEffect(() => {
    userDefaultsLoadedRef.current = false;
  }, [userId]);
    
  // Load user defaults ONLY after templates are fully loaded (prevents race conditions)
  useEffect(() => {
    if (!templatesLoading && isAuthReady && userId && db && userRole && hasAccess && Object.keys(availableScripts).length > 0 && !userDefaultsLoadedRef.current) {
      console.log('✅ Templates loaded and user has access, loading user template defaults...');
            
      // Load user defaults (includes defaultTemplate from user document)
      if (loadUserDefaultsRef.current) {
        console.log('🔄 Calling loadUserDefaults...');
        loadUserDefaultsRef.current().catch(console.error);
        userDefaultsLoadedRef.current = true; // Mark as loaded to prevent re-running
      }
            
      // REMOVED: loadUserTemplateSettings - now defaultTemplate comes directly from user document
    }
  }, [templatesLoading, isAuthReady, userId, db, userRole, hasAccess, userEmail, userTeam]);

  // Set template ready state when initial loading is complete
  useEffect(() => {
    const anyLoading = templatesLoading || isLoadingData;
    console.log('🔍 Template ready check:', { 
      isInitialLoad, 
      currentScriptTemplate, 
      templatesLoading,
      isLoadingData,
      anyLoading,
      hasTemplate: !!currentScriptTemplate,
      willSetReady: !isInitialLoad && !!currentScriptTemplate && !anyLoading
    });
        
    if (!isInitialLoad && currentScriptTemplate && !anyLoading) {
      console.log('✅ Template fully loaded and ready:', currentScriptTemplate);
      setIsTemplateReady(true);
    } else if (isInitialLoad || !currentScriptTemplate || anyLoading) {
      console.log('⏳ Template not ready yet:', { isInitialLoad, currentScriptTemplate, anyLoading });
      setIsTemplateReady(false);
    }
  }, [isInitialLoad, currentScriptTemplate, templatesLoading, isLoadingData]);

  // Poll for AI personalization completion using direct Firestore reads
  // (avoids triggering isLoadingData which would cause UI flicker)
  useEffect(() => {
    const template = availableScripts[currentScriptTemplate];
    const status = template?.aiPersonalization?.status;
    
    if (status !== 'pending' || !refreshTemplates || !db || !userTeam) return;

    console.log('🤖 AI personalization pending — starting silent polling');
    let elapsed = 0;
    let cancelled = false;
    const POLL_INTERVAL = 5000;
    const MAX_WAIT = 60000; // 60 seconds safety net

    const checkStatus = async () => {
      try {
        const appId = getArtifactProjectId();
        const templatePath = `artifacts/${appId}/teams/${userTeam}/templates/${currentScriptTemplate}`;
        const templateDoc = await getDoc(doc(db, templatePath));
        if (cancelled) return;
        
        const data = templateDoc.data();
        const newStatus = data?.aiPersonalization?.status;
        console.log(`🔍 AI personalization status check: ${newStatus}`);
        
        if (newStatus && newStatus !== 'pending') {
          console.log('✅ AI personalization complete — refreshing templates');
          refreshTemplates();
          return; // Stop polling
        }
      } catch (err) {
        console.warn('⚠️ AI personalization poll error:', err.message);
      }

      elapsed += POLL_INTERVAL;
      if (elapsed >= MAX_WAIT) {
        console.log('⏰ AI personalization polling timed out');
        return;
      }
      if (!cancelled) {
        setTimeout(checkStatus, POLL_INTERVAL);
      }
    };

    // Start first check after the interval
    const timer = setTimeout(checkStatus, POLL_INTERVAL);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [currentScriptTemplate, availableScripts, refreshTemplates, db, userTeam]);

  // User status management now handled via Custom Claims
  useEffect(() => {
    if (db && userEmail && getEmailDocumentId && isAuthReady) {
      console.log('🔄 Database ready - user status managed via Custom Claims...');
    }
  }, [db, userEmail, getEmailDocumentId, isAuthReady]);

  // Update stage configuration when stage changes
  useEffect(() => {
    if (appStage !== 'salesFlow') return;
        
    const config = getStageConfig(currentStage);
    setCurrentStageConfig(config);
    updateUrgencyAndCategory(propertyDetails, leadCategory, setLeadCategory, setUrgency);
  }, [appStage, currentStage, propertyDetails, leadCategory, getStageConfig, updateUrgencyAndCategory]);

  // Initialize analytics service when user is authenticated
  useEffect(() => {
    if (db && userId && userEmail && userTeam && isAuthReady && !analytics) {
      console.log('📊 Initializing analytics service...', {
        userId,
        userEmail: userEmail ? '[REDACTED]' : 'null',
        userTeam,
        userTeamName
      });
      const analyticsService = new AnalyticsService(db, userId, userEmail, userTeam, userTeamName);
      analyticsService.init();
      setAnalytics(analyticsService);
            
      // Initialize first call session if we don't have one
      if (!currentCallSessionId) {
        const newSessionId = `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setCurrentCallSessionId(newSessionId);
                
        // Set analytics session ID to match call session ID
        analyticsService.setSessionId(newSessionId);
                
        // Track initial call start
        setTimeout(() => {
          analyticsService.trackCallEvent('call_start', {
            sessionId: newSessionId,
            template: currentScriptTemplate || 'example-script',
            startNode: 'opening'
          });
        }, 1000); // Small delay to ensure analytics is fully initialized
      }
    }
  }, [db, userId, userEmail, userTeam, userTeamName, isAuthReady, analytics, currentCallSessionId, currentScriptTemplate]);

  // Cleanup analytics on unmount
  useEffect(() => {
    return () => {
      if (analytics) {
        console.log('📊 Cleaning up analytics service');
        analytics.destroy();
      }
    };
  }, [analytics]);

  // Set up real-time listener for maintenance status
  useEffect(() => {
    if (!db) return;

    const appId = getArtifactProjectId();
    const maintenanceRef = doc(db, `artifacts/${appId}/global/maintenance`);
        
    try {
      const unsubscribe = onSnapshot(maintenanceRef, (doc) => {
        const newMaintenanceStatus = doc.exists() ? (doc.data().enabled || false) : false;
        setMaintenanceEnabled(newMaintenanceStatus);
        console.log('🔧 Maintenance mode updated:', newMaintenanceStatus);
                
        // If maintenance mode is enabled and user is not admin, sign them out
        if (newMaintenanceStatus && userId && userRole && userRole !== 'admin') {
          console.log('🔧 Maintenance mode enabled - signing out non-admin user');
          logCriticalAction('AUTH', 'Non-admin user signed out due to maintenance mode', {
            userRole: userRole,
            maintenanceEnabled: newMaintenanceStatus
          });
          // Reset login modal state to ensure user sees main login screen with maintenance message
          setShowLogin(false);
          handleLogout();
        }
      }, (error) => {
        if (error.code === 'permission-denied') {
          console.log('🔧 No permission to read maintenance status (normal for non-admin users)');
          setMaintenanceEnabled(false);
        } else {
          console.error('Error listening to maintenance status:', error);
          setMaintenanceEnabled(false);
        }
      });
      return () => unsubscribe();
    } catch (error) {
      console.error('Error setting up maintenance listener:', error);
      setMaintenanceEnabled(false);
    }
  }, [db, userId, userRole, handleLogout]);

  // Scroll to bottom of conversation
  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [conversationHistory]);

  // Save lead to Firestore with timeout protection and retry logic
  const saveLeadToFirestore = async (leadData, retryCount = 0) => {
    const maxRetries = 2; // Allow up to 2 retries (3 total attempts)
        
    logCriticalAction('DATA', 'Lead save initiated', {
      retryCount,
      hasDatabase: !!db,
      hasUserId: !!userId,
      isAuthReady,
      hasUserEmail: !!userEmail,
      userTeam: userTeam,
      leadDataKeys: Object.keys(leadData)
    });
        
    if (!db || !userId || !isAuthReady || !userEmail) {
      const errorMsg = 'Error: Database not ready or user not authenticated. Cannot save lead.';
      logCriticalAction('ERROR', 'Lead save failed - prerequisites not met', {
        hasDatabase: !!db,
        hasUserId: !!userId,
        isAuthReady,
        hasUserEmail: !!userEmail
      });
      setSaveStatus(errorMsg);
      return;
    }

    const attempt = retryCount + 1;
    const retryText = retryCount > 0 ? ` (Attempt ${attempt}/${maxRetries + 1})` : '';
    setSaveStatus(`Saving lead data...${retryText}`);
        
    try {
      const appId = getArtifactProjectId();
      const emailDocId = getEmailDocumentId(userEmail);
            
      // Save lead to team container (user must have a team with custom claims)
      if (!userTeam) {
        throw new Error('User has no team - cannot save lead. User must be assigned to a team.');
      }
            
      const leadsCollectionRef = collection(db, firestorePaths.getLeadsPath(appId, userTeam, emailDocId));
      console.log(`💾 Saving lead to team container: ${userTeam}`);

      // Build field label map from the current form config for label resolution in Leads
      const fieldLabels = {};
      if (currentProspectFormConfig?.fields) {
        currentProspectFormConfig.fields.forEach(f => {
          if (f.id && f.label) fieldLabels[f.id] = f.label;
        });
      }

      // Add timeout protection for the Firestore operation
      const savePromise = addDoc(leadsCollectionRef, {
        ...leadData,
        timestamp: new Date(),
        userId: userId,
        userEmail: userEmail,
        agentDisplayName: user?.displayName || '',
        _fieldLabels: fieldLabels
      });

      // Race between save operation and timeout (10 seconds for Firestore save)
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Firestore save timeout after 10 seconds')), 10000)
      );

      const docRef = await Promise.race([savePromise, timeoutPromise]);
            
      logCriticalAction('DATA', 'Lead saved successfully', {
        leadId: docRef.id,
        template: currentScriptTemplate,
        sessionId: currentCallSessionId,
        userTeam: userTeam,
        attempt: attempt,
        timestamp: new Date().toISOString()
      });
            
      setSaveStatus('Lead data saved successfully to Firestore!');
            
      // Track analytics asynchronously (don't block the save)
      if (analytics) {
        // Use setTimeout to make analytics non-blocking
        setTimeout(() => {
          analytics.trackCallEvent('lead_generated', {
            leadId: docRef.id,
            template: currentScriptTemplate,
            sessionId: currentCallSessionId
          }).catch(error => {
            console.warn('Analytics tracking failed (non-critical):', error);
          });
        }, 0);
      }
            
      // Update app stats
      setAppStats(prev => ({
        ...prev,
        leadsSaved: prev.leadsSaved + 1,
        lastSaveTime: new Date()
      }));

      return docRef; // Return the document reference for use in the calling function
    } catch (error) {
      logCriticalAction('ERROR', 'Lead save failed', {
        error: error.message,
        errorCode: error.code,
        attempt: attempt,
        maxRetries: maxRetries,
        isRetriable: isRetriableError(error),
        userTeam: userTeam,
        template: currentScriptTemplate
      });
            
      console.error(`Error saving lead data (attempt ${attempt}):`, error);
            
      // If we haven't exhausted retries and it's a retriable error, try again
      if (retryCount < maxRetries && isRetriableError(error)) {
        logCriticalAction('ACTION', 'Retrying lead save', {
          attempt: attempt,
          nextAttempt: attempt + 1,
          maxRetries: maxRetries + 1,
          error: error.message
        });
                
        console.log(`Retrying lead save in 2 seconds... (${attempt}/${maxRetries + 1})`);
        setSaveStatus(`Save failed, retrying in 2 seconds... (${attempt}/${maxRetries + 1})`);
                
        // Wait 2 seconds before retry
        await new Promise(resolve => setTimeout(resolve, 2000));
        return saveLeadToFirestore(leadData, retryCount + 1);
      }
            
      setSaveStatus(`Error saving lead: ${error.message}`);
      throw error; // Re-throw so calling function can handle
    }
  };

  // Helper function to determine if an error is retriable
  const isRetriableError = (error) => {
    const message = error.message.toLowerCase();
    return message.includes('timeout') || 
               message.includes('network') || 
               message.includes('unavailable') ||
               message.includes('deadline') ||
               message.includes('503') ||
               message.includes('500');
  };

  // Handle prospect data form submission
  const handleProspectDataSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (leadSaving) {
      console.log('Lead save already in progress, ignoring additional clicks');
      return;
    }

    const requiredFields = currentProspectFormConfig?.fields?.filter(f => f.required) || [];
    const missingFields = requiredFields.filter(f => !prospectDataForm[f.id]);

    if (missingFields.length > 0) {
      setProspectDataError(`Please fill in all required fields: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    setProspectDataError('');
    setLeadSaving(true); // Start loading state

    // Add timeout safeguard to prevent stuck state (reduced to 12 seconds)
    const saveTimeout = setTimeout(() => {
      console.error('Lead save timeout after 12 seconds');
      setLeadSaving(false);
      setProspectDataError('Save operation timed out. Please check your connection and try again.');
    }, 12000); // 12 second timeout

    try {
      const completeLeadData = {
        ...propertyDetails,
        ...prospectDataForm,
        template: currentScriptTemplate,
        timestamp: new Date(),
        callSessionId: currentCallSessionId || ''
      };

      // Track call completion event asynchronously (don't block the save)
      if (analytics) {
        console.log('✅ Tracking call_complete with lead generation, template:', currentScriptTemplate);
        // Make analytics non-blocking
        setTimeout(() => {
          analytics.trackCallEvent('call_complete', {
            sessionId: currentCallSessionId,
            template: currentScriptTemplate,
            finalNode: currentStage,
            stepsCompleted: conversationHistory.length,
            leadGenerated: true
          }).catch(error => {
            console.warn('Analytics tracking failed (non-critical):', error);
          });
        }, 0);
      }

      // Save to Firestore (this now includes lead_generated analytics)
      await saveLeadToFirestore({
        ...completeLeadData,
        conversationHistory: conversationHistory.map(entry => `${entry.role}: ${entry.text}`)
      });

      // Note: lead_generated analytics is now handled inside saveLeadToFirestore
      // No need for duplicate analytics call here

      // Mark that we just submitted a lead
      setJustSubmittedLead(true);
            
      // If user is on leads page, trigger a refresh to show the new lead
      if (activeNavigation === 'leads') {
        // Small delay to ensure Firestore has synced, then trigger a re-render
        setTimeout(() => {
          // Force leads to reload by updating a key prop
          window.dispatchEvent(new CustomEvent('leadSaved'));
        }, 1000);
      }

      // Automatically start a new call after saving
      setTimeout(() => {
        resetApp(true);
      }, 500); // Small delay to ensure save is complete
            
    } catch (error) {
      console.error('Error saving lead:', error);
            
      // Provide more specific error messages based on error type
      let errorMessage = 'Failed to save lead. Please try again.';
      if (error.message.includes('timeout')) {
        errorMessage = 'Save operation timed out. Please check your internet connection and try again.';
      } else if (error.message.includes('permission')) {
        errorMessage = 'Permission denied. Please check your account access and try again.';
      } else if (error.message.includes('network')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error.message.includes('quota')) {
        errorMessage = 'Storage quota exceeded. Please contact support.';
      } else if (error.message) {
        errorMessage = `Failed to save lead: ${error.message}`;
      }
            
      setProspectDataError(errorMessage);
    } finally {
      clearTimeout(saveTimeout); // Clear the timeout
      setLeadSaving(false); // Always reset loading state
    }
  };

  // Handle multiple open text responses
  const handleMultipleOpenTextResponses = (openTextButtons, multipleInputs) => {
    console.log('🔄 Handling multiple open text responses:', { openTextButtons, multipleInputs });
        
    // Update property details with all the collected data
    const newPropertyDetails = { ...propertyDetails };
        
    openTextButtons.forEach(button => {
      if (button.dataField && multipleInputs[button.value]) {
        newPropertyDetails[button.dataField] = multipleInputs[button.value].trim();
      }
    });
        
    setPropertyDetails(newPropertyDetails);
        
    // Add the agent's script to conversation history first
    if (currentStageConfig?.script) {
      setConversationHistory(prev => [...prev, {
        role: 'Agent',
        text: typeof currentStageConfig.script === 'function' 
          ? currentStageConfig.script(propertyDetails) 
          : currentStageConfig.script
      }]);
    }
        
    // Add the prospect's responses to conversation history
    const prospectResponses = openTextButtons
      .filter(button => button.label && multipleInputs[button.value])
      .map(button => `${button.label}-${multipleInputs[button.value].trim()}`)
      .join(', ');
        
    if (prospectResponses) {
      setConversationHistory(prev => [...prev, {
        role: 'Prospect',
        text: prospectResponses
      }]);
    }
        
    // Get the next node from the first open text button (they should all point to the same next node)
    const nextNode = openTextButtons[0]?.nextStage;
        
    if (nextNode) {
      console.log('✅ Moving to next stage:', nextNode);
      setPreviousStages(prev => [...prev, currentStage]);
            
      // Track node visit for analytics
      if (analytics && currentCallSessionId) {
        analytics.trackCallEvent('node_visited', {
          sessionId: currentCallSessionId,
          template: currentScriptTemplate,
          fromNode: currentStage,
          toNode: nextNode,
          responseType: 'multiple_text',
          response: 'multiple_text_inputs'
        });
      }
            
      setCurrentStage(nextNode);
    } else {
      console.log('❌ No next node found for multiple open text responses');
    }
        
    // Clear all multiple inputs
    setMultipleInputs({});
  };

  // Handle prospect response
  const handleProspectResponse = (response, type = 'button') => {
    // Initialize variables at the top to avoid temporal dead zone issues
    let prospectText = '';
    let nextNode;
    let conversationUpdates = [];
        
    // Get current graph early for objection handling
    const template = availableScripts?.[currentScriptTemplate];
    const currentGraph = template?.graph || template?.data?.scriptGraph || scriptGraph;
        
    // Handle special cases first
    if (response === 'back') {
      if (previousStages.length > 0) {
        const newPreviousStages = [...previousStages];
        const previousStage = newPreviousStages.pop();
        setPreviousStages(newPreviousStages);
        setCurrentStage(previousStage);
                
        // Remove the last two entries from conversation history (agent script + prospect response)
        setConversationHistory(prev => {
          const newHistory = [...prev];
          // Remove the last two entries if they exist
          if (newHistory.length >= 2) {
            newHistory.splice(-2);
          }
          return newHistory;
        });
                
        // Clear any text input
        setProspectInput('');
        return;
      }
      return;
    }

    if (response === 'end_call' || response === 'end_call_no_notes') {
      setCurrentStage('call_end');
      setRightPanelContent('prospectData');
      setProspectInput('');
      return;
    }

    // Handle objection responses
    if (type === 'objection' && response.startsWith('objection_') && availableScripts && currentScriptTemplate) {
      const objectionId = response.replace('objection_', '');
      const objectionNodeId = `objection_${objectionId}`;
            
      console.log('🔍 OBJECTION DEBUG: Handling objection selection', {
        objectionId,
        objectionNodeId,
        currentStage,
        previousStagesBeforeAdd: [...previousStages],
        previousStagesLength: previousStages.length
      });
            
      // Find the objection in the current step
      const currentStep = availableScripts[currentScriptTemplate]?.steps?.find(step => step.id === currentStageConfig?.step);
      const objection = currentStep?.objectionResponses?.find(obj => obj.id === objectionId);
            
      if (objection) {
        // Reset conversation updates array for objection handling
        conversationUpdates = [];
                
        // Add the agent's script to conversation history
        if (currentStageConfig?.script) {
          conversationUpdates.push({
            role: 'Agent',
            text: typeof currentStageConfig.script === 'function' 
              ? currentStageConfig.script(propertyDetails) 
              : currentStageConfig.script
          });
        }
                
        // Add the prospect's objection to conversation history
        conversationUpdates.push({
          role: 'Prospect',
          text: objection.label
        });
                
        // Update conversation history
        setConversationHistory(prev => [...prev, ...conversationUpdates]);
                
        // Transition to the objection handling node
        console.log('🔍 OBJECTION DEBUG: Saving current stage to previousStages before transition', {
          stageToBeSaved: currentStage,
          isCurrentStageObjection: currentStage?.startsWith('objection_'),
          previousStagesBeforeAdd: [...previousStages],
          previousStagesLength: previousStages.length
        });
                
        // Strategy: Always ensure previousStages has at least one non-objection node
        // so that "Continue" can return somewhere useful
        if (!currentStage?.startsWith('objection_')) {
          // We're transitioning from a regular node to an objection - save it
          setPreviousStages(prev => {
            const newStages = [...prev, currentStage];
            console.log('🔍 OBJECTION DEBUG: Updated previousStages (non-objection node saved)', {
              oldStages: prev,
              newStages,
              addedStage: currentStage
            });
            return newStages;
          });
        } else {
          // We're transitioning from an objection to another objection
          // Keep the existing previousStages (which should have the original question)
          console.log('🔍 OBJECTION DEBUG: On objection -> objection transition, keeping previousStages:', [...previousStages]);
                    
          // If previousStages is somehow empty, populate it from the objection's step
          if (previousStages.length === 0) {
            console.log('🔍 OBJECTION DEBUG: previousStages is empty! Finding original question from objection step');
            const currentNode = currentGraph?.nodes?.[currentStage];
            const originalQuestionStep = currentNode?.step;
                        
            if (originalQuestionStep) {
              // Find the first non-objection node in this step
              const nodesInStep = Object.entries(currentGraph?.nodes || {})
                .filter(([id, node]) => node.step === originalQuestionStep && !id.startsWith('objection_'))
                .map(([id]) => id);
                            
              if (nodesInStep.length > 0) {
                const originalNode = nodesInStep[0];
                console.log('🔍 OBJECTION DEBUG: Found and saving original node:', originalNode);
                setPreviousStages([originalNode]);
              }
            }
          }
        }
                
        console.log('🔍 OBJECTION DEBUG: Transitioning to objection node', {
          from: currentStage,
          to: objectionNodeId
        });
                
        setCurrentStage(objectionNodeId);
        setProspectInput('');
                
        return;
      }
    }

    // Reset conversation updates array for main flow
    conversationUpdates = [];
        
    // Add the agent's script to conversation history
    if (currentStageConfig?.script) {
      conversationUpdates.push({
        role: 'Agent',
        text: typeof currentStageConfig.script === 'function' 
          ? currentStageConfig.script(propertyDetails) 
          : currentStageConfig.script
      });
    }

    // Add the prospect's response to conversation history
    if (type === 'text' && prospectInput.trim()) {
      prospectText = prospectInput.trim();
    } else if (type === 'button' || type === 'predetermined') {
      console.log('Processing button response:', response);
      console.log('Button type:', type);
            
      // Get the button label from the current stage config
      const currentButton = currentStageConfig?.buttons?.find(button => button.value === response);
      if (currentButton && currentButton.label) {
        prospectText = currentButton.label;
        console.log('✅ Found button label:', prospectText);
      } else {
        // Fallback to hardcoded map for common responses
        const responseMap = {
          'yes': 'Yes, I\'m open to selling',
          'not_interested': 'No, not interested',
          'already_have_agent': 'We already have an agent',
          'wrong_time': 'Wrong time',
          'money_priority': 'Getting the Most Money',
          'timing_priority': 'Timing is Most Important',
          'both_important': 'Both are important',
          'market_not_right': 'Market isn\'t right',
          'vacant': 'Vacant',
          'rented': 'Rented',
          'owner_occupied': 'Owner Occupied',
          'renovations': 'Planning renovations',
          'thinking_selling': 'Thinking of Selling',
          'submit_rent': prospectInput.trim() || 'Submit Amount',
          'annual': 'Annual Lease',
          'month_to_month': 'Month-to-Month',
          'tenants_staying': 'Tenants Staying',
          'vacant_closing': 'Deliver Vacant at Closing',
          'may_retain': 'Yes, may retain',
          'will_sell': 'No, will sell',
          'need_more_time': 'Need more time',
          'not_sure_timing': 'Not sure if now is right time',
          'open_acceleration': 'Yes, open to acceleration',
          'not_open_acceleration': 'No, not open',
          'higher_prices': 'Higher prices than today',
          'faster_sale': 'Faster sale process',
          'less_hassle': 'Less hassle/stress involved',
          'life_event': 'A specific life event',
          'just_curious': 'Just curious, not really selling',
          'actively_listed': 'Actively listed',
          'agent_future': 'Have an agent for future plans',
          'friend_family': 'Just a friend/family in real estate',
          'not_serious': 'Not serious about selling right now',
          'market_improve': 'Market needs to improve',
          'personal_reasons': 'Personal/family reasons',
          'job_uncertainty': 'Job/relocation uncertainty',
          'property_repairs': 'Property needs repairs/updates',
          'no_specific': 'No specific reason, just not now',
          'reconsider_yes': 'Yes, would reconsider',
          'reconsider_no': 'No, still hesitant',
          'need_more_info': 'Need more information/data',
          'schedule_tuesday': 'Tuesday evening works',
          'schedule_thursday': 'Thursday afternoon works',
          'another_time': 'Another time',
          'submit_time': prospectInput.trim() || 'Submit Time',
          'send_analysis': 'Send Market Analysis',
          'schedule_followup': 'Schedule Follow-up Call',
          'add_email_updates': 'Add to Email Updates',
          'submit_notes': prospectInput.trim() || 'Submit Notes',
          'end_call': 'End Call & Save',
          'end_call_no_notes': 'End Call & Save',
          'back': 'Go back to previous stage'
        };
        const mappedText = responseMap[response];
        prospectText = mappedText || response;
                
        if (!mappedText) {
          console.log('⚠️ Response not found in button config or fallback map:', response);
          console.log('Available buttons:', currentStageConfig?.buttons?.map(b => ({ value: b.value, label: b.label })));
        }
      }
            
      console.log('Response mapping debug:', {
        response,
        buttonLabel: currentButton?.label,
        finalProspectText: prospectText,
        availableButtons: currentStageConfig?.buttons?.length || 0
      });
    }

    console.log('Button response debug:', {
      response,
      type,
      prospectText,
      hasProspectText: !!prospectText
    });

    if (prospectText) {
      console.log('✅ Adding button response to conversation:', prospectText);
      conversationUpdates.push({
        role: 'Prospect',
        text: prospectText
      });
    } else {
      console.log('❌ No prospect text to add for button response');
    }

    // Update conversation history with all updates at once
    if (conversationUpdates.length > 0) {
      setConversationHistory(prev => {
        const newHistory = [...prev, ...conversationUpdates];
        console.log('New conversation history length:', newHistory.length);
        console.log('Added entries:', conversationUpdates.length);
        return newHistory;
      });
    }

    // Update property details based on response using script engine
    const newPropertyDetails = { ...propertyDetails };
        
    // Get the current node and response data from script engine
    const currentNode = scriptEngine.getNode(currentStage);
    const responseData = currentNode?.responses[response]?.data;
        
    // Update property details with response data
    if (responseData) {
      Object.assign(newPropertyDetails, responseData);
    }
        
    // Handle text input data
    if (type === 'text' && prospectInput.trim()) {
      // Check if current stage has open text responses
      const openTextButton = currentStageConfig?.buttons?.find(button => button.type === 'open_text');
            
      if (openTextButton && openTextButton.dataField) {
        // Store the text input in the specified data field
        newPropertyDetails[openTextButton.dataField] = prospectInput.trim();
      } else {
        // Fallback to stage-specific handling
        switch (currentStage) {
          case 'rent_amount':
            newPropertyDetails.rentAmount = prospectInput.trim();
            break;
          case 'closing_schedule_other':
            newPropertyDetails.appointmentDate = prospectInput.trim();
            break;
          case 'closing_notes':
            newPropertyDetails.additionalNotes = prospectInput.trim();
            break;
          default:
            // No specific handling for other stages
            break;
        }
      }
    }

    setPropertyDetails(newPropertyDetails);

    // Find next stage using script engine
    // (template and currentGraph already defined at top of function)
        
    if (!currentGraph || !currentGraph.nodes) {
      console.warn('⚠️ No valid graph found for template:', currentScriptTemplate);
      console.warn('   Template structure:', template ? Object.keys(template) : 'template is null');
    }
        
    const currentEngine = new ScriptEngine(currentGraph);
        
    logCriticalAction('ACTION', 'Processing prospect response', {
      currentStage,
      response: type === 'text' ? '[TEXT_INPUT]' : response,
      responseType: type,
      currentTemplate: currentScriptTemplate,
      availableScriptsCount: Object.keys(availableScripts).length,
      graphNodeCount: currentGraph.nodes ? Object.keys(currentGraph.nodes).length : 0,
      propertyDetailsCount: Object.keys(newPropertyDetails).length,
      conversationLength: conversationHistory.length
    });
        
    // Handle open text responses
    if (type === 'text') {
      // For text responses, find the open text button and get its next node
      const openTextButton = currentStageConfig?.buttons?.find(button => button.type === 'open_text');
      nextNode = openTextButton?.nextStage;
      console.log('📝 Text response - nextNode:', nextNode);
    } else {
      nextNode = currentEngine.getNextNode(currentStage, response, newPropertyDetails);
      console.log('📝 Button response - nextNode:', nextNode);
    }
        
    console.log('🎯 Final nextNode:', nextNode);
        
    // Handle missing node scenario - if current node doesn't exist, reset to valid start
    if (!nextNode && !currentEngine.getNode(currentStage)) {
      console.log('❌ Current node not found in script graph, attempting recovery...');
            
      // Find the first valid node or designated opening node
      const availableNodes = Object.keys(currentGraph.nodes || {});
      const openingNode = availableNodes.find(id => currentGraph.nodes[id].isOpeningNode);
      const fallbackNode = openingNode || availableNodes[0];
            
      if (fallbackNode) {
        console.log(`🔄 Recovering from invalid node "${currentStage}" to "${fallbackNode}"`);
                
        // Log this critical error for debugging
        logCriticalAction('ERROR', 'Invalid node recovery', {
          invalidNode: currentStage,
          recoveryNode: fallbackNode,
          template: currentScriptTemplate,
          availableNodes: availableNodes.length,
          userEmail: userEmail,
          timestamp: new Date().toISOString()
        });
                
        // Reset to the valid node
        setCurrentStage(fallbackNode);
        setProspectInput('');
                
        // Add a recovery message to conversation history
        conversationUpdates.push({
          role: 'System',
          text: 'Restarting conversation flow...'
        });
                
        // Update conversation history and exit early
        setConversationHistory(prev => [...prev, ...conversationUpdates]);
        return;
      } else {
        console.error('❌ No valid nodes found in script template');
        // If no valid nodes exist, show an error state
        setCurrentStage('__error__');
        return;
      }
    }
        
    // Handle special objection response - return to previous question
    if (nextNode === '__stay_current__' || nextNode === '__return_to_previous__') {
      console.log('🔍 RETURN DEBUG: Handling return from objection', {
        nextNode,
        currentStage,
        isObjectionNode: currentStage?.startsWith('objection_'),
        previousStagesLength: previousStages.length,
        previousStages: [...previousStages]
      });
            
      // If we're on an objection node, go back to the previous stage
      if (currentStage?.startsWith('objection_') && previousStages.length > 0) {
        console.log('🔍 RETURN DEBUG: Popping from previousStages');
        const newPreviousStages = [...previousStages];
        const previousStage = newPreviousStages.pop();
                
        console.log('🔍 RETURN DEBUG: Returning to stage', {
          poppedStage: previousStage,
          remainingStages: newPreviousStages,
          currentObjectionNode: currentStage
        });
                
        setPreviousStages(newPreviousStages);
        setCurrentStage(previousStage);
                
        // Remove the objection entries from conversation history
        // This removes the last 2 entries: the objection response (agent) and the objection selection (prospect)
        setConversationHistory(prev => {
          const newHistory = [...prev];
          // Remove the last 2 entries if they exist (objection response + objection selection)
          if (newHistory.length >= 2) {
            newHistory.splice(-2);
          }
          return newHistory;
        });
                
        setProspectInput('');
        return;
      } else {
        // For non-objection cases, add the response if it's meaningful
        // Ensure prospectText is defined and not the continue button text
        if (typeof prospectText === 'string' && prospectText && prospectText !== 'Continue with question') {
          setConversationHistory(prev => [...prev, {
            role: 'Prospect',
            text: prospectText
          }]);
        }
        setProspectInput('');
        return;
      }
    }
        
    if (nextNode) {
      logCriticalAction('ACTION', 'Stage transition', {
        fromStage: currentStage,
        toStage: nextNode,
        responseType: type,
        template: currentScriptTemplate,
        sessionId: currentCallSessionId,
        previousStagesCount: previousStages.length,
        isEndScript: nextNode === '__end_script__'
      });

      setPreviousStages(prev => [...prev, currentStage]);
            
      // Track node visit for analytics
      if (analytics && currentCallSessionId) {
        analytics.trackCallEvent('node_visited', {
          sessionId: currentCallSessionId,
          template: currentScriptTemplate,
          fromNode: currentStage,
          toNode: nextNode,
          responseType: type,
          response: type === 'text' ? 'text_input' : response
        });
      }
            
      setCurrentStage(nextNode);
            
      // Handle end script transition - automatically show prospect data collection
      if (nextNode === '__end_script__') {
        logCriticalAction('ACTION', 'End script reached - switching to prospect data', {
          fromStage: currentStage,
          sessionId: currentCallSessionId,
          template: currentScriptTemplate,
          totalStagesVisited: previousStages.length + 1
        });
        setRightPanelContent('prospectData');
      }
    } else {
      logCriticalAction('ERROR', 'No next stage found - attempting recovery', {
        currentStage: currentStage,
        response: type === 'text' ? '[TEXT_INPUT]' : response,
        responseType: type,
        template: currentScriptTemplate,
        availableNodes: currentGraph.nodes ? Object.keys(currentGraph.nodes) : []
      });
            
      // Recovery: Try to find a working fallback node or end the script
      // Note: currentGraph is already defined at the top of this function
            
      if (currentGraph?.nodes) {
        // Try to find the opening node first
        const openingNode = Object.keys(currentGraph.nodes).find(id => currentGraph.nodes[id].isOpeningNode);
                
        // Try to find any node that connects to end script
        const endConnectedNode = Object.keys(currentGraph.nodes).find(id => {
          const node = currentGraph.nodes[id];
          return node.responses && Object.values(node.responses).some(resp => resp.nextNode === '__end_script__');
        });
                
        // Use opening node, or end-connected node, or just end the script
        if (openingNode && openingNode !== currentStage) {
          console.log(`🔄 Recovery: Redirecting to opening node: ${openingNode}`);
          setCurrentStage(openingNode);
        } else if (endConnectedNode && endConnectedNode !== currentStage) {
          console.log(`🔄 Recovery: Redirecting to end-connected node: ${endConnectedNode}`);
          setCurrentStage(endConnectedNode);
        } else {
          console.log('🔄 Recovery: Ending script due to broken template');
          setCurrentStage('__end_script__');
          setRightPanelContent('prospectData');
        }
      } else {
        // No nodes available, end the script
        console.log('🔄 Recovery: No nodes available, ending script');
        setCurrentStage('__end_script__');
        setRightPanelContent('prospectData');
      }
    }
        
    // Clear inputs after processing
    setProspectInput('');
    setMultipleInputs({});
  };

  // Reset the application
  const resetApp = (isCompletion = false) => {
    logCriticalAction('ACTION', 'Application reset initiated', {
      isCompletion: isCompletion,
      currentStage: currentStage,
      currentSessionId: currentCallSessionId,
      conversationLength: conversationHistory.length,
      template: currentScriptTemplate,
      hasPropertyDetails: Object.keys(propertyDetails).length > 0
    });

    // Generate a new session ID for the upcoming call *before* tracking any events
    const newSessionId = `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Track appropriate event if there was an active call session
    if (analytics && currentCallSessionId && conversationHistory.length > 0) {
      // Only track completion here if a lead was NOT just submitted
      if (isCompletion && !justSubmittedLead) {
        // This is a "Start New Call" after successful completion
        console.log('🎯 Tracking call_complete with template:', currentScriptTemplate);
        analytics.trackCallEvent('call_complete', {
          sessionId: currentCallSessionId, // Use the ID of the call that is ending
          template: currentScriptTemplate,
          finalNode: currentStage,
          stepsCompleted: conversationHistory.length,
          completionType: 'manual_new_call'
        });
      } else if (!isCompletion) {
        // This is a true reset/abandonment
        console.log('🔄 Tracking call_reset with template:', currentScriptTemplate);
        analytics.trackCallEvent('call_reset', {
          sessionId: currentCallSessionId, // Use the ID of the call that is being reset
          template: currentScriptTemplate,
          nodeId: currentStage,
          stepsCompleted: conversationHistory.length,
          resetReason: 'user_initiated'
        });
      }
    }
        
    // Now, set the new session ID for the call that is about to begin
    setCurrentCallSessionId(newSessionId);
        
    // Set analytics session ID to match the new call session ID
    if (analytics) {
      analytics.setSessionId(newSessionId);
    }
        
    // Reset the lead submission flag for the new call
    setJustSubmittedLead(false);
        
    // Apply any pending template assignment now that the call has ended
    if (pendingTemplateAssignment && userRole === 'basic') {
      console.log(`🔄 Applying pending template assignment: ${pendingTemplateAssignment.templateId}`);
      setCurrentScriptTemplate(pendingTemplateAssignment.templateId);
            
      // Show notification about template switch
      setTimeout(() => {
        showInfo(`📋 Template updated! Your new script template "${pendingTemplateAssignment.templateName}" is now active for this call.`);
      }, 500); // Small delay to let the UI update
            
      // Clear the pending assignment
      setPendingTemplateAssignment(null);
    }
        
    // Track the start of the new call with the new session ID
    if (analytics) {
      // Use setTimeout to ensure currentScriptTemplate is up-to-date
      setTimeout(() => {
        const templateToTrack = currentScriptTemplate || defaultTemplate || 'example-script';
        console.log('🚀 Final template for tracking:', templateToTrack);
        analytics.trackCallEvent('call_start', {
          sessionId: newSessionId,
          template: templateToTrack,
          startNode: 'opening'
        });
      }, 100); // Small delay to let state updates settle
    }
        
    setAppStage('salesFlow');
    setCurrentStage('opening');
    setPreviousStages([]);
    setPropertyDetails({});
    setConversationHistory([]);
    setSaveStatus('');
    setUrgency('Low');
    setLeadCategory('Nurture Campaign');
    setProspectInput('');
    setMultipleInputs({});
    setRightPanelContent('script');
    setProspectDataForm({});
    setProspectDataError('');
  };

  // Set template as default
  const setAsDefaultTemplate = async (templateId) => {
    if (!userId) return;

    // CRITICAL: Agents cannot set default template - their template is controlled by team lead
    if (userRole === 'basic') {
      console.log('⏭️ Agents cannot set default template (controlled by team lead)');
      return;
    }

    // Optimistically update the UI
    setDefaultTemplate(templateId);

    try {
      await saveUserDefaults({ defaultTemplate: templateId });
      console.log(`✅ Template ${templateId} set as default.`);
    } catch (error) {
      console.error('Error setting default template:', error);
      // Revert UI change if save fails
      // Consider fetching the old default to revert to, or just remove the optimistic update
    }
  };

  const saveUserDefaults = async (settings) => {
    console.log('🔄 saveUserDefaults called with:', { settings, userTeam, userEmail: !!userEmail, db: !!db, userId: !!userId, userRole });
        
    // CRITICAL: Agents cannot save template preferences - their template is controlled by team lead
    if (userRole === 'basic') {
      console.log('⏭️ Skipping saveUserDefaults for agent (template controlled by team lead)');
      return;
    }
        
    if (!db || !userId || !userEmail || !userTeam) {
      console.error('❌ Cannot save user defaults: missing auth info or team assignment', {
        db: !!db,
        userId: !!userId,
        userEmail: !!userEmail,
        userTeam: !!userTeam
      });
      return;
    }
    try {
      const appId = getArtifactProjectId();
      const emailDocId = getEmailDocumentId(userEmail);
      
      // SIMPLIFIED: Write directly to user document (not subcollection)
      const userDocRef = doc(db, `artifacts/${appId}/teams/${userTeam}/users/${emailDocId}`);
      const firestorePath = `artifacts/${appId}/teams/${userTeam}/users/${emailDocId}`;
      console.log('💾 Firestore path for saveUserDefaults:', firestorePath);
            
      const dataToSave = { 
        defaultTemplate: settings.defaultTemplate,
        updatedAt: new Date()
      };
      console.log('💾 Saving defaultTemplate to user document:', dataToSave);
            
      await setDoc(userDocRef, dataToSave, { merge: true });
      console.log('✅ User defaultTemplate saved successfully to:', firestorePath);
    } catch (error) {
      console.error('❌ Error saving user defaults:', error);
      console.error('❌ Error details:', error.code, error.message, error.stack);
      // Optionally, handle this with a user-facing message
    }
  };

  // Handle AI-powered features (e.g., generating custom scripts)
  const handleAIAssist = async (action, context) => {
    if (action === 'generateCustomScript') {
      // Implement logic for generating custom scripts
      console.log('Generating custom script...');
      // Add your custom script generation logic here
    }
  };

  // Enhanced data capture functions
  const captureScreenshot = async () => {
    try {
      const canvas = await html2canvas(document.body, {
        height: window.innerHeight,
        width: window.innerWidth,
        useCORS: true,
        allowTaint: true,
        scale: 0.5 // Reduce size for storage
      });
      return canvas.toDataURL('image/jpeg', 0.7); // Compress to JPEG
    } catch (error) {
      console.error('Failed to capture screenshot:', error);
      return null;
    }
  };

  const captureApplicationState = () => {
    return {
      currentStage: currentStage,
      appStage: appStage,
      conversationHistory: conversationHistory.slice(-10), // Last 10 entries
      propertyDetails: propertyDetails,
      urgency: urgency,
      leadCategory: leadCategory,
      currentScriptTemplate: currentScriptTemplate,
      activeNavigation: activeNavigation,
      userRole: userRole,
      userTeam: userTeam,
      userTeamName: userTeamName,
      templatesLoading: templatesLoading,
      availableScriptsCount: Object.keys(availableScripts || {}).length,
      prospectDataForm: Object.keys(prospectDataForm || {}).length > 0 ? 
        'Has data (hidden for privacy)' : 'Empty',
      windowSize: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      url: window.location.href,
      referrer: document.referrer || 'Direct'
    };
  };

  // Handle problem report submission
  const handleReportProblem = async () => {
    setIsCapturingData(true);
        
    try {
      // Capture screenshot
      const screenshot = await captureScreenshot();
      setCapturedScreenshot(screenshot);
            
      setShowFeedbackModal(true);
    } catch (error) {
      console.error('Error preparing problem report:', error);
      // Still show the modal even if screenshot fails
      setShowFeedbackModal(true);
    } finally {
      setIsCapturingData(false);
    }
  };

  const handleSubmitProblemReport = async (e) => {
    e.preventDefault();
        
    if (!feedbackDescription.trim()) {
      showWarning('Please add a description before submitting.');
      return;
    }

    if (!firebaseDb) {
      showError('Database not available. Please refresh the page and try again.');
      return;
    }

    if (!userEmail) {
      showError('User not authenticated. Please log in and try again.');
      return;
    }

    setIsSubmittingReport(true);

    const reportData = {
      // Feedback info
      feedbackType: feedbackType,
      userEmail: userEmail || 'Unknown',
      description: feedbackDescription.trim(),
      timestamp: new Date(),
            
      // Technical info
      userAgent: navigator.userAgent,
      url: window.location.href,
      referrer: document.referrer || 'Direct',
      appVersion: process.env.REACT_APP_VERSION || '0.1.1', // App version from package.json or env
            
      // User context
      userRole: userRole || 'Unknown',
      userTeam: userTeamName || 'Unknown',
      userTeamId: userTeam || 'Unknown', // Team ID (technical identifier)
      currentPage: activeNavigation || 'Unknown',
            
      // Application state
      applicationState: captureApplicationState(),
            
      // Console logs (last 50 entries)
      consoleLogs: getConsoleHistory().slice(-50),
            
      // Screenshot (if captured)
      screenshot: capturedScreenshot || null,
            
      // Additional debugging info
      debugInfo: {
        isAuthReady: isAuthReady,
        templatesLoading: templatesLoading,
        hasUnsavedChanges: scriptBuilderHasUnsavedChanges,
        maintenanceEnabled: maintenanceEnabled,
        appId: firebaseConfig?.projectId || 'Unknown',
        environment: process.env.NODE_ENV || 'Unknown',
        buildTime: process.env.REACT_APP_BUILD_TIME || 'Unknown',
        errorStates: {
          prospectDataError: !!prospectDataError,
          saveStatus: saveStatus
        }
      }
    };

    try {
      await addDoc(collection(firebaseDb, 'problem-reports'), reportData);
            
      // Reset form and close modal
      setFeedbackDescription('');
      setFeedbackType('bug');
      setCapturedScreenshot(null);
      setShowTechnicalDetails(false);
      setShowFeedbackModal(false);
            
      // Show success message
      showSuccess('Thank you for your feedback! We\'ll review it shortly.');
            
    } catch (error) {
      console.error('Error submitting problem report:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack,
        dbAvailable: !!firebaseDb,
        userEmail: userEmail,
        reportData: reportData
      });
      showError('Failed to submit problem report. Please try again.');
    } finally {
      setIsSubmittingReport(false);
    }
  };

  // Handle maintenance mode toggle
  const toggleMaintenance = async () => {
    if (!db || !firebaseConfig) return;
        
    setIsUpdatingMaintenance(true);
    try {
      const appId = getArtifactProjectId();
      const maintenanceRef = doc(db, `artifacts/${appId}/global/maintenance`);
      const newStatus = !maintenanceEnabled;
            
      await setDoc(maintenanceRef, {
        enabled: newStatus,
        updatedAt: new Date(),
        updatedBy: userEmail
      });
            
      console.info(`Maintenance mode ${newStatus ? 'enabled' : 'disabled'}`);
      // The real-time listener will update the state
    } catch (error) {
      console.error('Error updating maintenance mode:', error);
      showError(`Failed to update maintenance mode: ${error.message}`);
    } finally {
      setIsUpdatingMaintenance(false);
    }
  };

  // Team Management Functions (for future use)
  // const createTeam = async (teamName, leadEmail) => { ... };
  // const inviteUserToTeam = async (userEmail, teamId, role) => { ... };
  // const assignTemplateToTeam = async (teamId, templateId) => { ... };

  // Test function to manually create a template
  const testCreateTemplate = useCallback(async () => {
    console.log('Testing template creation...');
    const testTemplateId = `test_template_${Date.now()}`;
    const testTemplateData = {
      name: 'Test Template',
      graph: scriptGraph,
      createdAt: new Date()
    };
        
    console.log('Test template data:', testTemplateData);
        
    // First, let's test if we can read from the global templates collection
    try {
      const appId = getArtifactProjectId();
      const templatesCollectionRef = collection(db, `artifacts/${appId}/templates`);
      console.log('Testing read access to global templates collection...');
      const querySnapshot = await getDocs(templatesCollectionRef);
      console.log('Read access successful, found', querySnapshot.size, 'templates');
    } catch (error) {
      console.error('Read access failed:', error);
    }
        
    const saveSuccess = await saveTemplateToFirestore(testTemplateId, testTemplateData);
    if (saveSuccess) {
      console.log('Test template created successfully');
      // Template reloading now handled by DataContext
      console.log('Template reloading disabled - DataContext will handle updates');
    } else {
      console.log('Test template creation failed');
    }
  }, [saveTemplateToFirestore, loadTemplatesFromFirestore, db]);

  // Test function to set default template
  const testSetDefault = useCallback(async () => {
    console.log('Testing set default functionality...');
    const customTemplates = Object.keys(availableScripts).filter(id => id !== 'example-script');
    if (customTemplates.length > 0) {
      const testTemplateId = customTemplates[0];
      console.log('Testing set default with existing template:', testTemplateId);
      await setAsDefaultTemplate(testTemplateId);
    } else {
      console.log('No custom templates available for testing');
    }
  }, [setAsDefaultTemplate, availableScripts]);

  // Check Firestore permissions and provide guidance
  const checkFirestorePermissions = useCallback(async () => {
    if (!db || !userId || !userEmail) {
      console.log('Cannot check permissions: Database or user not ready');
      return;
    }

    try {
      const appId = getArtifactProjectId();
      const emailDocId = getEmailDocumentId(userEmail);
            
      console.log('=== Firestore Permissions Check ===');
      console.log('Project ID:', appId);
      console.log('User ID:', userId);
      console.log('User Email:', userEmail ? '[REDACTED]' : 'null');
      console.log('Email Document ID:', emailDocId);
            
      // Test global templates collection access
      const templatesCollectionRef = collection(db, `artifacts/${appId}/templates`);
      console.log('Testing global templates collection access...');
            
      // Test read access
      try {
        const querySnapshot = await getDocs(templatesCollectionRef);
        console.log('✅ READ access to global templates: SUCCESS (found', querySnapshot.size, 'templates)');
      } catch (error) {
        console.log('❌ READ access to global templates: FAILED -', error.message);
      }
            
      // Test user data collections (team containerized)
      if (!userTeam) {
        console.log('⚠️ Cannot test user data access: No team assignment');
        return;
      }
      const userDefaultsRef = doc(db, `artifacts/${appId}/teams/${userTeam}/users/${emailDocId}/userDefaults`, 'templateSettings');
      const leadsCollectionRef = collection(db, `artifacts/${appId}/teams/${userTeam}/users/${emailDocId}/realEstateLeads`);
            
      // Test user defaults access
      try {
        const userDefaultsDoc = await getDoc(userDefaultsRef);
        console.log('✅ READ access to user defaults: SUCCESS');
      } catch (error) {
        console.log('❌ READ access to user defaults: FAILED -', error.message);
      }
            
      // Test leads collection access
      try {
        const leadsSnapshot = await getDocs(leadsCollectionRef);
        console.log('✅ READ access to leads collection: SUCCESS (found', leadsSnapshot.size, 'leads)');
      } catch (error) {
        console.log('❌ READ access to leads collection: FAILED -', error.message);
      }
            
      // Test write access with a test document
      try {
        const testDocRef = doc(templatesCollectionRef, `test_permission_${Date.now()}`);
        await setDoc(testDocRef, { test: true, timestamp: new Date() });
        console.log('✅ WRITE access to global templates: SUCCESS');
                
        // Clean up test document
        await deleteDoc(testDocRef);
        console.log('✅ DELETE access to global templates: SUCCESS');
      } catch (error) {
        console.log('❌ WRITE access to global templates: FAILED -', error.message);
        console.log('=== FIREBASE SECURITY RULES INFO ===');
        console.log('Security rules are configured to use Custom Claims and team containers.');
        console.log('Legacy paths are no longer accessible.');
      }
            
    } catch (error) {
      console.error('Error checking permissions:', error);
    }
  }, [db, userId, userEmail, getEmailDocumentId, userTeam]);

    

  // Safety: clear loading state if it takes too long (e.g., empty collection or offline)
  useEffect(() => {
    if (!templatesLoading) return;
    const timeoutId = setTimeout(() => {
      console.warn('Templates load timed out; clearing loading state');
      setTemplatesLoading(false);
    }, 15000); // 15s — gives DataContext time to finish even on slow connections
    return () => clearTimeout(timeoutId);
  }, [templatesLoading]);

  // Debug logging effects (moved out of render to prevent setState during render warnings)
  useEffect(() => {
    if (!isAuthReady) {
      debugLog('Auth not ready, showing loading screen', { isAuthReady, isLoadingUserAccess });
    }
  }, [isAuthReady, isLoadingUserAccess]);

  useEffect(() => {
    if (!userEmail && isAuthReady) {
      debugLog('No user email, showing login screen', { userEmail, hasAccess, isLoadingUserAccess });
    }
  }, [userEmail, hasAccess, isLoadingUserAccess, isAuthReady]);

  useEffect(() => {
    if (userEmail && isLoadingUserAccess && !userRole) {
      debugLog('User authenticated but claims loading', { userEmail, isLoadingUserAccess, userRole, hasAccess });
    }
  }, [userEmail, isLoadingUserAccess, userRole, hasAccess]);

  useEffect(() => {
    if (userEmail && !hasAccess && !isLoadingUserAccess && userRole !== null) {
      debugLog('Access denied for user', { userEmail, hasAccess, userRole, userTeam });
    }
  }, [userEmail, hasAccess, isLoadingUserAccess, userRole, userTeam]);

  // Authentication Gate - Show login screen if not authenticated
  // IMPORTANT: Check auth BEFORE routing to prevent permission errors
  if (!isAuthReady) {
    // Show a minimal loading state only during initial Firebase setup
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-blue-700 mb-2">✨TreeGenie</h1>
            <p className="text-gray-600">The Sales Script Genie</p>
          </div>
                    
          <div className="text-center">
            <LoadingSpinner size="large" message="Initializing authentication..." />
            {DEBUG_AUTH && (
              <p className="text-xs text-gray-400 mt-1">
                                Debug: isAuthReady={String(isAuthReady)}, isLoadingUserAccess={String(isLoadingUserAccess)}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }





  // Handle branded team entry URLs (/team/:slug)
  // This must be checked BEFORE auth to allow unauthenticated access to branded login
  if (location.pathname.startsWith('/team/')) {
    const BrandedTeamEntry = lazyWithRetry(() => import('./components/BrandedTeamEntry'), 'BrandedTeamEntry');
    return (
      <React.Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <LoadingSpinner size="large" message="Loading..." />
        </div>
      }>
        <BrandedTeamEntry />
      </React.Suspense>
    );
  }

  // Show login screen if no user is authenticated  
  // CRITICAL FIX: Always allow login screen access regardless of claims status
  // Don't show login screen if auth is still loading (prevents race condition after login)
  if (!userEmail && !isAuthLoading) {
    // Store the intended destination if we're on a valid app route
    // This allows redirect back after login
    const currentPath = location.pathname;
    const validRoutes = ['/call-script', '/my-scripts', '/command-center', '/dashboard', '/admin-panel',
      '/team-manager', '/ai-call-evaluator', '/settings',
      '/admin-templates', '/problem-analyzer', '/marketplace', '/marketplace/purchases',
      '/marketplace/seller', '/marketplace/sell', '/marketplace/admin'];
    
    if (validRoutes.includes(currentPath) && currentPath !== '/') {
      // Store intended route in sessionStorage (cleared after use)
      sessionStorage.setItem('intended_route_after_login', currentPath);
      console.log('📍 Storing intended route after login:', currentPath);
    }
    
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Welcome to</h2>
            <h1 className="text-3xl font-bold text-blue-700 mb-2">✨TreeGenie</h1>
            <p className="text-gray-600">The Sales Script Genie</p>
          </div>
                    
          {/* Show deactivation message if user was deactivated */}
          {isDeactivated && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center mb-3">
                <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <h3 className="text-sm font-semibold text-red-800">Account Deactivated</h3>
              </div>
              <p className="text-sm text-red-700 mb-3">
                                Your account has been deactivated and you have been automatically logged out.
              </p>
              <p className="text-sm text-red-700">
                                To reactivate your account, please contact:
              </p>
              <ul className="text-sm text-red-700 ml-4 mt-2 list-disc">
                <li>Your team lead</li>
                <li>Your administrator</li>
                <li>Support team</li>
              </ul>
            </div>
          )}

          {/* Show removal message if user was removed from team */}
          {isRemoved && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center mb-3">
                <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                <h3 className="text-sm font-semibold text-red-800">Access Removed</h3>
              </div>
              <p className="text-sm text-red-700 mb-3">
                You have been removed from your team and no longer have access to the system.
              </p>
              <p className="text-sm text-red-700">
                If you believe this was a mistake, please contact:
              </p>
              <ul className="text-sm text-red-700 ml-4 mt-2 list-disc">
                <li>Your team lead</li>
                <li>Your administrator</li>
              </ul>
            </div>
          )}

          {/* Show session conflict message if user was signed out by another login */}
          {isSessionConflict && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center mb-3">
                <svg className="w-5 h-5 text-amber-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h3 className="text-sm font-semibold text-amber-800">Signed Out — Active Session Elsewhere</h3>
              </div>
              <p className="text-sm text-amber-700 mb-2">
                Your account was signed in from another browser or device, so this session was ended automatically.
              </p>
              <p className="text-sm text-amber-700">
                Each account can only be active in one browser at a time. Sign in here to continue on this device.
              </p>
            </div>
          )}

          {/* Maintenance Mode Message */}
          {maintenanceEnabled && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <h3 className="text-sm font-semibold text-red-800">Under Maintenance</h3>
              </div>
              <p className="text-sm text-red-700">
                                🚨 Site under maintenance, please log in again later
              </p>
            </div>
          )}
                    
          <div className="text-center">
            <div className="mb-6">
                           
              <p className="text-gray-600 mb-6">
                {maintenanceEnabled 
                  ? 'Only administrators can sign in during maintenance.'
                  : firebaseConfig.projectId === 'treegenie-test'
                    ? 'Sign in with an existing account. New account creation is restricted on the test environment.'
                    : 'Please sign in or create an account to get started.'
                }
              </p>
            </div>
                        
            <div className="space-y-3">
              <button
                onClick={() => {
                  setLoginMode('signin');
                  setShowLogin(true);
                }}
                className={`w-full py-3 px-6 rounded-lg transition duration-300 font-semibold ${
                  maintenanceEnabled 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {maintenanceEnabled ? 'Admin Sign In' : 'Sign In'}
              </button>
                            
              {/* Create Account: shown on prod always, on test only after admin override */}
              {!maintenanceEnabled && (!isTestEnvironment || adminOverrideActive) && (
                <button
                  onClick={() => {
                    setLoginMode('signup');
                    setShowLogin(true);
                  }}
                  className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition duration-300 font-semibold"
                >
                                Create Account
                </button>
              )}

              {/* Admin override for test environment */}
              {isTestEnvironment && !maintenanceEnabled && !adminOverrideActive && (
                <div className="mt-2">
                  {!showAdminOverride ? (
                    <button
                      onClick={() => setShowAdminOverride(true)}
                      className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      Admin Access
                    </button>
                  ) : (
                    <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Admin Password</label>
                      <div className="flex gap-2">
                        <input
                          type="password"
                          value={adminPassword}
                          onChange={(e) => { setAdminPassword(e.target.value); setAdminPasswordError(''); }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              if (adminPassword === process.env.REACT_APP_TEST_ADMIN_PASSWORD) {
                                setAdminOverrideActive(true);
                                setShowAdminOverride(false);
                                setAdminPasswordError('');
                              } else {
                                setAdminPasswordError('Incorrect password');
                              }
                            }
                          }}
                          className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter admin password"
                          autoFocus
                        />
                        <button
                          onClick={() => {
                            if (adminPassword === process.env.REACT_APP_TEST_ADMIN_PASSWORD) {
                              setAdminOverrideActive(true);
                              setShowAdminOverride(false);
                              setAdminPasswordError('');
                            } else {
                              setAdminPasswordError('Incorrect password');
                            }
                          }}
                          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          Unlock
                        </button>
                      </div>
                      {adminPasswordError && (
                        <p className="text-xs text-red-500 mt-1">{adminPasswordError}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
                    
          {/* Login Modal */}
          {showLogin && (
            <Login 
              onLogin={handleLoginSuccess}
              onClose={() => setShowLogin(false)}
              firebaseConfig={firebaseConfig}
              initialMode={loginMode}
              adminOverrideKey={adminOverrideActive ? adminPassword : null}
            />
          )}
        </div>
      </div>
    );
  }

  // Show loading screen for Team Leads without teams (mid-signup)
  if (userEmail && isTeamLead && !userTeam) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" message="Setting up your team..." />
          <p className="text-gray-500 text-sm mt-2">This will only take a moment</p>
        </div>
      </div>
    );
  }

  // Show claims loading for authenticated users (but only if we don't have basic info yet)
  // NEW: Progressive enhancement - allow basic access while claims load
  if (userEmail && isLoadingUserAccess && !userRole) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-blue-700 mb-2">✨TreeGenie</h1>
            <p className="text-gray-600">The Sales Script Genie</p>
          </div>
                    
          <div className="text-center">
            <LoadingSpinner size="large" message="Loading your permissions..." />
            <p className="text-xs text-gray-500 mt-2">Setting up your account</p>
            {DEBUG_AUTH && (
              <div className="text-xs text-gray-400 mt-2">
                <p>User: {userEmail}</p>
                <p>Role: {userRole || 'loading...'}</p>
                <p>Team: {userTeam || 'loading...'}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Access Control - More forgiving approach  
  // CRITICAL FIX: Only deny access if explicitly forbidden, not on loading failures
  if (userEmail && !hasAccess && !isLoadingUserAccess && userRole !== null) {
    // Show access denied if we have conclusive evidence user lacks access
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-lg w-full mx-4">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-blue-700 mb-2">✨TreeGenie</h1>
            <p className="text-gray-600">The Sales Script Genie</p>
          </div>

          <div className="text-center">
            <div className="mb-6">
              <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" strokeWidth={2}/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 9l-6 6m0-6l6 6" />
              </svg>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Access Denied</h2>
              <p className="text-gray-600 mb-4">Your account does not have access to this application.</p>
              <p className="text-sm text-gray-500 mb-6">
                                Role: {userRole || 'Unknown'} | Team: {userTeam || 'None'}
              </p>
              <button
                onClick={() => signOut(firebaseAuth)}
                className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 mr-4"
              >
                                Sign Out
              </button>
              <button
                onClick={() => window.location.reload()}
                className="bg-gray-600 text-white px-6 py-2 rounded hover:bg-gray-700"
              >
                                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ROUTER: Handle flowchart editor route separately (independent page)
  // Checked AFTER auth is ready to ensure user has proper permissions
  if (location.pathname.startsWith('/flowchart-editor/')) {
    return <FlowChartEditorPage />;
  }

  return (
    <div className="min-h-screen bg-gray-50 font-inter text-gray-800" style={{ scrollBehavior: 'auto' }}>
      {/* Environment Banner removed — sync status now in Admin Tools tab */}
      
      {/* Left Sidebar */}
      <aside 
        className="fixed left-0 top-0 w-56 bg-white shadow-md h-screen flex flex-col z-10"
      >
        {/* Company Logo/Branding */}
        <div className="px-4 h-14 flex items-center border-b border-gray-200 overflow-hidden">
          <div className="flex items-center gap-2 min-w-0 overflow-hidden">
            {branding.logoUrl ? (
              <img 
                src={branding.logoUrl} 
                alt={branding.companyName} 
                className="w-8 h-8 object-contain rounded flex-shrink-0"
              />
            ) : (
              <span className="text-xl flex-shrink-0">✨</span>
            )}
            <div className="min-w-0">
              <h1 
                className="font-bold leading-tight"
                style={{ 
                  // Dynamic font size: larger for short names, smaller for long names
                  fontSize: branding.companyName.length <= 12 ? '1.5rem' : 
                    branding.companyName.length <= 20 ? '1.1rem' : '1rem',
                  fontFamily: branding.titleFont || 'system-ui',
                  color: branding.titleColor || '#1D4ED8',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-word'
                }}
                title={branding.companyName}
              >
                {branding.companyName}
              </h1>
              {branding.tagline && branding.showTaglineInSidebar && (
                <p 
                  style={{
                    fontFamily: branding.taglineFont || 'system-ui',
                    color: branding.taglineColor || '#6B7280',
                    fontSize: branding.tagline.length <= 20 ? '0.75rem' :
                      branding.tagline.length <= 35 ? '0.65rem' : '0.55rem',
                    lineHeight: 1.25,
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                  }}
                  title={branding.tagline}
                >
                  {branding.tagline}
                </p>
              )}
            </div>
          </div>
        </div>
                
        {/* User Profile Section */}
        <div className="px-3 py-2 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-semibold text-blue-700">
                {(user?.displayName || userEmail || '?')[0].toUpperCase()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate" title={user?.displayName || userEmail}>
                {user?.displayName || (userEmail ? userEmail.split('@')[0] : 'User')}
              </p>
              <div className="flex items-center gap-1.5">
                <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none ${
                  isAdmin
                    ? 'bg-purple-100 text-purple-700'
                    : isTeamLead
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                }`}>
                  {isAdmin ? 'Admin' : isTeamLead ? 'Team Lead' : 'Agent'}
                </span>
                {userTeamName && (
                  <span className="text-[10px] text-gray-400 truncate" title={userTeamName}>
                    {userTeamName}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Area — compact, no scroll */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Navigation Menu */}
          <nav className="px-3 py-1 space-y-0">
            {/* Section: Calling */}
            <div className="text-[10px] uppercase text-gray-400 px-3 pt-3 pb-0.5 font-semibold tracking-wider">Calling</div>

            {/* 1. Call Script (Main App) - Available for all users */}
            <button 
              onClick={() => navigateWithUnsavedCheck('callScript')}
              className={`w-full flex items-center space-x-2 py-1.5 px-3 text-left rounded-md transition-colors group ${
                activeNavigation === 'callScript' 
                  ? 'bg-blue-100 border-l-4 border-blue-600' 
                  : 'hover:bg-blue-50'
              }`}
            >
              <svg className={`w-5 h-5 flex-shrink-0 ${
                activeNavigation === 'callScript' 
                  ? 'text-blue-700' 
                  : 'text-gray-500 group-hover:text-blue-700'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className={`text-sm font-medium ${
                activeNavigation === 'callScript' 
                  ? 'text-blue-700' 
                  : 'text-gray-700 group-hover:text-blue-700'
              }`}>Call Script</span>
            </button>

            {/* Section: Tools */}
            <div className="text-[10px] uppercase text-gray-400 px-3 pt-3 pb-0.5 font-semibold tracking-wider">Tools</div>

            {/* 2. AI Call Evaluator - Available for all users */}
            <button 
              onClick={() => navigateWithUnsavedCheck('callEvaluator')}
              className={`w-full flex items-center space-x-2 py-1.5 px-3 text-left rounded-md transition-colors group ${
                activeNavigation === 'callEvaluator' 
                  ? 'bg-blue-100 border-l-4 border-blue-600' 
                  : 'hover:bg-blue-50'
              }`}
              title="Call Evaluator"
            >
              <div className="relative flex-shrink-0">
                <svg className={`w-5 h-5 ${
                  activeNavigation === 'callEvaluator' 
                    ? 'text-blue-700' 
                    : 'text-gray-500 group-hover:text-blue-700'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5L6 9H2v6h4l5 4V5z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.54 8.46a5 5 0 010 7.07" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.07 4.93a10 10 0 010 14.14" />
                </svg>
                <span className={`absolute -top-1 -right-1 text-[8px] font-bold px-1 rounded ${
                  activeNavigation === 'callEvaluator'
                    ? 'bg-blue-700 text-white'
                    : 'bg-blue-600 text-white group-hover:bg-blue-700'
                }`}>AI</span>
              </div>
              <span className={`text-sm font-medium ${
                activeNavigation === 'callEvaluator' 
                  ? 'text-blue-700' 
                  : 'text-gray-700 group-hover:text-blue-700'
              }`}>Call Evaluator</span>
            </button>
                    
            {/* 3. Script Builder - Team Leads and Admins */}
            {canAccessScriptBuilder() && (
              <button 
                onClick={() => navigateWithUnsavedCheck('myScripts')}
                disabled={templatesLoading}
                className={`w-full flex items-center space-x-2 py-1.5 px-3 text-left rounded-md transition-colors group disabled:opacity-50 disabled:cursor-not-allowed ${
                  activeNavigation === 'myScripts' 
                    ? 'bg-blue-100 border-l-4 border-blue-600' 
                    : 'hover:bg-blue-50'
                }`}
                title={templatesLoading ? 'Loading templates...' : 'Script Builder'}
              >
                <div className="relative flex-shrink-0">
                  <svg className={`w-5 h-5 ${
                    activeNavigation === 'myScripts' 
                      ? 'text-blue-700' 
                      : 'text-gray-500 group-hover:text-blue-700'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className={`absolute -top-1 -right-1 text-[8px] font-bold px-1 rounded ${
                    activeNavigation === 'myScripts'
                      ? 'bg-blue-700 text-white'
                      : 'bg-blue-600 text-white group-hover:bg-blue-700'
                  }`}>AI</span>
                </div>
                <span className={`text-sm font-medium ${
                  activeNavigation === 'myScripts' 
                    ? 'text-blue-700' 
                    : 'text-gray-700 group-hover:text-blue-700'
                }`}>Script Builder</span>
              </button>
            )}

            {/* Section: Management - always visible (Dashboard is available for all users) */}
            <div className="text-[10px] uppercase text-gray-400 px-3 pt-3 pb-0.5 font-semibold tracking-wider">Management</div>

            {/* Command Center / Dashboard - Available for all users */}
            <button
              onClick={() => navigateWithUnsavedCheck('dashboard')}
              className={`w-full flex items-center space-x-2 py-1.5 px-3 text-left rounded-md transition-colors group ${
                activeNavigation === 'dashboard'
                  ? 'bg-blue-100 border-l-4 border-blue-600'
                  : 'hover:bg-blue-50'
              }`}
              title={effectiveDisplayIsTeamLead || (!isViewingOtherTeam && isAdmin) ? 'Command Center' : 'Dashboard'}
            >
              <svg className={`w-5 h-5 flex-shrink-0 ${
                activeNavigation === 'dashboard'
                  ? 'text-blue-700'
                  : 'text-gray-500 group-hover:text-blue-700'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
              </svg>
              <span className={`text-sm font-medium ${
                activeNavigation === 'dashboard'
                  ? 'text-blue-700'
                  : 'text-gray-700 group-hover:text-blue-700'
              }`}>
                {effectiveDisplayIsTeamLead || (!isViewingOtherTeam && isAdmin) ? 'Command Center' : 'Dashboard'}
              </span>
            </button>

            {/* 5. Leads - Team Leads and Admins */}
            {canAccessLeads() && (
              <button 
                onClick={() => navigateWithUnsavedCheck('leads')}
                className={`w-full flex items-center space-x-2 py-1.5 px-3 text-left rounded-md transition-colors group ${
                  activeNavigation === 'leads' 
                    ? 'bg-blue-100 border-l-4 border-blue-600' 
                    : 'hover:bg-blue-50'
                }`}
                title="Leads"
              >
                <svg className={`w-5 h-5 flex-shrink-0 ${
                  activeNavigation === 'leads' 
                    ? 'text-blue-700' 
                    : 'text-gray-500 group-hover:text-blue-700'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className={`text-sm font-medium ${
                  activeNavigation === 'leads' 
                    ? 'text-blue-700' 
                    : 'text-gray-700 group-hover:text-blue-700'
                }`}>{effectiveDisplayIsAgent ? 'My Leads' : (!isAdmin && !isTeamLead) ? 'My Leads' : 'Leads'}</span>
              </button>
            )}

            {/* 6. Marketplace - Team Leads and Admins (hidden in agent view) */}
            {!effectiveDisplayIsAgent && (isTeamLead || isAdmin) && (
              <button
                onClick={() => { setMarketplaceTab('browse'); navigateWithUnsavedCheck('marketplace'); }}
                className={`w-full flex items-center space-x-2 py-1.5 px-3 text-left rounded-md transition-colors group ${
                  activeNavigation === 'marketplace' || activeNavigation?.startsWith('marketplace')
                    ? 'bg-blue-100 border-l-4 border-blue-600'
                    : 'hover:bg-blue-50'
                }`}
                title="Script Marketplace"
              >
                <svg className={`w-5 h-5 flex-shrink-0 ${
                  activeNavigation === 'marketplace' || activeNavigation?.startsWith('marketplace')
                    ? 'text-blue-700'
                    : 'text-gray-500 group-hover:text-blue-700'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className={`text-sm font-medium ${
                  activeNavigation === 'marketplace' || activeNavigation?.startsWith('marketplace')
                    ? 'text-blue-700'
                    : 'text-gray-700 group-hover:text-blue-700'
                }`}>Marketplace</span>
              </button>
            )}
                    
            {/* 7. Team Manager - Team Leads and Admins */}
            {userRole && canAccessTeamManager() && (
              <button 
                onClick={() => navigateWithUnsavedCheck('teamManager')}
                className={`w-full flex items-center space-x-2 py-1.5 px-3 text-left rounded-md transition-colors group ${
                  activeNavigation === 'teamManager' 
                    ? 'bg-blue-100 border-l-4 border-blue-600' 
                    : 'hover:bg-blue-50'
                }`}
                title="Team Manager"
              >
                <svg className={`w-5 h-5 flex-shrink-0 ${
                  activeNavigation === 'teamManager' 
                    ? 'text-blue-700' 
                    : 'text-gray-500 group-hover:text-blue-700'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className={`text-sm font-medium ${
                  activeNavigation === 'teamManager' 
                    ? 'text-blue-700' 
                    : 'text-gray-700 group-hover:text-blue-700'
                }`}>Team Manager</span>
              </button>
            )}
                    
            {/* 8. Admin Panel - Admins only */}
            {canAccessAdminPanel() && (
              <button 
                onClick={() => navigateWithUnsavedCheck('adminPanel')}
                className={`w-full flex items-center space-x-2 py-1.5 px-3 text-left rounded-md transition-colors group ${
                  activeNavigation === 'adminPanel' 
                    ? 'bg-blue-100 border-l-4 border-blue-600' 
                    : 'hover:bg-blue-50'
                }`}
                title="Admin Panel"
              >
                <svg className={`w-5 h-5 flex-shrink-0 ${
                  activeNavigation === 'adminPanel' 
                    ? 'text-blue-700' 
                    : 'text-gray-500 group-hover:text-blue-700'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className={`text-sm font-medium ${
                  activeNavigation === 'adminPanel' 
                    ? 'text-blue-700' 
                    : 'text-gray-700 group-hover:text-blue-700'
                }`}>Admin Panel</span>
              </button>
            )}

            {/* Section: System */}
            <div className="text-[10px] uppercase text-gray-400 px-3 pt-3 pb-0.5 font-semibold tracking-wider">System</div>

            {/* 9. Settings - Available for all users */}
            <button
              onClick={() => navigateWithUnsavedCheck('settings')}
              className={`w-full flex items-center space-x-2 py-1.5 px-3 text-left rounded-md transition-colors group ${
                activeNavigation === 'settings' 
                  ? 'bg-blue-100 border-l-4 border-blue-600' 
                  : 'hover:bg-blue-50'
              }`}
              title="Settings & Help"
            >
              <svg className={`w-5 h-5 flex-shrink-0 ${
                activeNavigation === 'settings' 
                  ? 'text-blue-700' 
                  : 'text-gray-500 group-hover:text-blue-700'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className={`text-sm font-medium ${
                activeNavigation === 'settings' 
                  ? 'text-blue-700' 
                  : 'text-gray-700 group-hover:text-blue-700'
              }`}>Settings</span>
            </button>

            {/* 10. Help Center - Available for all users */}
            <button
              onClick={() => navigateWithUnsavedCheck('helpCenter')}
              className={`w-full flex items-center space-x-2 py-1.5 px-3 text-left rounded-md transition-colors group ${
                activeNavigation === 'helpCenter' 
                  ? 'bg-blue-100 border-l-4 border-blue-600' 
                  : 'hover:bg-blue-50'
              }`}
              title="Help Center"
            >
              <svg className={`w-5 h-5 flex-shrink-0 ${
                activeNavigation === 'helpCenter' 
                  ? 'text-blue-700' 
                  : 'text-gray-500 group-hover:text-blue-700'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className={`text-sm font-medium ${
                activeNavigation === 'helpCenter' 
                  ? 'text-blue-700' 
                  : 'text-gray-700 group-hover:text-blue-700'
              }`}>Help Center</span>
            </button>
          </nav>
        
        </div>
        {/* End of scrollable nav area */}

        {/* Admin Team Selector — flex-shrink-0 so it's always visible, never clipped */}
        {isAdmin && (
          <div className="flex-shrink-0 px-3 py-2 border-t border-gray-200">
            <div className="mb-1">
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                Viewing Team
              </label>
            </div>
            <TeamSelector 
              selectedTeam={viewingTeamId || userTeam} 
              onTeamChange={(teamId) => {
                console.log('Global admin team changed to:', teamId);
                setViewingTeamId(teamId);
                // Reload templates for My Scripts page
                if (activeNavigation === 'myScripts') {
                  loadTemplatesFromFirestore(teamId);
                }
              }}
              disabled={false}
            />
            {/* View as toggle — only shown when viewing another team */}
            {isViewingOtherTeam && (
              <div className="mt-1.5">
                <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider block mb-1">
                  View as
                </label>
                <div className="flex rounded overflow-hidden border border-gray-200 text-[11px]">
                  <button
                    onClick={() => setViewingRole('team_lead')}
                    className={`flex-1 py-1 font-medium transition-colors leading-tight ${
                      viewingRole === 'team_lead'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Team Lead
                  </button>
                  <button
                    onClick={() => setViewingRole('agent')}
                    className={`flex-1 py-1 font-medium transition-colors border-l border-gray-200 leading-tight ${
                      viewingRole === 'agent'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Agent
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Fixed Footer - Always visible at bottom */}
        <div className="flex-shrink-0">
          {/* Send Feedback Button */}
          <div className="px-3 py-2 border-t border-gray-200">
            <button 
              onClick={handleReportProblem}
              disabled={isCapturingData}
              className={`w-full flex items-center space-x-2 py-1.5 px-3 text-left rounded-md transition-colors group ${
                isCapturingData 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:bg-gray-100'
              }`}
              title={isCapturingData ? 'Preparing...' : 'Send Feedback'}
            >
              <svg className="w-4 h-4 flex-shrink-0 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="text-xs font-medium text-gray-500 group-hover:text-gray-700">
                {isCapturingData ? 'Preparing...' : 'Send Feedback'}
              </span>
            </button>
          </div>
        
          {/* Powered by TreeGenie - Show when team has custom branding */}
          {(branding.companyName !== 'TreeGenie' || branding.logoUrl) && (
            <div className="px-3 py-1 border-t border-gray-100 bg-gray-50">
              <p className="text-[10px] text-gray-400 text-center">
              Powered by <a href="https://treegenie.ai" target="_blank" rel="noopener noreferrer" className="font-medium hover:text-gray-600 transition-colors">Treegenie.ai</a>
              </p>
            </div>
          )}
        
        </div>
        {/* End of fixed footer */}
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-w-0 h-screen ml-56">
        <AdminPreviewProvider isViewingAsAgent={effectiveDisplayIsAgent} isViewingAsTeamLead={isViewingOtherTeam && viewingRole === 'team_lead'}>
        {/* Profile Completion Prompt for existing users */}
        <ProfileCompletionPrompt />
        {/* Call Script Content */}
        {activeNavigation === 'callScript' && (
          <CallScript
            userId={userId}
            userEmail={userEmail}
            userRole={userRole}
            userTeam={userTeam}
            userTeamName={userTeamName}
            isAdmin={isAdmin}
            isTeamLead={isTeamLead}
            isAuthReady={isAuthReady}
            db={db}
            availableScripts={availableScripts}
            setAvailableScripts={setAvailableScripts}
            currentScriptTemplate={currentScriptTemplate}
            setCurrentScriptTemplate={setCurrentScriptTemplate}
            defaultTemplate={viewingTeamId && viewingTeamId !== userTeam ? viewingTeamLeadDefault : defaultTemplate}
            templatesLoading={templatesLoading}
            handleScriptTemplateChange={handleScriptTemplateChange}
            setAsDefaultTemplate={setAsDefaultTemplate}
            analytics={analytics}
            currentCallSessionId={currentCallSessionId}
            setCurrentCallSessionId={setCurrentCallSessionId}
            canSwitchTemplates={canSwitchTemplates}
            pendingTemplateAssignment={pendingTemplateAssignment}
            setPendingTemplateAssignment={setPendingTemplateAssignment}
            isTemplateReady={isTemplateReady}
            setIsTemplateReady={setIsTemplateReady}
            teamLeadCaptureForm={teamLeadCaptureForm}
            teamSubscriptionPlan={teamSubscriptionPlan}
          />
        )}

        {/* OLD CALL SCRIPT CODE - TO BE REMOVED AFTER TESTING */}
        {false && activeNavigation === 'callScript_old' && (
          <div className="flex-1 p-4">
            {/* Show loading state until template is ready */}
            {!isTemplateReady && (
              <div className="flex-1 flex items-center justify-center">
                <LoadingSpinner size="large" message="Loading your script template..." />
              </div>
            )}
                        
            {/* Main content - only show when template is ready */}
            {isTemplateReady && (
              <>
                {/* Header with Template Selector */}
                <div className="flex items-center justify-between mb-4 border-b border-gray-200 pb-3">
                  <h2 className="text-xl font-semibold text-blue-600 border-b-2 border-blue-600 pb-1">Call Script</h2>
                                
                  {/* Template Selector */}
                  <div className="flex flex-row items-center space-x-2">
                    {canSwitchTemplates() ? (
                      <>
                        <select
                          value={currentScriptTemplate || ''}
                          onChange={(e) => handleScriptTemplateChange(e.target.value)}
                          className="bg-white border border-gray-300 rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                        >
                          {Object.entries(availableScripts)
                            .sort(([idA, scriptA], [idB, scriptB]) => {
                              // Always put example-script at the bottom
                              if (idA === 'example-script') return 1;
                              if (idB === 'example-script') return -1;
                              // Sort user-created templates alphabetically by name
                              return (scriptA.name || '').localeCompare(scriptB.name || '');
                            })
                            .map(([id, script]) => (
                              <option key={id} value={id}>
                                {script.name} {defaultTemplate === id ? '(Default)' : ''}
                              </option>
                            ))}
                        </select>
                        {templatesLoading && (
                          <LoadingSpinner size="small" inline />
                        )}
                        {userRole !== 'basic' && currentScriptTemplate && currentScriptTemplate !== 'example-script' && availableScripts[currentScriptTemplate] && !availableScripts[currentScriptTemplate]?.isReadOnly && (
                          <button
                            onClick={() => setAsDefaultTemplate(currentScriptTemplate)}
                            disabled={currentScriptTemplate === defaultTemplate}
                            className="px-3 py-1 bg-green-500 text-white rounded-md text-xs hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed shadow-sm"
                            title="Set as Default"
                          >
                            {currentScriptTemplate === defaultTemplate ? 'Default' : 'Set Default'}
                          </button>
                        )}
                      </>
                    ) : (
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-md border border-gray-200">
                                                Template: {(() => {
                            // Try to get template name from current script template first
                            const currentTemplateName = availableScripts[currentScriptTemplate]?.name;
                            if (currentTemplateName) return currentTemplateName;
                                                    
                            // Then try default template
                            const defaultTemplateName = availableScripts[defaultTemplate]?.name;
                            if (defaultTemplateName) return defaultTemplateName;
                                                    
                            // If we have a current script template but no name, show loading
                            if (currentScriptTemplate && currentScriptTemplate !== 'example-script') {
                              return 'Loading template...';
                            }
                                                    
                            // Default fallback
                            return 'Example Script (Demo)';
                          })()}
                        </span>
                        {pendingTemplateAssignment && (
                          <span className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded-md border border-orange-200 mt-1">
                                                    ⏳ Next call: {pendingTemplateAssignment.templateName}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Main Content Area - Fixed Height Layout */}
                <div className={`flex-1 flex flex-col pr-6 pl-0 w-full h-full relative ${(rightPanelContent === 'prospectData' || (rightPanelContent === 'script' && currentStage === 'call_end')) ? 'overflow-y-auto pb-6' : 'overflow-hidden'}`} style={{ paddingTop: '15px' }}>
                            
                  {/* Navigation Controls - Centered between edge and conversation history */}
                  {!(rightPanelContent === 'prospectData' || (rightPanelContent === 'script' && currentStage === 'call_end')) && (
                    <div className="absolute z-10 flex flex-col space-y-2" style={{ top: '15px', left: '12px' }}>
                      {/* Reset Button - Top */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (conversationHistory.length > 0) {
                            setShowResetDialog(true);
                          }
                        }}
                        disabled={conversationHistory.length === 0}
                        className={`flex items-center px-4 py-2 rounded-lg transition duration-200 ease-in-out shadow-md text-sm font-medium ${
                          conversationHistory.length > 0 
                            ? 'bg-red-500 text-white hover:bg-red-600 cursor-pointer' 
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                                    Reset
                      </button>

                      {/* Back Button - Below Reset */}
                      {previousStages.length > 0 && currentStage !== 'call_end' && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleProspectResponse('back', 'action');
                          }}
                          className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-200 ease-in-out shadow-md text-sm font-medium"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                          </svg>
                                        Back
                        </button>
                      )}
                    </div>
                  )}

                  {/* Conversation History - With left padding to avoid button overlap - Hidden during lead data collection */}
                  {!(currentStage === 'call_end' || currentStage === '__end_script__' || rightPanelContent === 'prospectData') && (
                    <div className="w-full mb-0" style={{ paddingLeft: '110px' }}>
                      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 h-32 overflow-y-auto">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-medium text-gray-600">Conversation History</h3>
                          <span className="text-xs text-gray-400">{conversationHistory.length} messages</span>
                        </div>
                                        
                        {conversationHistory.length === 0 ? (
                          <div className="flex items-center justify-center h-16 text-gray-400 text-sm">
                                                Start the call to see conversation history
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {conversationHistory.map((entry, index) => (
                              <div 
                                key={index} 
                                className={`flex ${entry.role === 'Agent' ? 'justify-end' : 'justify-start'}`}
                              >
                                <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                                  entry.role === 'Agent' 
                                    ? 'bg-blue-100 text-black' 
                                    : 'bg-green-100 text-black'
                                }`}>
                                  <span className="font-medium">{entry.role}:</span> {entry.text}
                                </div>
                              </div>
                            ))}
                            <div ref={conversationEndRef} />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {rightPanelContent === 'script' && currentStage !== 'call_end' && (
                    <>
                      {/* Agent Script - FIXED POSITION AND SIZE */}
                      <div className="flex flex-col items-center mb-6" style={{ marginTop: '20px' }}>
                        <div className="w-full max-w-4xl h-48 bg-blue-50 rounded-xl shadow-sm border border-blue-300 p-4 flex flex-col mx-auto">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-medium text-gray-600">Agent Script</h3>
                          </div>
                          <div className="flex-1 flex items-center justify-center text-center">
                            <div className="text-xl md:text-2xl font-medium text-gray-800 leading-relaxed">
                              {`"${currentStageConfig?.script || 'No script available for this stage.'}"`}
                            </div>
                          </div>

                        </div>
                      </div>

                      {/* Response Options - Compact */}
                      {currentStage !== 'call_end' && (
                        <div className="w-full max-w-4xl flex flex-col items-center mx-auto">
                          {/* Check for open text responses */}
                          {(() => {
                            // Check if any button has type 'open_text'
                            const openTextButtons = currentStageConfig?.buttons?.filter(button => button.type === 'open_text') || [];
                                                
                            // Debug: Log the open text button data
                            console.log('🔍 Open text buttons found:', openTextButtons);
                            console.log('🔍 Current stage config:', currentStageConfig);
                                                
                            if (openTextButtons.length > 0) {
                              // Handle multiple open text responses
                              if (openTextButtons.length === 1) {
                                // Single open text response - use existing logic
                                const openTextButton = openTextButtons[0];
                                console.log('🔍 Single open text button:', openTextButton);
                                return (
                                  <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm w-full max-w-5xl mx-auto mb-40">
                                    <label className="block text-sm font-medium text-gray-700 mb-3">
                                      {openTextButton.label || 'Enter your response:'}
                                    </label>
                                    <input
                                      type="text"
                                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                                      placeholder={openTextButton.placeholder || 'Type your response here...'}
                                      value={prospectInput}
                                      onChange={(e) => setProspectInput(e.target.value)}
                                    />
                                    <div className="mt-3">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          if (prospectInput.trim()) {
                                            // Use the actual response key from the button
                                            handleProspectResponse(openTextButton.value, 'text');
                                          }
                                        }}
                                        disabled={!prospectInput.trim()}
                                        className="w-full px-6 py-3 rounded-lg shadow-sm transition duration-200 ease-in-out bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                                      >
                                                                        Submit
                                      </button>
                                    </div>
                                  </div>
                                );
                              } else {
                                // Multiple open text responses - show form with all fields
                                return (
                                  <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-lg space-y-4 w-full max-w-6xl mx-auto mb-32">
                                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                                                                    Please provide the following information:
                                    </h4>
                                    {openTextButtons.map((button, index) => (
                                      <div key={button.value} className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700">
                                          {button.label || `Field ${index + 1}:`}
                                        </label>
                                        <input
                                          type="text"
                                          className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                                          placeholder={button.placeholder || 'Type your response here...'}
                                          value={multipleInputs[button.value] || ''}
                                          onChange={(e) => setMultipleInputs(prev => ({
                                            ...prev,
                                            [button.value]: e.target.value
                                          }))}
                                        />
                                      </div>
                                    ))}
                                    <div className="mt-4">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          // Check if all required fields have content
                                          const allFieldsFilled = openTextButtons.every(button => 
                                            multipleInputs[button.value] && multipleInputs[button.value].trim()
                                          );
                                                                            
                                          if (allFieldsFilled) {
                                            // Submit all responses at once
                                            handleMultipleOpenTextResponses(openTextButtons, multipleInputs);
                                          }
                                        }}
                                        disabled={!openTextButtons.every(button => 
                                          multipleInputs[button.value] && multipleInputs[button.value].trim()
                                        )}
                                        className="w-full px-6 py-3 rounded-lg shadow-sm transition duration-200 ease-in-out bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                                      >
                                                                        Submit All Responses
                                      </button>
                                    </div>
                                  </div>
                                );
                              }
                            }
                                                
                            // Fallback to original text input if required
                            if (currentStageConfig?.requiresTextInput) {
                              return (
                                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                  <label className="block text-sm font-medium text-gray-700 mb-3">Prospect Response</label>
                                  <textarea
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm resize-none"
                                    rows="3"
                                    placeholder="Type prospect's response here..."
                                    value={prospectInput}
                                    onChange={(e) => setProspectInput(e.target.value)}
                                  ></textarea>
                                </div>
                              );
                            }
                                                
                            return null;
                          })()}

                          {/* Response Buttons - Compact Grid Layout */}
                          {(() => {
                            console.log('🔍 Button rendering debug:', {
                              currentStage,
                              isObjectionNode: currentStage?.startsWith('objection_'),
                              hasStageConfig: !!currentStageConfig,
                              hasButtons: !!currentStageConfig?.buttons,
                              buttonCount: currentStageConfig?.buttons?.length || 0,
                              buttons: currentStageConfig?.buttons?.map(b => ({ label: b.label, value: b.value, type: b.type })),
                              hasOpenText: !!currentStageConfig?.buttons?.find(button => button.type === 'open_text')
                            });
                            return null;
                          })()}
                          {!currentStageConfig?.buttons?.find(button => button.type === 'open_text') && (
                            <div className="flex flex-wrap justify-center gap-3">
                              {currentStageConfig?.buttons?.map((button, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleProspectResponse(button.value, button.type);
                                  }}
                                  disabled={
                                    (button.value === 'submit_rent' || button.value === 'submit_time' || button.value === 'submit_notes') &&
                                                                    currentStageConfig?.requiresTextInput &&
                                                                    prospectInput.trim() === ''
                                  }
                                  className={`px-6 py-4 rounded-lg font-medium text-base transition duration-200 ease-in-out hover:scale-105 shadow-sm ${
                                    button.value === 'end_call' || button.value === 'end_call_no_notes' 
                                      ? 'bg-red-500 text-white hover:bg-red-600' 
                                      : 'bg-blue-500 text-white hover:bg-blue-600'
                                  } ${
                                    ((button.value === 'submit_time' || button.value === 'submit_notes' || button.value === 'submit_rent') && currentStageConfig?.requiresTextInput && prospectInput.trim() === '') 
                                      ? 'opacity-50 cursor-not-allowed transform-none' 
                                      : ''
                                  }`}
                                >
                                  {button.label}
                                </button>
                              ))}
                            </div>
                          )}
                                            
                          {/* Objection Response Buttons - Red buttons below regular responses */}
                          {currentStageConfig?.step && currentScriptTemplate && availableScripts && availableScripts[currentScriptTemplate]?.steps && (
                            (() => {
                              const currentStep = availableScripts[currentScriptTemplate].steps.find(step => step.id === currentStageConfig.step);
                              const objectionResponses = currentStep?.objectionResponses || [];
                                                    
                              // Debug logging
                              console.log('🔍 Objection Debug:', 
                                'step:', currentStageConfig?.step,
                                'template:', currentScriptTemplate,
                                'objections:', objectionResponses.length
                              );
                                                    
                              if (currentStep) {
                                console.log('🔍 Current step for objections:', currentStep.id, 'objections:', objectionResponses.length);
                              }
                                                    
                              if (objectionResponses.length === 0) return null;
                                                    
                              return (
                                <div className="mt-4">
                                  <div className="text-center mb-3">
                                    <span className="text-sm text-gray-600 font-medium">Common Objections:</span>
                                  </div>
                                  <div className="flex flex-wrap justify-center gap-3">
                                    {objectionResponses.map((objection, index) => (
                                      <button
                                        key={index}
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleProspectResponse(`objection_${objection.id}`, 'objection');
                                        }}
                                        className="px-6 py-4 rounded-lg font-medium text-base transition duration-200 ease-in-out hover:scale-105 shadow-sm bg-red-500 text-white hover:bg-red-600"
                                      >
                                        {objection.label}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Call End Message and Lead Form */}
                  {rightPanelContent === 'script' && currentStage === 'call_end' && (
                    <div className="w-full max-w-2xl mx-auto">
                      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 text-center mb-6">
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-3">
                          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800 mb-2">Call Complete</h2>
                        <p className="text-sm text-gray-600">Great job! Please fill out the prospect information below.</p>
                      </div>
                    </div>
                  )}

                  {(rightPanelContent === 'prospectData' || (rightPanelContent === 'script' && currentStage === 'call_end')) && (
                    <div className="w-full max-w-2xl mx-auto">
                      <div className="text-center mb-3">
                        <h2 className="text-xl font-semibold text-gray-800 mb-1">{currentProspectFormConfig?.title || 'Lead Information'}</h2>
                        <p className="text-gray-600 text-sm">Please fill out the prospect details from your conversation.</p>
                      </div>

                      {prospectDataError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg relative mb-4" role="alert">
                          <strong className="font-bold">Error:</strong>
                          <span className="block sm:inline"> {prospectDataError}</span>
                        </div>
                      )}

                      {/* Custom Form Fields */}
                      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                        <div className="space-y-6">
                          {currentProspectFormConfig.fields.map((field) => (
                            <div key={field.id}>
                              <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 mb-2">
                                {field.label}{field.required && <span className="text-red-500 ml-1">*</span>}
                              </label>
                              {field.type === 'textarea' ? (
                                <textarea
                                  id={field.id}
                                  className="w-full p-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                                  placeholder={field.placeholder}
                                  value={prospectDataForm[field.id] || ''}
                                  onChange={(e) => setProspectDataForm(prev => ({ ...prev, [field.id]: e.target.value }))}
                                  rows={4}
                                />
                              ) : (
                                <input
                                  type={field.type}
                                  id={field.id}
                                  className="w-full p-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                                  placeholder={field.placeholder}
                                  value={prospectDataForm[field.id] || ''}
                                  onChange={(e) => setProspectDataForm(prev => ({ ...prev, [field.id]: e.target.value }))}
                                />
                              )}
                            </div>
                          ))}

                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleProspectDataSubmit(e);
                            }}
                            disabled={leadSaving}
                            className={`w-full px-8 py-4 rounded-xl font-medium text-lg transition duration-200 ease-in-out shadow-md transform hover:scale-105 mt-6 ${
                              leadSaving 
                                ? 'bg-gray-400 cursor-not-allowed transform-none' 
                                : 'bg-blue-600 hover:bg-blue-700'
                            } text-white`}
                          >
                            {leadSaving ? 'Saving Lead...' : 'Save & Start New Call'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                </div>

                {/* Coaching Footnote - Fixed at bottom - Hidden during lead data collection */}
                {isTemplateReady && currentStageConfig?.coaching && !(currentStage === 'call_end' || currentStage === '__end_script__' || rightPanelContent === 'prospectData') && (
                  <div className="fixed bottom-0 left-56 right-0 z-40" style={{ bottom: '10px' }}>
                    <div className="flex justify-center px-6">
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded-r-lg shadow-lg max-w-4xl">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <svg className="w-4 h-4 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                          </div>
                          <div className="ml-3 flex-1">
                            <p className="text-sm text-yellow-800 leading-relaxed">{currentStageConfig.coaching}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* My Scripts Content - Unified scripts management page */}
        {activeNavigation === 'myScripts' && canAccessScriptBuilder() && (
          <div className="flex-1 overflow-hidden">
            <MyScriptsPage
              key={userTeam}
              onClose={() => setActiveNavigation('callScript')}
              onApplyChanges={applyScriptBuilderChanges}
              onUpdateTemplate={updateScriptTemplate}
              onDeleteTemplate={deleteScriptTemplate}
              onSetAsDefault={setAsDefaultTemplate}
              onTemplateSelectionChange={handleScriptTemplateChange}
              availableTemplates={availableScripts}
              currentTemplate={currentScriptTemplate}
              defaultTemplate={viewingTeamId && viewingTeamId !== realTeamId ? viewingTeamLeadDefault : defaultTemplate}
              onUnsavedChangesChange={setScriptBuilderHasUnsavedChanges}
              onAutoSaveChange={setScriptBuilderAutoSave}
              onRegisterSave={(fn) => { scriptBuilderSaveRef.current = fn; }}
              onFlowchartModalChange={setScriptBuilderFlowchartModalOpen}
              refreshTemplates={refreshTemplates}
              templatesLoading={templatesLoading}
              db={db}
              analytics={analytics}
              teamSubscriptionPlan={teamSubscriptionPlan}
            />
          </div>
        )}
        {activeNavigation === 'myScripts' && !canAccessScriptBuilder() && (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center max-w-sm">
              <div className="text-4xl mb-3">🔒</div>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">Access Restricted</h2>
              <p className="text-sm text-gray-600 mb-4">Script Builder is only available to Team Leads and Admins. Contact your team lead if you need access.</p>
              <button
                onClick={() => setActiveNavigation('callScript')}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700"
              >
                Go to Call Script
              </button>
            </div>
          </div>
        )}

        {/* Admin Panel Content */}
        {activeNavigation === 'adminPanel' && (
          !userRole ? (
            <div className="flex-1 flex items-center justify-center">
              <LoadingSpinner size="large" message="Loading Admin Panel..." />
            </div>
          ) : !canAccessAdminPanel() ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="bg-white rounded-lg shadow-xl p-8 max-w-md mx-4">
                <div className="text-center">
                  <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" strokeWidth={2}/>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 9l-6 6m0-6l6 6" />
                  </svg>
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">Access Denied</h2>
                  <p className="text-gray-600 mb-4">You don&apos;t have permission to access this feature.</p>
                  <p className="text-sm text-gray-500">Only Administrators can access the Admin Panel.</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-full overflow-hidden">
              <div className="flex items-center border-b border-gray-200 px-4 h-14 bg-white">
                <h2 className="text-xl font-semibold text-purple-600 border-b-2 border-purple-600 pb-1">Admin Panel</h2>
              </div>
              <div className="bg-white px-4 pt-1.5">
                <Breadcrumbs items={[
                  { label: 'Home', href: '/call-script' },
                  { label: 'Admin Panel', current: true }
                ]} />
              </div>
              <div className="flex-1 p-4 overflow-auto">
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm w-full">
                  <AdminPanel
                    key={userTeam}
                    onClose={() => setActiveNavigation('callScript')}
                    userRole={userRole}
                    userTeam={userTeam}
                    db={db}
                    firebaseConfig={firebaseConfig}
                    getEmailDocumentId={getEmailDocumentId}
                    userEmail={userEmail}
                    isAdmin={isAdmin}
                    canManageUsers={canManageUsers}
                    availableScripts={availableScripts}
                    onShowProblemAnalyzer={() => setShowProblemAnalyzer(true)}
                    onShowAdminTemplateBrowser={() => setShowAdminTemplateBrowser(true)}
                    toggleMaintenance={toggleMaintenance}
                    maintenanceEnabled={maintenanceEnabled}
                    isUpdatingMaintenance={isUpdatingMaintenance}
                  />
                </div>
              </div>
            </div>
          )
        )}

        {/* Team Manager Content */}
        {activeNavigation === 'teamManager' && (
          !userRole ? (
            // Show loading while role is being determined
            <div className="flex-1 flex items-center justify-center">
              <LoadingSpinner size="large" message="Loading Team Manager..." />
            </div>
          ) : !canAccessTeamManager() ? (
            // Show access denied if user doesn't have permission
            <div className="flex-1 flex items-center justify-center">
              <div className="bg-white rounded-lg shadow-xl p-8 max-w-md mx-4">
                <div className="text-center">
                  <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" strokeWidth={2}/>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 9l-6 6m0-6l6 6" />
                  </svg>
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">Access Denied</h2>
                  <p className="text-gray-600 mb-4">You don&apos;t have permission to access Team Manager.</p>
                  <p className="text-sm text-gray-500">Only Team Leads and Admins can manage teams.</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-full overflow-hidden">
              <div className="flex items-center border-b border-gray-200 px-4 h-14 bg-white">
                <h2 className="text-xl font-semibold text-green-600 border-b-2 border-green-600 pb-1">Team Manager</h2>
              </div>
              <div className="bg-white px-4 pt-1.5">
                <Breadcrumbs items={[
                  { label: 'Home', href: '/call-script' },
                  { label: 'Team Manager', current: true }
                ]} />
              </div>
              <div className="flex-1 p-4 overflow-auto">
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <TeamManager
                    key={userTeam}
                    onTeamCreated={() => {
                      setShowTeamLeadMessage(false);
                      // Reload user defaults to get the new team
                      setTimeout(() => loadUserDefaults(), 1000);
                    }}
                    userRole={userRole}
                    userTeam={userTeam}
                    db={db}
                    firebaseConfig={firebaseConfig}
                    getEmailDocumentId={getEmailDocumentId}
                    userEmail={userEmail}
                    isAdmin={isAdmin}
                    canManageUsers={canManageUsers}
                    availableScripts={availableScripts}
                  />
                </div>
              </div>
            </div>
          )
        )}

        {/* AI Call Evaluator Content - Available for all users */}
        {activeNavigation === 'callEvaluator' && (
          <div className="flex flex-col h-full overflow-hidden">
            <div className="flex items-center border-b border-gray-200 px-4 h-14 bg-white">
              <h2 className="text-xl font-semibold text-indigo-600 border-b-2 border-indigo-600 pb-1">Call Evaluator</h2>
            </div>
            <div className="bg-white px-4 pt-1.5">
              <Breadcrumbs items={[
                { label: 'Home', href: '/call-script' },
                { label: 'AI Call Evaluator', current: true }
              ]} />
            </div>
            <CallEvaluator key={userTeam} />
          </div>
        )}

        {/* Command Center / Dashboard - Available for all users */}
        {activeNavigation === 'dashboard' && (
          <div className="flex-1 overflow-hidden">
            <PerformancePage
              key={userTeam}
              analytics={analytics}
              userEmail={userEmail}
              userRole={effectiveUserRole}
              userTeam={userTeam}
              userTeamName={userTeamName}
              isAdmin={effectiveIsAdmin}
              isTeamLead={effectiveIsTeamLead}
              userId={userId}
              templates={dataContextTemplates || {}}
              defaultTemplateId={defaultTemplate}
              onNavigate={(target, url) => navigateWithUnsavedCheck(target, url)}
            />
          </div>
        )}

        {/* Marketplace Access Denied - agents cannot access marketplace */}
        {activeNavigation === 'marketplace' && !(isTeamLead || isAdmin) && (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center max-w-sm">
              <div className="text-4xl mb-3">🔒</div>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">Access Restricted</h2>
              <p className="text-sm text-gray-600 mb-4">The Script Marketplace is only available to Team Leads and Admins. Contact your team lead if you need access.</p>
              <button
                onClick={() => setActiveNavigation('callScript')}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700"
              >
                Go to Call Script
              </button>
            </div>
          </div>
        )}

        {/* Marketplace Content - Team Leads and Admins only (not when viewing listing detail) */}
        {activeNavigation === 'marketplace' && (isTeamLead || isAdmin) && !location.pathname.startsWith('/marketplace/listing/') && (
          <div className="flex flex-col h-full overflow-hidden">
            {/* Header */}
            <div className="flex items-center border-b border-gray-200 px-4 h-14 bg-white">
              <h2 className="text-xl font-semibold text-teal-600 border-b-2 border-teal-600 pb-1">Script Marketplace</h2>
            </div>
            {/* Breadcrumbs */}
            <div className="bg-white px-4 pt-1.5">
              <Breadcrumbs items={[
                { label: 'Home', href: '/call-script' },
                { label: 'Marketplace', href: '/marketplace' },
                ...(marketplaceTab !== 'browse' ? [{
                  label: marketplaceTab === 'purchases' ? 'My Purchases'
                    : marketplaceTab === 'seller' ? 'Seller Dashboard'
                    : marketplaceTab === 'list' ? 'List Script'
                    : marketplaceTab === 'admin' ? 'Admin Queue'
                    : 'Browse Scripts',
                  current: true
                }] : [])
              ]} />
            </div>

            {/* Tabs */}
            <div className="flex space-x-1 border-b border-gray-200 bg-white px-4">
              <button
                onClick={() => setMarketplaceTab('browse')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  marketplaceTab === 'browse'
                    ? 'bg-teal-100 text-teal-700 border-b-2 border-teal-600'
                    : 'text-gray-600 hover:text-teal-600 hover:bg-teal-50'
                }`}
              >
                🛒 Browse Scripts
              </button>
              <button
                onClick={() => setMarketplaceTab('purchases')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  marketplaceTab === 'purchases'
                    ? 'bg-teal-100 text-teal-700 border-b-2 border-teal-600'
                    : 'text-gray-600 hover:text-teal-600 hover:bg-teal-50'
                }`}
              >
                📦 My Purchases
              </button>
              <button
                onClick={() => setMarketplaceTab('seller')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-1.5 ${
                  marketplaceTab === 'seller'
                    ? 'bg-teal-100 text-teal-700 border-b-2 border-teal-600'
                    : 'text-gray-600 hover:text-teal-600 hover:bg-teal-50'
                }`}
              >
                💰 Seller Dashboard
                {teamSubscriptionPlan === 'starter' && (
                  <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-semibold">Pro</span>
                )}
              </button>
              <button
                onClick={() => setMarketplaceTab('list')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-1.5 ${
                  marketplaceTab === 'list'
                    ? 'bg-teal-100 text-teal-700 border-b-2 border-teal-600'
                    : 'text-gray-600 hover:text-teal-600 hover:bg-teal-50'
                }`}
              >
                ➕ List Script
                {teamSubscriptionPlan === 'starter' && (
                  <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-semibold">Pro</span>
                )}
              </button>
              {isAdmin && !isViewingOtherTeam && (
                <button
                  onClick={() => setMarketplaceTab('admin')}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    marketplaceTab === 'admin'
                      ? 'bg-teal-100 text-teal-700 border-b-2 border-teal-600'
                      : 'text-gray-600 hover:text-teal-600 hover:bg-teal-50'
                  }`}
                >
                  🔍 Admin Queue
                </button>
              )}
            </div>
            
            {/* Tab Content */}
            <div className="flex-1 p-4 overflow-auto">
              {marketplaceTab === 'browse' && <MarketplaceBrowser />}
              {marketplaceTab === 'purchases' && <MyPurchases />}
              {marketplaceTab === 'seller' && (
                teamSubscriptionPlan === 'starter' ? (
                  <div className="flex flex-col items-center justify-center h-64 text-center p-8">
                    <div className="text-4xl mb-3">💰</div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Selling scripts is a Pro feature</h3>
                    <p className="text-sm text-gray-500 mb-4 max-w-sm">List your scripts on the marketplace and earn revenue from other teams. Upgrade to Pro to unlock selling.</p>
                    <a href="/settings?tab=subscription" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
                      Upgrade to Pro →
                    </a>
                  </div>
                ) : <SellerDashboard />
              )}
              {marketplaceTab === 'list' && (
                teamSubscriptionPlan === 'starter' ? (
                  <div className="flex flex-col items-center justify-center h-64 text-center p-8">
                    <div className="text-4xl mb-3">➕</div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Listing scripts is a Pro feature</h3>
                    <p className="text-sm text-gray-500 mb-4 max-w-sm">Publish your call scripts to the marketplace for other teams to buy and use. Upgrade to Pro to start selling.</p>
                    <a href="/settings?tab=subscription" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
                      Upgrade to Pro →
                    </a>
                  </div>
                ) : <MarketplaceListingForm />
              )}
              {marketplaceTab === 'admin' && isAdmin && !isViewingOtherTeam && <MarketplaceAdminQueue />}
            </div>
          </div>
        )}

        {/* Marketplace Listing Detail - shown when viewing a specific listing */}
        {activeNavigation === 'marketplace' && location.pathname.startsWith('/marketplace/listing/') && (isTeamLead || isAdmin) && (
          <div className="flex flex-col h-full overflow-hidden">
            <div className="flex items-center border-b border-gray-200 px-4 h-14 bg-white">
              <h2 className="text-xl font-semibold text-teal-600 border-b-2 border-teal-600 pb-1">Script Details</h2>
            </div>
            <div className="bg-white px-4 pt-1.5">
              <Breadcrumbs items={[
                { label: 'Home', href: '/call-script' },
                { label: 'Marketplace', href: '/marketplace' },
                { label: 'Script Details', current: true }
              ]} />
            </div>
            <div className="flex-1 p-4 overflow-auto">
              <MarketplaceListingDetail />
            </div>
          </div>
        )}

        {/* Leads Content */}
        {activeNavigation === 'leads' && (
          !userRole ? (
            <div className="flex-1 flex items-center justify-center">
              <LoadingSpinner size="large" message="Loading Leads..." />
            </div>
          ) : !canAccessLeads() ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="bg-white rounded-lg shadow-xl p-8 max-w-md mx-4">
                <div className="text-center">
                  <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" strokeWidth={2}/>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 9l-6 6m0-6l6 6" />
                  </svg>
                  <h2 className="text-xl font-semibold text-gray-800 mb-2">Access Denied</h2>
                  <p className="text-gray-600 mb-4">You don&apos;t have permission to access Leads.</p>
                  <p className="text-sm text-gray-500">Only Team Leads and Admins can view leads data.</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-full overflow-hidden">
              <div className="flex items-center border-b border-gray-200 px-4 h-14 bg-white">
                <h2 className="text-xl font-semibold text-orange-600 border-b-2 border-orange-600 pb-1">{(!effectiveIsAdmin && !effectiveIsTeamLead) ? 'My Leads' : 'Leads'}</h2>
              </div>
              <div className="bg-white px-4 pt-1.5">
                <Breadcrumbs items={[
                  { label: 'Home', href: '/call-script' },
                  { label: (!effectiveIsAdmin && !effectiveIsTeamLead) ? 'My Leads' : 'Leads', current: true }
                ]} />
              </div>
              <div className="flex-1 p-4 overflow-auto">
                <Leads 
                  key={userTeam}
                  db={db}
                  userId={userId}
                  userEmail={userEmail}
                  userRole={effectiveUserRole}
                  userTeam={userTeam}
                  userTeamName={userTeamName}
                  isAdmin={effectiveIsAdmin}
                  isTeamLead={effectiveIsTeamLead}
                  availableScripts={availableScripts}
                  onUpdateTemplate={updateScriptTemplate}
                />
              </div>
            </div>
          )
        )}

        {/* Settings Content */}
        {activeNavigation === 'settings' && (
          <SettingsPage
            onLogout={handleLogout}
          />
        )}

        {/* Help Center Content */}
        {activeNavigation === 'helpCenter' && (
          <HelpCenter />
        )}
        </AdminPreviewProvider>
      </main>
                            
      {/* Help Panel */}
      {showHelpPanel && (
        <HelpPanel 
          onClose={() => setActiveNavigation('callScript')}
          userEmail={userEmail}
          isAuthReady={isAuthReady}
          userRole={userRole}
          userTeam={userTeam}
          userTeamName={userTeamName}
          defaultTemplate={viewingTeamId && viewingTeamId !== userTeam ? viewingTeamLeadDefault : defaultTemplate}
          templatesLoading={templatesLoading}
          availableScripts={availableScripts}
          currentScriptTemplate={currentScriptTemplate}
          isBasic={userRole === 'basic'}
          onLogout={handleLogout}
          onResetApp={resetApp}
          onExportConversation={() => {
            // Export conversation as text
            const exportData = conversationHistory.map(entry => 
              `${entry.role}: ${entry.text}`
            ).join('\n\n');
            const blob = new Blob([exportData], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `conversation-${new Date().toISOString().split('T')[0]}.txt`;
            a.click();
          }}
          showDebugInfo={showDebugInfo}
          setShowDebugInfo={setShowDebugInfo}
          testCreateTemplate={testCreateTemplate}
          testSetDefault={testSetDefault}
          checkFirestorePermissions={checkFirestorePermissions}
          setTemplateAsDefault={setAsDefaultTemplate}
          availableScriptsForDebug={availableScripts}
          userRoleForDebug={userRole}
          userTeamForDebug={userTeam}
          defaultTemplateForDebug={defaultTemplate}
          isAdmin={isAdmin}
          isTeamLead={isTeamLead}
          canAccessScriptBuilder={canAccessScriptBuilder}
          canSwitchTemplates={canSwitchTemplates}
          canManageUsers={canManageUsers}
          currentScriptTemplateForDebug={currentScriptTemplate}
          db={db}
          firebaseConfig={firebaseConfig}
          getEmailDocumentId={getEmailDocumentId}
          userId={userId}
          userEmailForDebug={userEmail}
          loadUserDefaults={loadUserDefaults}
          conversationHistory={conversationHistory}
          setUserRole={() => {}} // AuthContext manages user role
        />
      )}
      {/* Success Message - Hidden when flowchart modal is open */}
      {showSuccessMessage && !scriptBuilderFlowchartModalOpen && (
        <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Script changes applied successfully!</span>
          </div>
        </div>
      )}

      {/* Send Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <h2 className="text-lg font-semibold text-gray-900">Send Feedback</h2>
                </div>
                <button
                  onClick={() => setShowFeedbackModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isSubmittingReport}
                  aria-label="Close feedback form"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmitProblemReport} className="space-y-4">
                {/* Feedback Type Selector */}
                <div className="flex gap-2">
                  {[
                    { value: 'bug', label: 'Bug Report', icon: '🐛' },
                    { value: 'feature', label: 'Feature Request', icon: '💡' },
                    { value: 'general', label: 'General', icon: '💬' },
                  ].map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFeedbackType(type.value)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                        feedbackType === type.value
                          ? 'bg-blue-50 text-blue-700 border-2 border-blue-300'
                          : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <span>{type.icon}</span>
                      <span>{type.label}</span>
                    </button>
                  ))}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {feedbackType === 'bug' 
                      ? 'What happened? What were you trying to do?' 
                      : feedbackType === 'feature' 
                        ? 'What would you like to see?' 
                        : 'What\'s on your mind?'}
                  </label>
                  <textarea
                    value={feedbackDescription}
                    onChange={(e) => setFeedbackDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={4}
                    placeholder={
                      feedbackType === 'bug'
                        ? 'Example: I clicked "Save Lead" but nothing happened...'
                        : feedbackType === 'feature'
                          ? 'Example: It would be great if I could export leads to CSV...'
                          : 'Share your thoughts, questions, or suggestions...'
                    }
                    disabled={isSubmittingReport}
                    required
                  />
                </div>

                {/* Screenshot preview - compact */}
                {capturedScreenshot && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
                    <img 
                      src={capturedScreenshot} 
                      alt="Screenshot attached" 
                      className="h-16 object-contain border border-gray-200 rounded"
                    />
                    <span className="text-xs text-gray-500">Screenshot attached automatically</span>
                  </div>
                )}

                {/* Collapsible technical details */}
                <button
                  type="button"
                  onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className={`w-3 h-3 transition-transform ${showTechnicalDetails ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  What info is included with this feedback?
                </button>

                {showTechnicalDetails && (
                  <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-md space-y-1">
                    <p>To help us understand and resolve issues, we automatically include:</p>
                    <ul className="ml-3 space-y-0.5 list-disc">
                      <li>Your account info (email, role, team)</li>
                      <li>Which page you were on</li>
                      <li>A screenshot of the current screen</li>
                      <li>Browser and app version details</li>
                      <li>Recent app activity logs (no passwords or personal data)</li>
                    </ul>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-1">
                  <button
                    type="button"
                    onClick={() => setShowFeedbackModal(false)}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                    disabled={isSubmittingReport}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSubmittingReport}
                  >
                    {isSubmittingReport ? 'Sending...' : 'Send Feedback'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Analyzer */}
      {showProblemAnalyzer && (
        <ProblemReportAnalyzer 
          db={firebaseDb}
          onClose={() => setShowProblemAnalyzer(false)}
        />
      )}

      {/* Admin Template Browser */}
      {showAdminTemplateBrowser && (
        <AdminTemplateBrowser 
          availableScripts={availableScripts}
          onClose={() => setShowAdminTemplateBrowser(false)}
        />
      )}

      {/* Team Lead Message */}
      {showTeamLeadMessage && (
        <div className="fixed top-4 left-4 bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded z-50 max-w-md">
          <div className="flex items-start">
            <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <div className="font-medium mb-1">Welcome, Team Lead!</div>
              <div className="text-sm">
                                You need to create a team to access team features. Go to <strong>Team Manager</strong> to set up your team.
              </div>
              <button 
                onClick={() => setShowTeamLeadMessage(false)}
                className="text-blue-600 hover:text-blue-800 text-xs mt-2 underline"
              >
                                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      {showResetDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Reset Call Script</h3>
                <p className="text-gray-600 text-sm">
                                    This will clear your current progress and erase all conversation history for this call. You&apos;ll start fresh with a new call.
                </p>
              </div>
            </div>
                        
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowResetDialog(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition duration-200 ease-in-out text-sm font-medium"
              >
                                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowResetDialog(false);
                  resetApp();
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 ease-in-out text-sm font-medium"
              >
                                Reset
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default App;
